import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CropSquareIcon from '@mui/icons-material/CropSquare';

import { DEFAULT_PIPE_COMBOS } from '../pipeCatalog';
import fittingsData from '../fittingsData';
import {
  isFittingCompatibleWithMainPipe,
  isFittingCompatibleWithSquareProfile,
  isEndFittingCompatibleWithMainPipe,
  isEndFittingCompatibleWithSquareProfile,
} from './floorCollectorFittingCompatibility';
import { flangePipeInsertion, getCapRequirementForFitting, getHoleDiameterForPipe, nakShaibaPipeMeasure, getNippelRoundOverrideValue, getSquareMuftaCircuitDisplayValue, isAutoConstructorReducerAllowed, resolveNippelMuftaFitting, endFittingAutoLength } from './collectorUtils';
import CenterFittingsUI from './CenterFittingsUI';
import {
  determineThickness,
  getMinThickness,
  getActualCoating,
} from './pipeThicknessUtils';
import {
  generateCenterFittings,
  generateCenterFittingDimensions,
  generateCenterFittingBalloons,
  updateCenterFittingsForDiameter,
} from './centerFittingsLogic';
import { mergeFloorCircuitAutoSpacingToNext } from './floorCollectorCircuitSpacing';

/**
 * Извлекаем уникальные диаметры из каталога труб
 */
const getAvailableDiameters = () => {
  const diameters = new Set();
  Object.keys(DEFAULT_PIPE_COMBOS).forEach(key => {
    const [diameter] = key.split('|');
    diameters.add(parseFloat(diameter));
  });
  
  // Исключаем диаметры: все до 38 включительно, 54, 306
  const excludedDiameters = [54, 306];
  
  return Array.from(diameters)
    .filter(d => d > 38 && !excludedDiameters.includes(d)) // Убираем ≤38, 54, 306
    .sort((a, b) => a - b)
    .map(d => ({
      value: d,
      label: `⌀${d}`,
    }));
};

const availableDiameters = getAvailableDiameters();

/**
 * Доступные квадратные профили для основной трубы
 */
const availableSquareProfiles = [
  { value: '40x40', label: '40×40', displaySize: 40 },
  { value: '50x50', label: '50×50', displaySize: 50 },
  { value: '60x60', label: '60×60', displaySize: 60 },
  { value: '80x80', label: '80×80', displaySize: 80 },
  { value: '100x100', label: '100×100', displaySize: 100 },
  { value: '150x150', label: '150×150', displaySize: 150 },
];

/**
 * Категории фитингов для контуров
 */
const circuitFittingCategories = [
  { id: 'Ниппели', label: 'Ниппели' },
  { id: 'Муфты', label: 'Муфты' },
  { id: 'Фланцы', label: 'Фланцы' },
  { id: 'Накидные гайки', label: 'Накидные гайки' },
  { id: 'Шайбы с наружной резьбой', label: 'Шайбы с нар. резьбой' },
];

const centerFittingCategories = circuitFittingCategories;

/**
 * Получаем переходы концентрические
 */
const getConcentricReducers = () => {
  return Object.entries(fittingsData)
    .filter(([key, data]) => data.category === 'Переходы концентрические' && isAutoConstructorReducerAllowed(key))
    .map(([key, data]) => ({
      id: key,
      label: data.displayName.replace('Переход концентрический ', ''),
      diameter: data.diameter,
      length: data.length,
    }));
};

const concentricReducers = getConcentricReducers();

/**
 * Получаем фитинги по категории
 * @param {string} category - Категория фитинга
 * @param {number|null} mainPipeDiameter - Диаметр основной трубы (для круглых)
 * @param {string} shape - Форма трубы ('round' или 'square')
 * @param {string|null} profile - Профиль трубы (для квадратных, например '60x60')
 */
const getFittingsByCategory = (category, mainPipeDiameter = null, shape = 'round', profile = null) => {
  return Object.entries(fittingsData)
    .filter(([key, data]) => data.category === category)
    .map(([key, data]) => ({
      id: key,
      label: data.displayName,
      diameter: data.diameter,
      length: data.length,
      isCompatible: shape === 'square' && profile
        ? isFittingCompatibleWithSquareProfile(key, profile)
        : (mainPipeDiameter ? isFittingCompatibleWithMainPipe(key, mainPipeDiameter) : true),
    }));
};


/**
 * Маппинг Ду фланца → диаметр трубы
 */
const DN_TO_PIPE_DIAMETER = {
  25: 33.7,
  32: 42.4,
  40: 48.3,
  50: 60.3,
  65: 76.1,
  80: 88.9,
  100: 114.3,
  125: 139.7,
  150: 168.3,
  200: 219.1,
  250: 273,
  300: 323.9,
  350: 355.6,
};

/**
 * Маппинг размера накидной гайки → диаметр трубы
 */
const NAK_GAIKA_TO_PIPE_DIAMETER = {
  '1': 21.3,
  '1_1.4': 32,
  '1_1.2': 32,
  '2': 42.4,
};

// Маппинг размеров шайб с наружной резьбой на диаметр трубы (х3)
const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
  '1': 26.9,
  '1_1.4': 32,
  '1_1.2': 32,
  '2': 42.4,
};

/** Пересчитать «До след.» при смене фитинга/типа; не трогать при ручном spacingToNext. */
function shouldApplyFloorAutoSpacing(keyOrObj) {
  if (keyOrObj === 'spacingToNext') return false;
  if (typeof keyOrObj === 'object' && keyOrObj && Object.keys(keyOrObj).length > 0) {
    const keys = Object.keys(keyOrObj);
    if (keys.every((k) => k === 'spacingToNext')) return false;
    return ['fittingId', 'fittingCategory', 'type', 'diameter'].some((k) =>
      Object.prototype.hasOwnProperty.call(keyOrObj, k)
    );
  }
  if (typeof keyOrObj === 'string') {
    return ['fittingId', 'fittingCategory', 'type', 'diameter'].includes(keyOrObj);
  }
  return false;
}

/**
 * Извлечь Ду из ключа фланца (например, "Flanec_40_galv" → 40)
 */
const extractDnFromFlangeKey = (flangeKey) => {
  if (!flangeKey) return null;
  const match = flangeKey.match(/Flanec_(\d+)_/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Извлечь размер из ключа накидной гайки (например, "Nak_Gaika_1" → "1")
 */
const extractSizeFromNakGaikaKey = (nakGaikaKey) => {
  if (!nakGaikaKey) return null;
  const match = nakGaikaKey.match(/Nak_Gaika_(.+)/);
  return match ? match[1] : null;
};

/**
 * Получить диаметр трубы по Ду фланца
 */
const getPipeDiameterForDn = (dn) => {
  return DN_TO_PIPE_DIAMETER[dn] || null;
};

/**
 * Получить диаметр трубы по размеру накидной гайки
 */
const getPipeDiameterForNakGaika = (size) => {
  return NAK_GAIKA_TO_PIPE_DIAMETER[size] || null;
};

/**
 * Извлечь размер из ключа шайбы с наружной резьбой (например, "Shaib_Nar_Rezba_1" -> "1")
 */
const extractSizeFromShaibaNarRezba = (key) => {
  const match = key.match(/Shaib_Nar_Rezba_(.+)/);
  return match ? match[1] : null;
};

/**
 * Получить диаметр трубы по размеру шайбы с наружной резьбой
 */
const getPipeDiameterForShaibaNarRezba = (size) => {
  return SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[size] || null;
};

/**
 * Получить ключ шайбы/бортшайбы для фланца или накидной гайки
 */
const getShaibaKeyForFitting = (fittingKey) => {
  if (!fittingKey) return null;
  
  if (fittingKey.startsWith('Flanec_')) {
    const dn = extractDnFromFlangeKey(fittingKey);
    return dn ? `Bortshaiba_${dn}` : null;
  } else if (fittingKey.startsWith('Nak_Gaika_')) {
    const size = extractSizeFromNakGaikaKey(fittingKey);
    return size ? `Shaib_Gaika_${size}` : null;
  }
  
  return null;
};

/**
 * Найти подходящий переход концентрический
 * @param {number} fromDiameter - больший диаметр (основная труба)
 * @param {number} toDiameter - меньший диаметр (под фланец)
 * @returns {object|null} - {key, info} или null
 */
const findConcentricReducer = (fromDiameter, toDiameter) => {
  // Ищем переход с точным соответствием диаметров
  const reducerKey = `Perehod_Konc_${fromDiameter}х${toDiameter}`;
  if (!isAutoConstructorReducerAllowed(reducerKey)) return null;
  const reducerInfo = fittingsData[reducerKey];
  if (reducerInfo) {
    return { key: reducerKey, info: reducerInfo };
  }
  
  // Пробуем с русской "х"
  const reducerKeyRu = `Perehod_Konc_${fromDiameter}x${toDiameter}`;
  const reducerInfoRu = fittingsData[reducerKeyRu];
  if (reducerInfoRu) {
    return { key: reducerKeyRu, info: reducerInfoRu };
  }
  
  return null;
};

/**
 * Найти эллиптическую заглушку по диаметру
 * @param {number} diameter - диаметр трубы (гребёнки)
 * @returns {object|null} - {key, info} или null
 */
const findEllipticalCap = (diameter) => {
  const capKey = `Zaglushka_EL_${diameter}`;
  const capInfo = fittingsData[capKey];
  if (capInfo) {
    return { key: capKey, info: capInfo };
  }
  return null;
};

/**
 * Найти прямую заглушку по диаметру
 * @param {number} diameter - диаметр трубы (гребёнки)
 * @returns {object|null} - {key, info} или null
 */
const findStraightCap = (diameter) => {
  const capKey = `Zaglushka_PR_${diameter}`;
  const capInfo = fittingsData[capKey];
  if (capInfo) {
    return { key: capKey, info: capInfo };
  }
  return null;
};

/**
 * Параметры по умолчанию для этажного коллектора
 */
const defaultParams = {
  shape: 'round',            // Форма коллектора: 'round' или 'square'
  mainPipeDiameter: 60.3,    // Диаметр основной трубы (гребёнки) для круглой формы
  mainPipeProfile: '60x60',  // Профиль квадратной трубы (для квадратной формы)
  // Верхние контуры
  circuitCount: 4,           // Количество верхних контуров
  firstCircuitOffset: 60,    // Отступ первого контура от края
  firstCircuitOffsetMode: 'fromEdge', // 'fromEdge' или 'fromBottomCircuit'
  firstCircuitOffsetReferenceIndex: 0, // индекс нижнего контура, от которого отступаем (0 = первый)
  firstCircuitOffsetSaved: null, // сохранённое значение до включения режима привязки
  firstCircuitOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
  lastCircuitOffset: 60,     // Отступ последнего контура от края
  circuitSpacing: 100,       // Расстояние между контурами (общее)
  // Нижние контуры
  bottomCircuitCount: 0,     // Количество нижних контуров
  bottomFirstCircuitOffset: 60,
  bottomFirstCircuitOffsetMode: 'fromEdge', // 'fromEdge' или 'fromTopCircuit'
  bottomFirstCircuitOffsetReferenceIndex: 0, // индекс верхнего контура, от которого отступаем (0 = первый)
  bottomFirstCircuitOffsetSaved: null, // сохранённое значение до включения режима привязки
  bottomFirstCircuitOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
  bottomLastCircuitOffset: 60,
  bottomCircuitSpacing: 100,
  // Центральные фитинги (на самой гребенке, для термометров)
  hasCenterFittings: false,  // Есть ли центральные фитинги
  centerFittingCount: 0,     // Количество центральных фитингов
  centerFittingSpacing: 100, // Расстояние между центральными фитингами
  // Вход/выход
  hasLeftConnection: true,   // Есть ли подключение слева
  hasRightConnection: true,  // Есть ли подключение справа
  leftConnectionType: 'fitting', // 'pipe' или 'fitting'
  rightConnectionType: 'fitting',
  leftConnectionDiameter: 33.7,
  rightConnectionDiameter: 33.7,
  leftConnectionLength: null,
  rightConnectionLength: null,
  leftConnectionFittingCategory: '',  // Пустая категория = "Выбрать"
  leftConnectionFitting: '',          // Пустой фитинг = "Выбрать"
  rightConnectionFittingCategory: '', // Пустая категория = "Выбрать"
  rightConnectionFitting: '',         // Пустой фитинг = "Выбрать"
  leftConnectionFittingLength: null, // null = использовать длину из каталога
  rightConnectionFittingLength: null,
  // Заглушки и переходы
  hasLeftCap: true,          // Есть ли заглушка/переход слева (по умолчанию включена)
  hasRightCap: true,         // Есть ли заглушка/переход справа (по умолчанию включена)
  leftCapElementType: 'cap',  // 'cap' (заглушка) или 'reducer' (переход) - для квадратных только 'cap'
  rightCapElementType: 'cap',
  leftCapType: 'elliptical',    // 'straight' или 'elliptical' (для круглых), для квадратных только 'straight'
  rightCapType: 'elliptical',
  leftReducer: 'Perehod_Konc_60.3х48.3',  // ID перехода слева (только для круглых)
  rightReducer: 'Perehod_Konc_60.3х48.3', // ID перехода справа (только для круглых)
  // Ручное управление длиной
  useManualLength: false, // Флаг ручного управления длиной
  manualLength: null, // Ручная длина коллектора
};

/**
 * Редактор параметров Этажного коллектора
 */
function FloorCollectorEditor({
  onGenerate,
  onBack,
  onBackToDrawing,
  initialParams,
  calculationMode,
  onBackToCharacteristics,
}) {
  // Инициализация параметров из сохранённых или по умолчанию
  const [params, setParams] = useState(() => {
    if (initialParams?.params) {
      return { ...defaultParams, ...initialParams.params };
    }
    return defaultParams;
  });
  
  // Верхние контуры
  const [circuits, setCircuits] = useState(() => {
    if (initialParams?.circuits) {
      return initialParams.circuits;
    }
    return Array(defaultParams.circuitCount).fill(null).map((_, i) => ({
      id: i + 1,
      type: 'fitting',
      diameter: 26.9,
      length: 80,
      fittingCategory: 'Ниппели', // категория фитинга
      fittingId: 'Nippel_3.4',
      fittingLength: null, // null = использовать длину из каталога
      spacingToNext: null, // null = использовать общий circuitSpacing
    }));
  });
  
  // Нижние контуры
  const [bottomCircuits, setBottomCircuits] = useState(() => {
    if (initialParams?.bottomCircuits) {
      return initialParams.bottomCircuits;
    }
    return [];
  });
  
  // Центральные фитинги (на самой гребенке)
  const [centerFittings, setCenterFittings] = useState(() => {
    if (initialParams?.centerFittings && initialParams.centerFittings.length > 0) {
      return initialParams.centerFittings;
    }
    return [];
  });
  
  // Состояние для диалога предупреждения о несовместимости
  const [incompatibilityDialog, setIncompatibilityDialog] = useState({
    open: false,
    message: '',
  });
  
  // Состояние для drag & drop
  const [draggedCircuitIndex, setDraggedCircuitIndex] = useState(null);
  const [draggedBottomCircuitIndex, setDraggedBottomCircuitIndex] = useState(null);
  
  // Проверка есть ли произвольные межосевые
  const hasCustomSpacing = circuits.some(c => c.spacingToNext !== null);
  const hasBottomCustomSpacing = bottomCircuits.some(c => c.spacingToNext !== null);

  // Обновление общего параметра (можно передать объект для обновления нескольких свойств)
  const updateParam = (keyOrObj, value) => {
    setParams((prev) => {
      if (typeof keyOrObj === 'object') {
        return { ...prev, ...keyOrObj };
      }
      // Не преобразовывать boolean в number
      if (typeof value === 'boolean') {
        return { ...prev, [keyOrObj]: value };
      }
      const numValue = Number(value);
      return { ...prev, [keyOrObj]: Number.isFinite(numValue) ? numValue : value };
    });
  };

  // Обновление количества верхних контуров
  const updateCircuitCount = (delta) => {
    const newCount = Math.max(0, params.circuitCount + delta);
    setParams((prev) => ({ ...prev, circuitCount: newCount }));
    
    if (delta > 0) {
      setCircuits((prev) => {
        const added = [
          ...prev,
          ...Array(delta).fill(null).map((_, i) => ({
            id: prev.length + i + 1,
            type: 'fitting',
            diameter: 26.9,
            length: 80,
            fittingCategory: 'Ниппели',
            fittingId: 'Nippel_3.4',
            fittingLength: null,
            spacingToNext: null,
          })),
        ];
        return mergeFloorCircuitAutoSpacingToNext(added);
      });
    } else {
      setCircuits((prev) => mergeFloorCircuitAutoSpacingToNext(prev.slice(0, newCount)));
    }
  };

  // Обновление количества нижних контуров
  const updateBottomCircuitCount = (delta) => {
    const newCount = Math.max(0, params.bottomCircuitCount + delta);
    setParams((prev) => ({ ...prev, bottomCircuitCount: newCount }));
    
    if (delta > 0) {
      setBottomCircuits((prev) => {
        const added = [
          ...prev,
          ...Array(delta).fill(null).map((_, i) => ({
            id: prev.length + i + 1,
            type: 'fitting',
            diameter: 26.9,
            length: 80,
            fittingCategory: 'Ниппели',
            fittingId: 'Nippel_3.4',
            fittingLength: null,
            spacingToNext: null,
          })),
        ];
        return mergeFloorCircuitAutoSpacingToNext(added);
      });
    } else {
      setBottomCircuits((prev) => mergeFloorCircuitAutoSpacingToNext(prev.slice(0, newCount)));
    }
  };
  
  // Обновление общего межосевого (сбрасывает все индивидуальные)
  const updateCircuitSpacing = (value) => {
    const numValue = Number(value);
    if (Number.isFinite(numValue)) {
      setParams((prev) => ({ ...prev, circuitSpacing: numValue }));
      // Сбрасываем все индивидуальные межосевые
      setCircuits((prev) => prev.map(c => ({ ...c, spacingToNext: null })));
    }
  };
  
  const updateBottomCircuitSpacing = (value) => {
    const numValue = Number(value);
    if (Number.isFinite(numValue)) {
      setParams((prev) => ({ ...prev, bottomCircuitSpacing: numValue }));
      setBottomCircuits((prev) => prev.map(c => ({ ...c, spacingToNext: null })));
    }
  };

  // Обновление параметра верхнего контура (можно передать объект для обновления нескольких свойств)
  const updateCircuit = (index, keyOrObj, value) => {
    setCircuits((prev) => {
      let next = prev.map((c, i) => {
        if (i !== index) return c;
        if (typeof keyOrObj === 'object') {
          return { ...c, ...keyOrObj };
        }
        const key = keyOrObj;
        if (key === 'fittingId') {
          const fitting = fittingsData[value];
          return { ...c, [key]: value, diameter: fitting?.diameter || c.diameter };
        }
        return { ...c, [key]: typeof value === 'string' ? value : Number(value) };
      });
      if (shouldApplyFloorAutoSpacing(keyOrObj)) {
        next = mergeFloorCircuitAutoSpacingToNext(next);
      }
      return next;
    });
  };

  // Обновление параметра нижнего контура (можно передать объект для обновления нескольких свойств)
  const updateBottomCircuit = (index, keyOrObj, value) => {
    setBottomCircuits((prev) => {
      let next = prev.map((c, i) => {
        if (i !== index) return c;
        if (typeof keyOrObj === 'object') {
          return { ...c, ...keyOrObj };
        }
        const key = keyOrObj;
        if (key === 'fittingId') {
          const fitting = fittingsData[value];
          return { ...c, [key]: value, diameter: fitting?.diameter || c.diameter };
        }
        return { ...c, [key]: typeof value === 'string' ? value : Number(value) };
      });
      if (shouldApplyFloorAutoSpacing(keyOrObj)) {
        next = mergeFloorCircuitAutoSpacingToNext(next);
      }
      return next;
    });
  };

  // Удаление контура
  const deleteCircuit = (index) => {
    setCircuits((prev) => {
      const newCircuits = prev.filter((_, i) => i !== index);
      return mergeFloorCircuitAutoSpacingToNext(newCircuits.map((c, i) => ({ ...c, id: i + 1 })));
    });
    // Уменьшаем счетчик контуров
    setParams((prev) => ({ ...prev, circuitCount: Math.max(0, prev.circuitCount - 1) }));
  };

  // Перемещение контура
  const moveCircuit = (fromIndex, toIndex) => {
    setCircuits((prev) => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return mergeFloorCircuitAutoSpacingToNext(newCircuits.map((c, i) => ({ ...c, id: i + 1 })));
    });
  };

  // Удаление нижнего контура
  const deleteBottomCircuit = (index) => {
    setBottomCircuits((prev) => {
      const newCircuits = prev.filter((_, i) => i !== index);
      return mergeFloorCircuitAutoSpacingToNext(newCircuits.map((c, i) => ({ ...c, id: i + 1 })));
    });
    // Уменьшаем счетчик контуров
    setParams((prev) => ({ ...prev, bottomCircuitCount: Math.max(0, prev.bottomCircuitCount - 1) }));
  };

  // Перемещение нижнего контура
  const moveBottomCircuit = (fromIndex, toIndex) => {
    setBottomCircuits((prev) => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return mergeFloorCircuitAutoSpacingToNext(newCircuits.map((c, i) => ({ ...c, id: i + 1 })));
    });
  };

  // === Функции для центральных фитингов (на самой гребенке) ===
  
  // Обновление количества центральных фитингов
  const updateCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.centerFittingCount + delta);
    setParams((prev) => ({ ...prev, centerFittingCount: newCount }));
    
    if (delta > 0) {
      setCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2', // 1/2"
          positionType: 'center', // 'distance', 'center', 'between'
          side: 'both', // 'both', 'front', 'back'
          distanceFromStart: 50, // Для типа 'distance'
          distanceDirection: 'left', // 'left' (слева) или 'right' (справа)
          betweenIndex: 0, // Для типа 'between' (0 = между 1 и 2, 1 = между 2 и 3 и т.д.)
          referenceRow: 'top', // 'top' или 'bottom' - относительно какой трубы рассчитывать позицию
        })),
      ]);
    } else {
      setCenterFittings((prev) => prev.slice(0, newCount));
    }
  };
  
  // Обновление параметра центрального фитинга
  const updateCenterFitting = (index, keyOrObj, value) => {
    setCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };
  
  // Удаление центрального фитинга
  const deleteCenterFitting = (index) => {
    setCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, centerFittingCount: Math.max(0, prev.centerFittingCount - 1) }));
  };

  // Вычисляем эффективный отступ первого верхнего контура (может зависеть от нижнего контура)
  const effectiveFirstCircuitOffset = useMemo(() => {
    // Если режим "от нижнего контура" включен
    if (params.firstCircuitOffsetMode === 'fromBottomCircuit') {
      // Проверяем наличие нижних контуров
      if (bottomCircuits.length === 0) {
        return params.firstCircuitOffset;
      }
      
      // Вычисляем позицию выбранного нижнего контура
      const referenceIndex = params.firstCircuitOffsetReferenceIndex || 0;
      
      if (referenceIndex >= bottomCircuits.length) {
        return params.bottomFirstCircuitOffset + (params.firstCircuitOffset || 0);
      }
      
      // Вычисляем позицию выбранного нижнего контура
      let referenceCircuitPosition = params.bottomFirstCircuitOffset;
      
      for (let i = 0; i < referenceIndex; i++) {
        const spacing = Number(bottomCircuits[i]?.spacingToNext) || params.bottomCircuitSpacing;
        referenceCircuitPosition += spacing;
      }
      
      return referenceCircuitPosition + (params.firstCircuitOffset || 0);
    }
    
    return params.firstCircuitOffset;
  }, [
    params.firstCircuitOffsetMode, 
    params.firstCircuitOffset, 
    params.firstCircuitOffsetReferenceIndex,
    params.bottomFirstCircuitOffset,
    params.bottomCircuitSpacing,
    bottomCircuits
  ]);

  // Вычисляем эффективный отступ первого нижнего контура (может зависеть от верхнего контура)
  const effectiveBottomFirstCircuitOffset = useMemo(() => {
    // Если режим "от верхнего контура" включен
    if (params.bottomFirstCircuitOffsetMode === 'fromTopCircuit') {
      // Проверяем наличие верхних контуров
      if (circuits.length === 0) {
        return params.bottomFirstCircuitOffset;
      }
      
      // Вычисляем позицию выбранного верхнего контура
      const referenceIndex = params.bottomFirstCircuitOffsetReferenceIndex || 0;
      
      if (referenceIndex >= circuits.length) {
        return effectiveFirstCircuitOffset + (params.bottomFirstCircuitOffset || 0);
      }
      
      // Вычисляем позицию выбранного верхнего контура
      let referenceCircuitPosition = effectiveFirstCircuitOffset;
      
      for (let i = 0; i < referenceIndex; i++) {
        const spacing = Number(circuits[i]?.spacingToNext) || params.circuitSpacing;
        referenceCircuitPosition += spacing;
      }
      
      return referenceCircuitPosition + (params.bottomFirstCircuitOffset || 0);
    }
    
    return params.bottomFirstCircuitOffset;
  }, [
    params.bottomFirstCircuitOffsetMode, 
    params.bottomFirstCircuitOffset, 
    params.bottomFirstCircuitOffsetReferenceIndex,
    effectiveFirstCircuitOffset,
    params.circuitSpacing,
    circuits
  ]);

  // Вычисляем позицию последнего контура для верхних и нижних рядов
  const shouldUseTopOffset = useMemo(() => {
    // Позиция конца верхних контуров (без учета последнего отступа)
    let topEnd = effectiveFirstCircuitOffset;
    if (params.circuitCount > 0) {
      for (let i = 0; i < params.circuitCount - 1; i++) {
        const spacing = Number(circuits[i]?.spacingToNext) || params.circuitSpacing;
        topEnd += spacing;
      }
    }
    
    // Позиция конца нижних контуров (без учета последнего отступа)
    let bottomEnd = effectiveBottomFirstCircuitOffset;
    if (params.bottomCircuitCount > 0) {
      for (let i = 0; i < params.bottomCircuitCount - 1; i++) {
        const spacing = Number(bottomCircuits[i]?.spacingToNext) || params.bottomCircuitSpacing;
        bottomEnd += spacing;
      }
    }
    
    // Определяем, какой ряд дальше
    // Если один из рядов пустой, используем отступ непустого ряда
    if (params.circuitCount === 0 && params.bottomCircuitCount > 0) {
      return false;
    }
    if (params.bottomCircuitCount === 0 && params.circuitCount > 0) {
      return true;
    }
    
    // Если оба ряда есть, используем отступ более дальнего
    return topEnd >= bottomEnd;
  }, [
    effectiveFirstCircuitOffset,
    effectiveBottomFirstCircuitOffset,
    params.circuitCount,
    params.bottomCircuitCount,
    params.circuitSpacing,
    params.bottomCircuitSpacing,
    circuits,
    bottomCircuits
  ]);

  // Автоматический расчёт длины основной трубы (максимум из верхних и нижних)
  const calculatedMainLength = useMemo(() => {
    // Если включен ручной режим, используем заданное значение
    if (params.useManualLength) {
      return params.manualLength || 150;
    }
    
    // Определяем, какой отступ последнего контура использовать
    const effectiveLastOffset = shouldUseTopOffset ? params.lastCircuitOffset : params.bottomLastCircuitOffset;
    
    // Верхние контуры: первый отступ + сумма межосевых + последний отступ
    let topLength = 0;
    if (params.circuitCount > 0) {
      topLength = effectiveFirstCircuitOffset;
      // Сумма межосевых (индивидуальных или общих)
      for (let i = 0; i < params.circuitCount - 1; i++) {
        const spacing = Number(circuits[i]?.spacingToNext) || params.circuitSpacing;
        topLength += spacing;
      }
      topLength += effectiveLastOffset;
    }

    // Нижние контуры
    let bottomLength = 0;
    if (params.bottomCircuitCount > 0) {
      bottomLength = effectiveBottomFirstCircuitOffset;
      for (let i = 0; i < params.bottomCircuitCount - 1; i++) {
        const spacing = Number(bottomCircuits[i]?.spacingToNext) || params.bottomCircuitSpacing;
        bottomLength += spacing;
      }
      bottomLength += effectiveLastOffset;
    }

    // Если оба массива контуров пустые, используем минимум 100мм
    // Иначе минимум 120мм для совместимости с существующими контурами
    const minLength = (params.circuitCount === 0 && params.bottomCircuitCount === 0) ? 100 : 120;
    return Math.max(topLength, bottomLength, minLength);
  }, [params.useManualLength, params.manualLength, effectiveFirstCircuitOffset, params.lastCircuitOffset, params.circuitCount, params.circuitSpacing, circuits,
      effectiveBottomFirstCircuitOffset, params.bottomLastCircuitOffset, params.bottomCircuitCount, params.bottomCircuitSpacing, bottomCircuits, shouldUseTopOffset]);

  // Автоматическое отключение привязки при удалении всех контуров
  useEffect(() => {
    // Если включена привязка верхних к нижним контурам, но нижних контуров больше нет
    if (params.firstCircuitOffsetMode === 'fromBottomCircuit' && bottomCircuits.length === 0) {
      setParams(prev => ({
        ...prev,
        firstCircuitOffsetMode: 'fromEdge',
        firstCircuitOffset: prev.firstCircuitOffsetSaved !== null 
          ? prev.firstCircuitOffsetSaved 
          : prev.firstCircuitOffset,
      }));
    }
    
    // Если включена привязка нижних к верхним контурам, но верхних контуров больше нет
    if (params.bottomFirstCircuitOffsetMode === 'fromTopCircuit' && circuits.length === 0) {
      setParams(prev => ({
        ...prev,
        bottomFirstCircuitOffsetMode: 'fromEdge',
        bottomFirstCircuitOffset: prev.bottomFirstCircuitOffsetSaved !== null 
          ? prev.bottomFirstCircuitOffsetSaved 
          : prev.bottomFirstCircuitOffset,
      }));
    }
  }, [
    bottomCircuits.length, 
    circuits.length,
    params.firstCircuitOffsetMode, 
    params.bottomFirstCircuitOffsetMode,
    params.firstCircuitOffsetSaved,
    params.bottomFirstCircuitOffsetSaved,
    params.firstCircuitOffset,
    params.bottomFirstCircuitOffset,
    setParams
  ]);

  // Вспомогательная функция для расчета длины подключения (только для UI)
  const getConnectionLengthForUI = useCallback((connectionFitting, connectionFittingLength, connectionLength, isLeftConnection) => {
    // Если задана длина вручную, используем её
    if (connectionLength !== null && connectionLength !== undefined) {
      return connectionLength;
    }
    
    // Ниппели - либо длина из каталога, либо заданная вручную
    if (connectionFitting?.startsWith('Nippel_')) {
      if (connectionFittingLength !== null && connectionFittingLength !== undefined) {
        return connectionFittingLength;
      }
      const fittingData = fittingsData[connectionFitting];
      return fittingData?.length || 0;
    }
    
    // Муфты на торце режутся пополам: авто — длина после резки
    if (connectionFitting?.startsWith('Mufta_') || connectionFitting?.startsWith('Nippel_Mufta_')) {
      const fittingData = fittingsData[connectionFitting];
      return endFittingAutoLength(connectionFitting, connectionFittingLength, fittingData?.length || 0);
    }
    
    // Фланцы - зависит от соотношения Ду фланца и диаметра основной трубы
    if (connectionFitting?.startsWith('Flanec_')) {
      const flangeDn = extractDnFromFlangeKey(connectionFitting);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      
      // Для квадратных профилей всегда используем дефолтную длину 100 мм
      // (нет логики "одинаковых диаметров")
      if (params.shape === 'square') {
        return 100;
      }
      
      // Для круглых труб: если Ду фланца совпадает с диаметром основной трубы - не добавляем длину
      if (flangePipeDiameter && flangePipeDiameter === params.mainPipeDiameter) {
        return 0;
      }
      
      // Если Ду фланца меньше диаметра основной трубы - 100 мм (дефолт)
      // Эта длина уже учитывает заглушку/переход (не нужно их прибавлять отдельно)
      // (собственное значение уже проверено выше через connectionLength)
      if (flangePipeDiameter && flangePipeDiameter < params.mainPipeDiameter) {
        return 100;
      }
      
      // В остальных случаях (если не удалось определить диаметр)
      return 100;
    }
    
    // Накидные гайки - дефолт 58 для квадратных, 40 для круглых
    if (connectionFitting?.startsWith('Nak_Gaika_')) {
      return params.shape === 'square' ? 58 : 40;
    }
    
    // Шайбы с резьбой - дефолт 58 для квадратных, 40 для круглых
    if (connectionFitting?.startsWith('Shaib_Nar_Rezba_')) {
      return params.shape === 'square' ? 58 : 40;
    }
    
    return 0;
  }, [params.mainPipeDiameter, params.shape]);

  // Расчет общей длины коллектора (для отображения в UI)
  const calculatedTotalLength = useMemo(() => {
    let totalLength = calculatedMainLength;
    
    // Вспомогательная функция для проверки, является ли фланец с меньшим диаметром
    const isFlangeWithSmallerDiameter = (connectionFitting) => {
      if (!connectionFitting?.startsWith('Flanec_')) {
        return false;
      }
      // Для квадратных профилей всегда считаем, что фланец требует вылет
      if (params.shape === 'square') {
        return true;
      }
      // Для круглых труб проверяем диаметр
      const flangeDn = extractDnFromFlangeKey(connectionFitting);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      return flangePipeDiameter && flangePipeDiameter < params.mainPipeDiameter;
    };
    
    // Добавляем левое подключение
    if (params.hasLeftConnection) {
      if (params.leftConnectionType === 'pipe') {
        totalLength += params.leftConnectionLength || 100;
      } else if (params.leftConnectionType === 'fitting') {
        totalLength += getConnectionLengthForUI(
          params.leftConnectionFitting,
          params.leftConnectionFittingLength,
          params.leftConnectionLength,
          true
        );
      }
    }
    
    // Добавляем правое подключение
    if (params.hasRightConnection) {
      if (params.rightConnectionType === 'pipe') {
        totalLength += params.rightConnectionLength || 100;
      } else if (params.rightConnectionType === 'fitting') {
        totalLength += getConnectionLengthForUI(
          params.rightConnectionFitting,
          params.rightConnectionFittingLength,
          params.rightConnectionLength,
          false
        );
      }
    }
    
    // Проверяем, есть ли слева фланец с меньшим диаметром
    const hasLeftFlangeSmaller = params.hasLeftConnection && 
                                  params.leftConnectionType === 'fitting' &&
                                  isFlangeWithSmallerDiameter(params.leftConnectionFitting);
    
    // Проверяем, есть ли справа фланец с меньшим диаметром
    const hasRightFlangeSmaller = params.hasRightConnection && 
                                   params.rightConnectionType === 'fitting' &&
                                   isFlangeWithSmallerDiameter(params.rightConnectionFitting);
    
    // Добавляем левую заглушку/переход (если выступают наружу)
    // НО не добавляем, если слева фланец с меньшим диаметром (уже учтено в 100 мм)
    if (params.hasLeftCap && !hasLeftFlangeSmaller) {
      if (params.leftCapElementType === 'reducer') {
        // Переход выступает наружу - добавляем его длину
        const reducerData = fittingsData[params.leftReducer];
        if (reducerData) {
          totalLength += reducerData.length;
        }
      } else if (params.leftCapType === 'elliptical') {
        // Эллиптическая заглушка выступает наружу
        const capKey = `Zaglushka_EL_${params.mainPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          totalLength += capData.length;
        }
      }
      // Прямые заглушки уже включены в calculatedMainLength
    }
    
    // Добавляем правую заглушку/переход
    // НО не добавляем, если справа фланец с меньшим диаметром (уже учтено в 100 мм)
    if (params.hasRightCap && !hasRightFlangeSmaller) {
      if (params.rightCapElementType === 'reducer') {
        const reducerData = fittingsData[params.rightReducer];
        if (reducerData) {
          totalLength += reducerData.length;
        }
      } else if (params.rightCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.mainPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          totalLength += capData.length;
        }
      }
    }
    
    return Math.round(totalLength);
  }, [
    calculatedMainLength,
    params.hasLeftConnection,
    params.leftConnectionType,
    params.leftConnectionLength,
    params.leftConnectionFitting,
    params.leftConnectionFittingLength,
    params.hasRightConnection,
    params.rightConnectionType,
    params.rightConnectionLength,
    params.rightConnectionFitting,
    params.rightConnectionFittingLength,
    params.hasLeftCap,
    params.leftCapElementType,
    params.leftCapType,
    params.leftReducer,
    params.mainPipeDiameter,
    params.shape,
    params.hasRightCap,
    params.rightCapElementType,
    params.rightCapType,
    params.rightReducer,
    getConnectionLengthForUI,
  ]);

  // Вычисляем отступ последнего контура в ручном режиме (обратный расчет)
  const { lastCircuitOffsetCalculated, bottomLastCircuitOffsetCalculated } = useMemo(() => {
    if (!params.useManualLength) {
      return {
        lastCircuitOffsetCalculated: params.lastCircuitOffset,
        bottomLastCircuitOffsetCalculated: params.bottomLastCircuitOffset,
      };
    }
    
    // Для верхних контуров
    let topRemainder = params.manualLength || 150;
    topRemainder -= params.firstCircuitOffset;
    
    for (let i = 0; i < params.circuitCount - 1; i++) {
      const spacing = Number(circuits[i]?.spacingToNext) || params.circuitSpacing;
      topRemainder -= spacing;
    }
    
    // Для нижних контуров
    let bottomRemainder = params.manualLength || 150;
    bottomRemainder -= params.bottomFirstCircuitOffset;
    
    for (let i = 0; i < params.bottomCircuitCount - 1; i++) {
      const spacing = Number(bottomCircuits[i]?.spacingToNext) || params.bottomCircuitSpacing;
      bottomRemainder -= spacing;
    }
    
    return {
      lastCircuitOffsetCalculated: Math.max(topRemainder, 0),
      bottomLastCircuitOffsetCalculated: Math.max(bottomRemainder, 0),
    };
  }, [params.useManualLength, params.manualLength, params.firstCircuitOffset, params.lastCircuitOffset, params.circuitCount, params.circuitSpacing, circuits,
      params.bottomFirstCircuitOffset, params.bottomLastCircuitOffset, params.bottomCircuitCount, params.bottomCircuitSpacing, bottomCircuits]);

  // Сериализация ключевых полей центральных фитингов для отслеживания изменений
  const centerFittingsKey = useMemo(() => 
    JSON.stringify(centerFittings.map(f => ({ 
      id: f.id, 
      positionType: f.positionType, 
      distanceFromStart: f.distanceFromStart, 
      distanceDirection: f.distanceDirection, 
      betweenIndex: f.betweenIndex, 
      referenceRow: f.referenceRow 
    }))),
    [centerFittings]
  );

  // Динамическое превью схемы
  const schematicPreview = useMemo(() => {
    const svgWidth = 450;
    const svgHeight = 220;
    const padding = 40;
    
    // Дополнительная длина для входов/выходов
    const leftExtra = params.hasLeftConnection ? 60 : 0;
    const rightExtra = params.hasRightConnection ? 60 : 0;
    const totalLength = calculatedMainLength + leftExtra + rightExtra;
    
    // Масштабирование
    const scale = Math.min(
      (svgWidth - padding * 2) / Math.max(totalLength, 200),
      1
    );
    
    const mainY = svgHeight / 2;
    const mainStartX = padding + leftExtra * scale;
    const mainEndX = mainStartX + calculatedMainLength * scale;
    
    // Позиции верхних контуров (с учётом индивидуальных межосевых)
    let previewCurrentX = effectiveFirstCircuitOffset;
    const circuitPositions = circuits.map((circuit, i) => {
      const pos = mainStartX + previewCurrentX * scale;
      if (i < circuits.length - 1) {
        previewCurrentX += Number(circuit.spacingToNext) || params.circuitSpacing;
      }
      return pos;
    });
    
    // Позиции нижних контуров (с учётом индивидуальных межосевых)
    let previewBottomCurrentX = effectiveBottomFirstCircuitOffset;
    const bottomCircuitPositions = bottomCircuits.map((circuit, i) => {
      const pos = mainStartX + previewBottomCurrentX * scale;
      if (i < bottomCircuits.length - 1) {
        previewBottomCurrentX += Number(circuit.spacingToNext) || params.bottomCircuitSpacing;
      }
      return pos;
    });
    
    return (
      <svg 
        viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
        style={{ width: '100%', height: '100%', maxHeight: 220 }}
      >
        {/* Левая заглушка/переход - всегда снаружи */}
        {params.hasLeftCap && (
          <g>
            {params.leftCapElementType === 'reducer' ? (
              // Переход конц. - трапеция
              <polygon
                points={`${mainStartX - 12},${mainY - 6} ${mainStartX},${mainY - 4} ${mainStartX},${mainY + 4} ${mainStartX - 12},${mainY + 6}`}
                fill="#424242"
              />
            ) : (
              // Заглушка
              <rect
                x={mainStartX - 8}
                y={mainY - 3}
                width={8}
                height={6}
                fill="#bdbdbd"
                rx={params.leftCapType === 'elliptical' ? 2 : 0}
              />
            )}
          </g>
        )}
        
        {/* Правая заглушка - всегда снаружи */}
        {/* Правая заглушка/переход - всегда снаружи */}
        {params.hasRightCap && (
          <g>
            {params.rightCapElementType === 'reducer' ? (
              // Переход конц. - трапеция
              <polygon
                points={`${mainEndX},${mainY - 4} ${mainEndX + 12},${mainY - 6} ${mainEndX + 12},${mainY + 6} ${mainEndX},${mainY + 4}`}
                fill="#424242"
              />
            ) : (
              // Заглушка
              <rect
                x={mainEndX}
                y={mainY - 3}
                width={8}
                height={6}
                fill="#bdbdbd"
                rx={params.rightCapType === 'elliptical' ? 2 : 0}
              />
            )}
          </g>
        )}
        
        {/* Левое подключение */}
        {params.hasLeftConnection && (
          <g>
            {/* Все заглушки снаружи, но для прямых - подключение от края заглушки */}
            <line
              x1={padding}
              y1={mainY}
              x2={mainStartX - (params.hasLeftCap ? 8 : 0)}
              y2={mainY}
              stroke="#d32f2f"
              strokeWidth={2}
              strokeLinecap="round"
            />
            <text x={padding + 5} y={mainY - 10} fontSize={9} fill="#d32f2f">
              {params.leftConnectionType === 'pipe' ? `⌀${params.leftConnectionDiameter}` : 'Фитинг'}
            </text>
          </g>
        )}
        
        {/* Правое подключение */}
        {params.hasRightConnection && (
          <g>
            {/* Все заглушки снаружи, но для прямых - подключение от края заглушки */}
            <line
              x1={mainEndX + (params.hasRightCap ? 8 : 0)}
              y1={mainY}
              x2={mainEndX + rightExtra * scale}
              y2={mainY}
              stroke="#d32f2f"
              strokeWidth={2}
              strokeLinecap="round"
            />
            <text x={mainEndX + 5} y={mainY - 10} fontSize={9} fill="#d32f2f">
              {params.rightConnectionType === 'pipe' ? `⌀${params.rightConnectionDiameter}` : 'Фитинг'}
            </text>
          </g>
        )}
        
        {/* Основная труба (гребёнка) */}
        {params.shape === 'square' ? (
          // Квадратная труба - рисуем прямоугольник
          <rect
            x={mainStartX}
            y={mainY - 3}
            width={mainEndX - mainStartX}
            height={6}
            fill="rgba(211, 47, 47, 0.15)"
            stroke="#d32f2f"
            strokeWidth={2}
          />
        ) : (
          // Круглая труба - рисуем линию
          <line
            x1={mainStartX}
            y1={mainY}
            x2={mainEndX}
            y2={mainY}
            stroke="#d32f2f"
            strokeWidth={6}
            strokeLinecap="round"
          />
        )}
        
        {/* Верхние отводы контуров */}
        {circuitPositions.map((x, i) => {
          const circuitData = circuits[i];
          const isFlange = circuitData?.type === 'fitting' && circuitData?.fittingId?.startsWith('Flanec_');
          const isFreeFlange = isFlange && !circuitData?.fittingId?.endsWith('_welded');
          const isNakGaika = circuitData?.type === 'fitting' && circuitData?.fittingId?.startsWith('Nak_Gaika_');
          const isShaibaNarRezba = circuitData?.type === 'fitting' && circuitData?.fittingId?.startsWith('Shaib_Nar_Rezba_');
          
          // Для фланцев/накидных гаек/шайб с резьбой используем flangePipeLength, для остальных — длину фитинга или трубы
          const circuitLength = (isFlange || isNakGaika || isShaibaNarRezba)
            ? (circuitData.flangePipeLength ?? (isFlange ? 100 : (params.shape === 'square' ? 58 : 40)))
            : circuitData?.type === 'fitting' 
              ? (circuitData.fittingLength ?? fittingsData[circuitData.fittingId]?.length ?? 50)
              : (circuitData?.length || 80);
          const circuitY = mainY - circuitLength * scale * 0.8;
          const isFitting = circuitData?.type === 'fitting';
          
          return (
            <g key={`top-${i}`}>
              {isFlange ? (
                /* Фланцевый контур: трубка + бортшайба (для свободного) + фланец */
                <>
                  {/* Трубка */}
                  <line
                    x1={x}
                    y1={mainY}
                    x2={x}
                    y2={circuitY + 8}
                    stroke="#424242"
                    strokeWidth={Math.max(2, (circuitData?.diameter || 26.9) * scale * 0.06)}
                    strokeLinecap="round"
                  />
                  {/* Бортшайба (для свободного фланца) */}
                  {isFreeFlange && (
                    <rect
                      x={x - 6}
                      y={circuitY + 4}
                      width={12}
                      height={4}
                      fill="#bdbdbd"
                      rx={1}
                    />
                  )}
                  {/* Фланец */}
                  <rect
                    x={x - 8}
                    y={circuitY}
                    width={16}
                    height={8}
                    fill="#424242"
                    rx={1}
                  />
                </>
              ) : (
                <line
                  x1={x}
                  y1={mainY}
                  x2={x}
                  y2={circuitY}
                  stroke={isFitting ? '#424242' : '#d32f2f'}
                  strokeWidth={Math.max(2, (circuitData?.diameter || 26.9) * scale * 0.06)}
                  strokeLinecap="round"
                />
              )}
              <text
                x={x}
                y={circuitY - 8}
                textAnchor="middle"
                fontSize={11}
                fill="#333"
                fontWeight="500"
              >
                {i + 1}
              </text>
            </g>
          );
        })}
        
        {/* Нижние отводы контуров */}
        {bottomCircuitPositions.map((x, i) => {
          const circuitData = bottomCircuits[i];
          const isFlange = circuitData?.type === 'fitting' && circuitData?.fittingId?.startsWith('Flanec_');
          const isFreeFlange = isFlange && !circuitData?.fittingId?.endsWith('_welded');
          const isNakGaika = circuitData?.type === 'fitting' && circuitData?.fittingId?.startsWith('Nak_Gaika_');
          const isShaibaNarRezba = circuitData?.type === 'fitting' && circuitData?.fittingId?.startsWith('Shaib_Nar_Rezba_');
          
          // Для фланцев/накидных гаек/шайб с резьбой используем flangePipeLength, для остальных — длину фитинга или трубы
          const circuitLength = (isFlange || isNakGaika || isShaibaNarRezba)
            ? (circuitData.flangePipeLength ?? (isFlange ? 100 : (params.shape === 'square' ? 58 : 40)))
            : circuitData?.type === 'fitting' 
              ? (circuitData.fittingLength ?? fittingsData[circuitData.fittingId]?.length ?? 50)
              : (circuitData?.length || 80);
          const circuitY = mainY + circuitLength * scale * 0.8;
          const isFitting = circuitData?.type === 'fitting';
          
          return (
            <g key={`bottom-${i}`}>
              {isFlange ? (
                /* Фланцевый контур: трубка + бортшайба (для свободного) + фланец */
                <>
                  {/* Трубка */}
                  <line
                    x1={x}
                    y1={mainY}
                    x2={x}
                    y2={circuitY - 8}
                    stroke="#424242"
                    strokeWidth={Math.max(2, (circuitData?.diameter || 26.9) * scale * 0.06)}
                    strokeLinecap="round"
                  />
                  {/* Бортшайба (для свободного фланца) */}
                  {isFreeFlange && (
                    <rect
                      x={x - 6}
                      y={circuitY - 8}
                      width={12}
                      height={4}
                      fill="#bdbdbd"
                      rx={1}
                    />
                  )}
                  {/* Фланец */}
                  <rect
                    x={x - 8}
                    y={circuitY - 4}
                    width={16}
                    height={8}
                    fill="#424242"
                    rx={1}
                  />
                </>
              ) : (
                <line
                  x1={x}
                  y1={mainY}
                  x2={x}
                  y2={circuitY}
                  stroke={isFitting ? '#424242' : '#212121'}
                  strokeWidth={Math.max(2, (circuitData?.diameter || 26.9) * scale * 0.06)}
                  strokeLinecap="round"
                />
              )}
              <text
                x={x}
                y={circuitY + 15}
                textAnchor="middle"
                fontSize={11}
                fill="#333"
                fontWeight="500"
              >
                {i + 1}
              </text>
            </g>
          );
        })}
        
        {/* Центральные фитинги (на гребенке) */}
        {centerFittings.map((fitting, i) => {
          const fittingInfo = fittingsData[fitting.fittingId];
          if (!fittingInfo) return null;
          
          const positionType = fitting.positionType || 'center';
          const centerX = (mainStartX + mainEndX) / 2;
          let fittingX;
          
          // Вычисляем позицию X в зависимости от типа размещения
          if (positionType === 'center') {
            // По середине гребенки
            fittingX = centerX;
          } else if (positionType === 'between') {
            // Между контурами
            // betweenIndex - это индекс (0, 1, 2...), который обозначает:
            // 0 = между 1 и 2, 1 = между 2 и 3 и т.д.
            const betweenIndex = Number(fitting.betweenIndex) ?? 0;
            const referenceRow = fitting.referenceRow || 'top';
            
            // Выбираем правильный массив позиций в зависимости от выбранной трубы
            const positions = referenceRow === 'bottom' ? bottomCircuitPositions : circuitPositions;
            
            // Используем реальные позиции контуров
            if (betweenIndex < positions.length - 1) {
              const x1 = positions[betweenIndex];
              const x2 = positions[betweenIndex + 1];
              fittingX = (x1 + x2) / 2;
            } else {
              fittingX = centerX;
            }
          } else {
            // На расстоянии от начала или конца
            const distanceFromStart = Number(fitting.distanceFromStart) || 50;
            const direction = fitting.distanceDirection || 'left';
            
            if (direction === 'right') {
              // Отсчёт справа налево
              fittingX = mainEndX - distanceFromStart * scale;
            } else {
              // Отсчёт слева направо (по умолчанию)
              fittingX = mainStartX + distanceFromStart * scale;
            }
          }
          
          const fittingLength = fittingInfo.diameter || 30; // Используем диаметр для selfFacing
          
          // Для "с 2-х сторон" рисуем два круга
          const side = fitting.side || 'both';
          const fittingCount = side === 'both' ? 2 : 1;
          
          return (
            <g key={`center-${i}`}>
              {Array.from({ length: fittingCount }).map((_, idx) => (
                <circle
                  key={`fitting-${idx}`}
                  cx={fittingX}
                  cy={mainY}
                  r={Math.max(3, fittingLength * scale * 0.15)}
                  fill="none"
                  stroke="#424242"
                  strokeWidth={2}
                  opacity={idx === 1 ? 0.6 : 1} // Второй фитинг чуть прозрачнее
                />
              ))}
              <text
                x={fittingX}
                y={mainY - Math.max(5, fittingLength * scale * 0.15) - 5}
                textAnchor="middle"
                fontSize={9}
                fill="#424242"
                fontWeight="500"
              >
                {side === 'both' ? 'T×2' : side === 'back' ? 'T(з)' : 'T'}
              </text>
            </g>
          );
        })}
        
        {/* Размерные линии */}
        <line
          x1={mainStartX}
          y1={mainY + 35}
          x2={mainEndX}
          y2={mainY + 35}
          stroke="#999"
          strokeWidth={1}
          markerEnd="url(#arrowhead)"
          markerStart="url(#arrowhead)"
        />
        <text
          x={(mainStartX + mainEndX) / 2}
          y={mainY + 50}
          textAnchor="middle"
          fontSize={11}
          fill="#666"
        >
          {Math.round(calculatedMainLength)} мм
        </text>
        
        {/* Маркер стрелки */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="6"
            markerHeight="6"
            refX="3"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill="#999" />
          </marker>
        </defs>
      </svg>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, circuits, bottomCircuits, calculatedMainLength, centerFittingsKey]);

  // Генерация чертежа
  const handleGenerate = () => {
    // Проверяем совместимость всех фитингов в контурах перед генерацией
    const incompatibleCircuits = circuits.filter((circuit) => {
      if (circuit.type !== 'fitting') return false;
      if (params.shape === 'square') {
        return !isFittingCompatibleWithSquareProfile(circuit.fittingId, params.mainPipeProfile);
      }
      return !isFittingCompatibleWithMainPipe(circuit.fittingId, params.mainPipeDiameter);
    });
    const incompatibleBottomCircuits = bottomCircuits.filter((circuit) => {
      if (circuit.type !== 'fitting') return false;
      if (params.shape === 'square') {
        return !isFittingCompatibleWithSquareProfile(circuit.fittingId, params.mainPipeProfile);
      }
      return !isFittingCompatibleWithMainPipe(circuit.fittingId, params.mainPipeDiameter);
    });
    const incompatibleCenterFittings = centerFittings.filter((fitting) => {
      if (params.shape === 'square') {
        return !isFittingCompatibleWithSquareProfile(fitting.fittingId, params.mainPipeProfile);
      }
      return !isFittingCompatibleWithMainPipe(fitting.fittingId, params.mainPipeDiameter);
    });
    
    if (incompatibleCircuits.length > 0 || incompatibleBottomCircuits.length > 0 || incompatibleCenterFittings.length > 0) {
      setIncompatibilityDialog({
        open: true,
        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
      });
      return;
    }
    
    // Нормализуем параметры: заменяем пустые строки на числа
    const normalizeValue = (value, defaultValue) => {
      if (value === '' || value === null || value === undefined) {
        return defaultValue;
      }
      const num = Number(value);
      return Number.isFinite(num) ? num : defaultValue;
    };
    
    // Определяем дефолтные значения для торцевых подключений в зависимости от типа фитинга
    const getLeftConnectionDefault = () => {
      if (params.leftConnectionFitting?.startsWith('Nak_Gaika_') || params.leftConnectionFitting?.startsWith('Shaib_Nar_Rezba_')) {
        return params.shape === 'square' ? 58 : 40;
      }
      return 100;
    };
    
    const getRightConnectionDefault = () => {
      if (params.rightConnectionFitting?.startsWith('Nak_Gaika_') || params.rightConnectionFitting?.startsWith('Shaib_Nar_Rezba_')) {
        return params.shape === 'square' ? 58 : 40;
      }
      return 100;
    };
    
    // Если включен ручной режим, используем вычисленные значения отступов
    const effectiveParams = params.useManualLength ? {
      ...params,
      lastCircuitOffset: lastCircuitOffsetCalculated,
      bottomLastCircuitOffset: bottomLastCircuitOffsetCalculated,
    } : params;
    
    const normalizedParams = {
      ...effectiveParams,
      leftConnectionLength: normalizeValue(effectiveParams.leftConnectionLength, getLeftConnectionDefault()),
      rightConnectionLength: normalizeValue(effectiveParams.rightConnectionLength, getRightConnectionDefault()),
      firstCircuitOffset: normalizeValue(effectiveParams.firstCircuitOffset, 50),
      lastCircuitOffset: normalizeValue(effectiveParams.lastCircuitOffset, 50),
      circuitSpacing: normalizeValue(effectiveParams.circuitSpacing, 150),
      bottomFirstCircuitOffset: normalizeValue(effectiveParams.bottomFirstCircuitOffset, 50),
      bottomLastCircuitOffset: normalizeValue(effectiveParams.bottomLastCircuitOffset, 50),
      bottomCircuitSpacing: normalizeValue(effectiveParams.bottomCircuitSpacing, 150),
    };
    
    const normalizedCircuits = circuits.map(c => ({
      ...c,
      length: normalizeValue(c.length, 80),
      flangePipeLength: c.flangePipeLength !== null && c.flangePipeLength !== undefined 
        ? normalizeValue(c.flangePipeLength, 100) 
        : c.flangePipeLength,
    }));
    
    const normalizedBottomCircuits = bottomCircuits.map(c => ({
      ...c,
      length: normalizeValue(c.length, 80),
      flangePipeLength: c.flangePipeLength !== null && c.flangePipeLength !== undefined 
        ? normalizeValue(c.flangePipeLength, 100) 
        : c.flangePipeLength,
    }));
    
    const drawingData = generateFloorCollector({
      ...normalizedParams,
      mainPipeLength: calculatedMainLength,
      circuits: normalizedCircuits,
      bottomCircuits: normalizedBottomCircuits,
      centerFittings: centerFittings, // Передаем центральные фитинги
    }, effectiveFirstCircuitOffset, effectiveBottomFirstCircuitOffset, shouldUseTopOffset);
    // Передаём параметры для возможности вернуться
    const savedParams = {
      params,
      circuits,
      bottomCircuits,
      centerFittings, // Сохраняем центральные фитинги
    };
    onGenerate(drawingData, savedParams);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        py: 3,
        overflow: 'visible',
      }}
    >
      <Container maxWidth="xl" sx={{ overflow: 'visible' }}>
        {/* Шапка */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <IconButton
              onClick={onBack}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <HomeIcon />
            </IconButton>
            {onBackToCharacteristics && (
              <IconButton
                onClick={onBackToCharacteristics}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {onBackToDrawing && (
              <IconButton
                onClick={onBackToDrawing}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Этажный коллектор
                </Typography>
                {params._calculatedFrom === 'characteristics' && (
                  <Chip
                    label="Рассчитано из характеристик"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography color="text.secondary" variant="body2">
                Настройте параметры и сгенерируйте чертёж
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={params.shape}
              exclusive
              onChange={(e, newShape) => {
                if (newShape !== null) {
                  setParams(prev => {
                    const updates = { shape: newShape };
                    
                    // Для квадратных труб: только прямые заглушки, без переходов
                    if (newShape === 'square') {
                      updates.leftCapType = 'straight';
                      updates.rightCapType = 'straight';
                      updates.leftCapElementType = 'cap';
                      updates.rightCapElementType = 'cap';
                      
                      // Устанавливаем дефолтный профиль, если его нет
                      const profile = prev.mainPipeProfile || '60x60';
                      if (!prev.mainPipeProfile) {
                        updates.mainPipeProfile = profile;
                      }
                      
                      // Включаем заглушки, если они были выключены
                      if (!prev.hasLeftCap) {
                        updates.hasLeftCap = true;
                      }
                      if (!prev.hasRightCap) {
                        updates.hasRightCap = true;
                      }
                      
                      // Проверяем и обновляем левый торцевой фитинг
                      if (prev.leftConnectionFitting) {
                        const isCompatible = isEndFittingCompatibleWithSquareProfile(prev.leftConnectionFitting, profile);
                        if (!isCompatible) {
                          // Ищем ближайший совместимый фитинг в той же категории
                          const category = fittingsData[prev.leftConnectionFitting]?.category;
                          if (category) {
                            const compatibleFittings = getFittingsByCategory(category, null, 'square', profile)
                              .filter(f => f.isCompatible);
                            if (compatibleFittings.length > 0) {
                              updates.leftConnectionFitting = compatibleFittings[0].id;
                            }
                          }
                        }
                      }
                      
                      // Проверяем и обновляем правый торцевой фитинг
                      if (prev.rightConnectionFitting) {
                        const isCompatible = isEndFittingCompatibleWithSquareProfile(prev.rightConnectionFitting, profile);
                        if (!isCompatible) {
                          // Ищем ближайший совместимый фитинг в той же категории
                          const category = fittingsData[prev.rightConnectionFitting]?.category;
                          if (category) {
                            const compatibleFittings = getFittingsByCategory(category, null, 'square', profile)
                              .filter(f => f.isCompatible);
                            if (compatibleFittings.length > 0) {
                              updates.rightConnectionFitting = compatibleFittings[0].id;
                            }
                          }
                        }
                      }
                      
                      // Обновляем вылеты для накидных гаек и шайб с резьбой при смене профиля
                      // Принудительно сбрасываем на null для автоматического использования дефолта 58
                      const isLeftNakGaikaOrShaiba = prev.leftConnectionFitting?.startsWith('Nak_Gaika_') || 
                                                      prev.leftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
                      const isRightNakGaikaOrShaiba = prev.rightConnectionFitting?.startsWith('Nak_Gaika_') || 
                                                       prev.rightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
                      
                      if (isLeftNakGaikaOrShaiba) {
                        updates.leftConnectionLength = null; // Принудительно сбрасываем на null
                      }
                      if (isRightNakGaikaOrShaiba) {
                        updates.rightConnectionLength = null; // Принудительно сбрасываем на null
                      }
                    }
                    // Для круглых труб: возвращаем эллиптические заглушки по умолчанию
                    else if (newShape === 'round') {
                      updates.leftCapType = 'elliptical';
                      updates.rightCapType = 'elliptical';
                      
                      // Обновляем вылеты для накидных гаек и шайб с резьбой при смене профиля
                      // Принудительно сбрасываем на null для автоматического использования дефолта 40
                      const isLeftNakGaikaOrShaiba = prev.leftConnectionFitting?.startsWith('Nak_Gaika_') || 
                                                      prev.leftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
                      const isRightNakGaikaOrShaiba = prev.rightConnectionFitting?.startsWith('Nak_Gaika_') || 
                                                       prev.rightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
                      
                      if (isLeftNakGaikaOrShaiba) {
                        updates.leftConnectionLength = null; // Принудительно сбрасываем на null
                      }
                      if (isRightNakGaikaOrShaiba) {
                        updates.rightConnectionLength = null; // Принудительно сбрасываем на null
                      }
                    }
                    
                    return { ...prev, ...updates };
                  });
                }
              }}
              size="small"
              sx={{
                bgcolor: 'white',
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.5,
                  border: '0.0625rem solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="round" aria-label="круглая форма">
                <Tooltip title="Круглая форма">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RadioButtonUncheckedIcon fontSize="small" />
                    <Typography variant="body2">Круглый</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="square" aria-label="квадратная форма">
                <Tooltip title="Квадратная форма">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CropSquareIcon fontSize="small" />
                    <Typography variant="body2">Квадратный</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ overflow: 'visible' }}>
          {/* Левая панель — схема-превью */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Схема
              </Typography>
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  p: 2,
                  minHeight: 220,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                {schematicPreview}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Легенда */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip size="small" sx={{ bgcolor: '#d32f2f', color: 'white' }} label="Труба (подача)" />
                <Chip size="small" sx={{ bgcolor: '#212121', color: 'white' }} label="Труба (обратка)" />
                <Chip size="small" sx={{ bgcolor: '#424242', color: 'white' }} label="Фитинг/Фланец" />
                <Chip size="small" sx={{ bgcolor: '#bdbdbd', color: 'white' }} label="Заглушка" />
              </Box>
              
              {/* Информация о рассчитанных параметрах */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Рассчитанные параметры:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="body2">
                  <strong>Длина гребёнки:</strong> {calculatedMainLength} мм
                </Typography>
                <Typography variant="body2">
                  <strong>Подключений:</strong> {params.circuitCount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  <strong>Общая длина:</strong> {calculatedTotalLength} мм
                </Typography>
                {params.hasLeftConnection && (
                  <Typography variant="caption" color="text.secondary">
                    (Левое: {params.leftConnectionType === 'pipe' 
                      ? (params.leftConnectionLength || 100)
                      : getConnectionLengthForUI(params.leftConnectionFitting, params.leftConnectionFittingLength, params.leftConnectionLength, true)
                    } мм)
                  </Typography>
                )}
                {params.hasRightConnection && (
                  <Typography variant="caption" color="text.secondary">
                    (Правое: {params.rightConnectionType === 'pipe'
                      ? (params.rightConnectionLength || 100)
                      : getConnectionLengthForUI(params.rightConnectionFitting, params.rightConnectionFittingLength, params.rightConnectionLength, false)
                    } мм)
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Правая панель — параметры */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Параметры
              </Typography>

              {/* Основные параметры */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                Основная труба (гребёнка)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{params.shape === 'round' ? 'Диаметр' : 'Профиль'}</InputLabel>
                    <Select
                      value={params.shape === 'round' ? params.mainPipeDiameter : params.mainPipeProfile}
                      label={params.shape === 'round' ? 'Диаметр' : 'Профиль'}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (params.shape === 'round') {
                          const newDiameter = newValue;
                          // Существующая логика для круглых труб
                          const updates = { mainPipeDiameter: newDiameter };
                          
                          // Пересчитываем заглушки/переходы для левого подключения
                          if (params.leftConnectionFitting) {
                            const leftFitting = params.leftConnectionFitting;
                            const isNakGaika = leftFitting.startsWith('Nak_Gaika_');
                            const isShaibaNarRezba = leftFitting.startsWith('Shaib_Nar_Rezba_');
                            const isFlange = leftFitting.startsWith('Flanec_');
                            
                            if (isNakGaika || isShaibaNarRezba) {
                              let pipeDiameter;
                              if (isNakGaika) {
                                const size = extractSizeFromNakGaikaKey(leftFitting);
                                pipeDiameter = getPipeDiameterForNakGaika(size);
                              } else {
                                const size = extractSizeFromShaibaNarRezba(leftFitting);
                                pipeDiameter = getPipeDiameterForShaibaNarRezba(size);
                              }
                              
                              if (pipeDiameter && pipeDiameter !== newDiameter) {
                                // Диаметры НЕ совпадают
                                if (newDiameter === 48.3 && pipeDiameter === 42.4) {
                                  const reducer = findConcentricReducer(newDiameter, pipeDiameter);
                                  if (reducer) {
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'reducer';
                                    updates.leftReducer = reducer.key;
                                  } else {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap) {
                                      updates.hasLeftCap = true;
                                      updates.leftCapElementType = 'cap';
                                      updates.leftCapType = 'elliptical';
                                    }
                                  }
                                } else if ((newDiameter === 42.4 || newDiameter === 48.3) && 
                                           (pipeDiameter === 21.3 || pipeDiameter === 26.9 || pipeDiameter === 32)) {
                                  const straightCap = findStraightCap(newDiameter);
                                  if (straightCap) {
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'cap';
                                    updates.leftCapType = 'straight';
                                  }
                                } else {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap) {
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'cap';
                                    updates.leftCapType = 'elliptical';
                                  }
                                }
                              } else {
                                // Диаметры совпадают → закрываем заглушки/переходы
                                updates.hasLeftCap = false;
                              }
                            } else if (isFlange) {
                              const dn = extractDnFromFlangeKey(leftFitting);
                              const flangePipeDiameter = getPipeDiameterForDn(dn);
                              
                              if (flangePipeDiameter && flangePipeDiameter !== newDiameter) {
                                // Диаметры НЕ совпадают
                                if (newDiameter === 48.3 && flangePipeDiameter === 42.4) {
                                  const reducer = findConcentricReducer(newDiameter, flangePipeDiameter);
                                  if (reducer) {
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'reducer';
                                    updates.leftReducer = reducer.key;
                                  } else {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap) {
                                      updates.hasLeftCap = true;
                                      updates.leftCapElementType = 'cap';
                                      updates.leftCapType = 'elliptical';
                                    }
                                  }
                                } else if ((newDiameter === 42.4 || newDiameter === 48.3) && 
                                           (flangePipeDiameter === 21.3 || flangePipeDiameter === 26.9 || flangePipeDiameter === 32)) {
                                  const straightCap = findStraightCap(newDiameter);
                                  if (straightCap) {
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'cap';
                                    updates.leftCapType = 'straight';
                                  }
                                } else {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap) {
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'cap';
                                    updates.leftCapType = 'elliptical';
                                  }
                                }
                              } else {
                                // Диаметры совпадают → закрываем заглушки/переходы
                                updates.hasLeftCap = false;
                              }
                            }
                          }
                          
                          // Аналогичная логика для правого подключения
                          if (params.rightConnectionFitting) {
                            const rightFitting = params.rightConnectionFitting;
                            const isNakGaika = rightFitting.startsWith('Nak_Gaika_');
                            const isShaibaNarRezba = rightFitting.startsWith('Shaib_Nar_Rezba_');
                            const isFlange = rightFitting.startsWith('Flanec_');
                            
                            if (isNakGaika || isShaibaNarRezba) {
                              let pipeDiameter;
                              if (isNakGaika) {
                                const size = extractSizeFromNakGaikaKey(rightFitting);
                                pipeDiameter = getPipeDiameterForNakGaika(size);
                              } else {
                                const size = extractSizeFromShaibaNarRezba(rightFitting);
                                pipeDiameter = getPipeDiameterForShaibaNarRezba(size);
                              }
                              
                              if (pipeDiameter && pipeDiameter !== newDiameter) {
                                // Диаметры НЕ совпадают
                                if (newDiameter === 48.3 && pipeDiameter === 42.4) {
                                  const reducer = findConcentricReducer(newDiameter, pipeDiameter);
                                  if (reducer) {
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'reducer';
                                    updates.rightReducer = reducer.key;
                                  } else {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap) {
                                      updates.hasRightCap = true;
                                      updates.rightCapElementType = 'cap';
                                      updates.rightCapType = 'elliptical';
                                    }
                                  }
                                } else if ((newDiameter === 42.4 || newDiameter === 48.3) && 
                                           (pipeDiameter === 21.3 || pipeDiameter === 26.9 || pipeDiameter === 32)) {
                                  const straightCap = findStraightCap(newDiameter);
                                  if (straightCap) {
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'cap';
                                    updates.rightCapType = 'straight';
                                  }
                                } else {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap) {
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'cap';
                                    updates.rightCapType = 'elliptical';
                                  }
                                }
                              } else {
                                // Диаметры совпадают → закрываем заглушки/переходы
                                updates.hasRightCap = false;
                              }
                            } else if (isFlange) {
                              const dn = extractDnFromFlangeKey(rightFitting);
                              const flangePipeDiameter = getPipeDiameterForDn(dn);
                              
                              if (flangePipeDiameter && flangePipeDiameter !== newDiameter) {
                                // Диаметры НЕ совпадают
                                if (newDiameter === 48.3 && flangePipeDiameter === 42.4) {
                                  const reducer = findConcentricReducer(newDiameter, flangePipeDiameter);
                                  if (reducer) {
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'reducer';
                                    updates.rightReducer = reducer.key;
                                  } else {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap) {
                                      updates.hasRightCap = true;
                                      updates.rightCapElementType = 'cap';
                                      updates.rightCapType = 'elliptical';
                                    }
                                  }
                                } else if ((newDiameter === 42.4 || newDiameter === 48.3) && 
                                           (flangePipeDiameter === 21.3 || flangePipeDiameter === 26.9 || flangePipeDiameter === 32)) {
                                  const straightCap = findStraightCap(newDiameter);
                                  if (straightCap) {
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'cap';
                                    updates.rightCapType = 'straight';
                                  }
                                } else {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap) {
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'cap';
                                    updates.rightCapType = 'elliptical';
                                  }
                                }
                              } else {
                                // Диаметры совпадают → закрываем заглушки/переходы
                                updates.hasRightCap = false;
                              }
                            }
                          }
                          
                          // Обновляем центральные фитинги при изменении диаметра основной трубы
                          const updatedCenterFittings = updateCenterFittingsForDiameter(
                            centerFittings,
                            newDiameter,
                            isFittingCompatibleWithMainPipe,
                            (category) => getFittingsByCategory(category, newDiameter, 'round', null)
                          );
                          setCenterFittings(updatedCenterFittings);
                          
                          // Обновляем торцевые фитинги (кроме накидных гаек, шайб и фланцев)
                          if (params.leftConnectionFitting && 
                              !params.leftConnectionFitting.startsWith('Nak_Gaika_') &&
                              !params.leftConnectionFitting.startsWith('Shaib_Nar_Rezba_') &&
                              !params.leftConnectionFitting.startsWith('Flanec_')) {
                            if (!isEndFittingCompatibleWithMainPipe(params.leftConnectionFitting, newDiameter)) {
                              const category = params.leftConnectionFittingCategory || 'Ниппели';
                              const fittingsInCategory = getFittingsByCategory(category, newDiameter, 'round', null)
                                .map(f => ({ ...f, isCompatible: isEndFittingCompatibleWithMainPipe(f.id, newDiameter) }));
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                updates.leftConnectionFitting = maxCompatibleFitting.id;
                                updates.leftConnectionFittingLength = null;
                                // Пересчитываем заглушку/переход для нового фитинга
                                const capReq = getCapRequirementForFitting(maxCompatibleFitting.id, newDiameter);
                                if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                  updates.hasLeftCap = true;
                                  updates.leftCapElementType = 'reducer';
                                  updates.leftReducer = capReq.reducer.key;
                                } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                  updates.hasLeftCap = true;
                                  updates.leftCapElementType = 'cap';
                                  updates.leftCapType = 'elliptical';
                                } else if (capReq.fitsFlush) {
                                  updates.hasLeftCap = false;
                                }
                              }
                            } else {
                              // Фитинг совместим с новым диаметром — пересчитываем заглушку для текущего фитинга
                              const capReq = getCapRequirementForFitting(params.leftConnectionFitting, newDiameter);
                              if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                updates.hasLeftCap = true;
                                updates.leftCapElementType = 'reducer';
                                updates.leftReducer = capReq.reducer.key;
                              } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                updates.hasLeftCap = true;
                                updates.leftCapElementType = 'cap';
                                updates.leftCapType = 'elliptical';
                              } else if (capReq.fitsFlush) {
                                updates.hasLeftCap = false;
                              }
                            }
                          }
                          
                          // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                          if (!params.leftConnectionFitting && params.hasLeftCap && params.leftCapElementType === 'cap' && params.shape !== 'square') {
                            const ellipticalCap = findEllipticalCap(newDiameter);
                            if (ellipticalCap && params.leftCapType !== 'elliptical') {
                              updates.leftCapType = 'elliptical';
                            }
                          }
                          
                          if (params.rightConnectionFitting && 
                              !params.rightConnectionFitting.startsWith('Nak_Gaika_') &&
                              !params.rightConnectionFitting.startsWith('Shaib_Nar_Rezba_') &&
                              !params.rightConnectionFitting.startsWith('Flanec_')) {
                            if (!isEndFittingCompatibleWithMainPipe(params.rightConnectionFitting, newDiameter)) {
                              const category = params.rightConnectionFittingCategory || 'Ниппели';
                              const fittingsInCategory = getFittingsByCategory(category, newDiameter, 'round', null)
                                .map(f => ({ ...f, isCompatible: isEndFittingCompatibleWithMainPipe(f.id, newDiameter) }));
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                updates.rightConnectionFitting = maxCompatibleFitting.id;
                                updates.rightConnectionFittingLength = null;
                                // Пересчитываем заглушку/переход для нового фитинга
                                const capReq = getCapRequirementForFitting(maxCompatibleFitting.id, newDiameter);
                                if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                  updates.hasRightCap = true;
                                  updates.rightCapElementType = 'reducer';
                                  updates.rightReducer = capReq.reducer.key;
                                } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                  updates.hasRightCap = true;
                                  updates.rightCapElementType = 'cap';
                                  updates.rightCapType = 'elliptical';
                                } else if (capReq.fitsFlush) {
                                  updates.hasRightCap = false;
                                }
                              }
                            } else {
                              // Фитинг совместим с новым диаметром — пересчитываем заглушку для текущего фитинга
                              const capReq = getCapRequirementForFitting(params.rightConnectionFitting, newDiameter);
                              if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                updates.hasRightCap = true;
                                updates.rightCapElementType = 'reducer';
                                updates.rightReducer = capReq.reducer.key;
                              } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                updates.hasRightCap = true;
                                updates.rightCapElementType = 'cap';
                                updates.rightCapType = 'elliptical';
                              } else if (capReq.fitsFlush) {
                                updates.hasRightCap = false;
                              }
                            }
                          }
                          
                          // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                          if (!params.rightConnectionFitting && params.hasRightCap && params.rightCapElementType === 'cap' && params.shape !== 'square') {
                            const ellipticalCap = findEllipticalCap(newDiameter);
                            if (ellipticalCap && params.rightCapType !== 'elliptical') {
                              updates.rightCapType = 'elliptical';
                            }
                          }
                          
                          // Обновляем верхние контуры
                          const updatedCircuits = circuits.map((circuit) => {
                            if (circuit.type === 'fitting' && circuit.fittingId) {
                              if (!isFittingCompatibleWithMainPipe(circuit.fittingId, newDiameter)) {
                                const category = circuit.fittingCategory || 'Ниппели';
                                const fittingsInCategory = getFittingsByCategory(category, newDiameter, 'round', null);
                                const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  return {
                                    ...circuit,
                                    fittingId: maxCompatibleFitting.id,
                                    fittingLength: null, // Сбрасываем на авто
                                  };
                                }
                              }
                            }
                            return circuit;
                          });
                          setCircuits(mergeFloorCircuitAutoSpacingToNext(updatedCircuits));
                          
                          // Обновляем нижние контуры
                          const updatedBottomCircuits = bottomCircuits.map((circuit) => {
                            if (circuit.type === 'fitting' && circuit.fittingId) {
                              if (!isFittingCompatibleWithMainPipe(circuit.fittingId, newDiameter)) {
                                const category = circuit.fittingCategory || 'Ниппели';
                                const fittingsInCategory = getFittingsByCategory(category, newDiameter, 'round', null);
                                const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  return {
                                    ...circuit,
                                    fittingId: maxCompatibleFitting.id,
                                    fittingLength: null, // Сбрасываем на авто
                                  };
                                }
                              }
                            }
                            return circuit;
                          });
                          setBottomCircuits(mergeFloorCircuitAutoSpacingToNext(updatedBottomCircuits));
                          
                          setParams(prev => ({ ...prev, ...updates }));
                        } else {
                          // Для квадратной формы
                          const newProfile = newValue;
                          const updates = { mainPipeProfile: newProfile };
                          
                          // Пересчитываем торцевые фитинги для левого подключения
                          if (params.leftConnectionFitting) {
                            const leftFitting = params.leftConnectionFitting;
                            
                            // Проверяем совместимость с новым профилем
                            if (!isEndFittingCompatibleWithSquareProfile(leftFitting, newProfile)) {
                              // Фитинг несовместим - ищем максимальный совместимый в той же категории
                              const category = params.leftConnectionFittingCategory || 'Фланцы';
                              const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                // Берем ПОСЛЕДНИЙ (максимальный) совместимый
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                updates.leftConnectionFitting = maxCompatibleFitting.id;
                              }
                            }
                          }
                          
                          // Аналогично для правого подключения
                          if (params.rightConnectionFitting) {
                            const rightFitting = params.rightConnectionFitting;
                            
                            if (!isEndFittingCompatibleWithSquareProfile(rightFitting, newProfile)) {
                              const category = params.rightConnectionFittingCategory || 'Фланцы';
                              const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                              const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                              
                              if (compatibleFittings.length > 0) {
                                const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                updates.rightConnectionFitting = maxCompatibleFitting.id;
                              }
                            }
                          }
                          
                          // Обновляем центральные фитинги при изменении профиля
                          const updatedCenterFittings = updateCenterFittingsForDiameter(
                            centerFittings,
                            null, // для квадратных не используется диаметр
                            (fittingId) => isFittingCompatibleWithSquareProfile(fittingId, newProfile),
                            (category) => getFittingsByCategory(category, null, 'square', newProfile)
                          );
                          setCenterFittings(updatedCenterFittings);
                          
                          // Обновляем верхние контуры
                          const updatedCircuits = circuits.map((circuit) => {
                            if (circuit.type === 'fitting' && circuit.fittingId) {
                              if (!isFittingCompatibleWithSquareProfile(circuit.fittingId, newProfile)) {
                                const category = circuit.fittingCategory || 'Ниппели';
                                const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                                const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  return {
                                    ...circuit,
                                    fittingId: maxCompatibleFitting.id,
                                    fittingLength: null, // Сбрасываем на авто
                                  };
                                }
                              }
                            }
                            return circuit;
                          });
                          setCircuits(mergeFloorCircuitAutoSpacingToNext(updatedCircuits));
                          
                          // Обновляем нижние контуры
                          const updatedBottomCircuits = bottomCircuits.map((circuit) => {
                            if (circuit.type === 'fitting' && circuit.fittingId) {
                              if (!isFittingCompatibleWithSquareProfile(circuit.fittingId, newProfile)) {
                                const category = circuit.fittingCategory || 'Ниппели';
                                const fittingsInCategory = getFittingsByCategory(category, null, 'square', newProfile);
                                const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  return {
                                    ...circuit,
                                    fittingId: maxCompatibleFitting.id,
                                    fittingLength: null, // Сбрасываем на авто
                                  };
                                }
                              }
                            }
                            return circuit;
                          });
                          setBottomCircuits(mergeFloorCircuitAutoSpacingToNext(updatedBottomCircuits));
                          
                          setParams(prev => ({ ...prev, ...updates }));
                        }
                      }}
                    >
                      {params.shape === 'round' 
                        ? availableDiameters.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))
                        : availableSquareProfiles.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={params.useManualLength}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateParam('useManualLength', checked);
                          // При включении ручного режима, инициализируем значение текущим расчетным
                          if (checked) {
                            updateParam('manualLength', Number(calculatedMainLength.toFixed(2)));
                          }
                        }}
                      />
                    }
                    label="Ручная длина"
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Длина"
                    size="small"
                    type="number"
                    value={params.useManualLength ? (params.manualLength || '') : Number(calculatedMainLength.toFixed(2))}
                    onChange={(e) => updateParam('manualLength', Number(e.target.value))}
                    InputProps={{ 
                      readOnly: !params.useManualLength,
                      endAdornment: <Typography variant="caption">мм</Typography> 
                    }}
                    helperText={params.useManualLength ? "Ручной" : "Авто"}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {/* Вход/Выход слева */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Подключение слева
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={params.hasLeftConnection}
                        onChange={(e) => updateParam('hasLeftConnection', e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Вкл.</Typography>}
                  />
                </Grid>
                {params.hasLeftConnection && (
                  <>
                    <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel shrink>Категория</InputLabel>
                            <Select
                              value={params.leftConnectionFittingCategory || ''}
                              label="Категория"
                              displayEmpty
                              notched
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                updateParam({
                                  leftConnectionFittingCategory: newCategory,
                                  leftConnectionFitting: '', // Сбрасываем фитинг при смене категории
                                  leftConnectionFittingLength: null
                                });
                              }}
                            >
                              <MenuItem value="">
                                <em>Выбрать</em>
                              </MenuItem>
                              {circuitFittingCategories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel shrink>Фитинг</InputLabel>
                            <Select
                              value={params.leftConnectionFitting || ''}
                              label="Фитинг"
                              displayEmpty
                              notched
                              onChange={(e) => {
                                const newFitting = e.target.value;
                                
                                // Проверяем совместимость фитинга с диаметром гребенки
                                const isCompatible = params.shape === 'square'
                                  ? isEndFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                  : isEndFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                
                                if (newFitting && !isCompatible) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                  });
                                  return;
                                }
                                
                                const updates = {
                                  leftConnectionFitting: newFitting,
                                  leftConnectionFittingLength: null,
                                  leftConnectionLength: null
                                };
                                
                                // Для квадратных профилей нет переходов и эллиптических заглушек
                                // Используем getCapRequirementForFitting только для круглых труб
                                if (newFitting && params.shape === 'round') {
                                  const capReq = getCapRequirementForFitting(newFitting, params.mainPipeDiameter);
                                  
                                  if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                    // Нужен переход
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'reducer';
                                    updates.leftReducer = capReq.reducer.key;
                                  } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                    // Нужна эллиптическая заглушка
                                    updates.hasLeftCap = true;
                                    updates.leftCapElementType = 'cap';
                                    updates.leftCapType = 'elliptical';
                                  } else if (capReq.fitsFlush) {
                                    // Впритык - закрываем заглушки
                                    updates.hasLeftCap = false;
                                  }
                                }
                                
                                updateParam(updates);
                              }}
                            >
                              <MenuItem value="">
                                <em>Выбрать</em>
                              </MenuItem>
                              {getFittingsByCategory(params.leftConnectionFittingCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile).map((f) => {
                                const isCompatible = params.shape === 'square'
                                  ? isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile)
                                  : isEndFittingCompatibleWithMainPipe(f.id, params.mainPipeDiameter);
                                return (
                                  <MenuItem 
                                    key={f.id} 
                                    value={f.id}
                                    onClick={(e) => {
                                      if (!isCompatible) {
                                        e.stopPropagation();
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      textDecoration: !isCompatible ? 'line-through' : 'none',
                                      color: !isCompatible ? 'text.disabled' : 'inherit',
                                      cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                      '&:hover': {
                                        backgroundColor: !isCompatible ? 'transparent' : undefined,
                                      },
                                    }}
                                  >
                                    {f.label}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                        {/* Поле "Длина" (вылет) показываем только если:
                            1. Фланец (свободный или приварной) с переходом или заглушкой (диаметры НЕ совпадают, гребёнка > фланца)
                            2. Накидная гайка или шайба с наружной резьбой с заглушкой (диаметры НЕ совпадают)
                            3. Или обычный фитинг (не фланец, не накидная гайка, не шайба)
                            Для фланца/накидной гайки/шайбы БЕЗ заглушки (диаметры совпадают) — поле не показываем */}
                        {(() => {
                          const isFlange = params.leftConnectionFitting?.startsWith('Flanec_');
                          const isNakGaika = params.leftConnectionFitting?.startsWith('Nak_Gaika_');
                          const isShaibaNarRezba = params.leftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
                          
                          if (isNakGaika || isShaibaNarRezba) {
                            // Для накидных гаек и шайб с наружной резьбой: проверяем совпадение диаметров
                            let pipeDiameter;
                            if (isNakGaika) {
                              const size = extractSizeFromNakGaikaKey(params.leftConnectionFitting);
                              pipeDiameter = getPipeDiameterForNakGaika(size);
                            } else {
                              const size = extractSizeFromShaibaNarRezba(params.leftConnectionFitting);
                              pipeDiameter = getPipeDiameterForShaibaNarRezba(size);
                            }
                            
                            // Показываем поле "Вылет" только если диаметры НЕ совпадают (нужна заглушка)
                            const needsCap = pipeDiameter && pipeDiameter !== params.mainPipeDiameter;
                            if (needsCap) {
                            return (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  label="Вылет"
                                  type="number"
                                  size="small"
                                  value={params.leftConnectionLength === 0 ? '' : (params.leftConnectionLength ?? '')}
                                  onChange={(e) => updateParam('leftConnectionLength', e.target.value)}
                                  placeholder="Авто"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                />
                              </Grid>
                            );
                            }
                            // Диаметры совпадают - поле не показываем
                            return null;
                          } else if (isFlange) {
                            const dn = extractDnFromFlangeKey(params.leftConnectionFitting);
                            const flangePipeDiameter = getPipeDiameterForDn(dn);
                            // Для квадратных профилей всегда показываем вылет (нет логики "одинаковых диаметров")
                            // Для круглых труб показываем поле если диаметры не совпадают И гребёнка больше фланца
                            const needsReducerOrCap = params.shape === 'square' 
                              ? true 
                              : (flangePipeDiameter && flangePipeDiameter !== params.mainPipeDiameter && params.mainPipeDiameter > flangePipeDiameter);
                            if (needsReducerOrCap) {
                              return (
                                <Grid item xs={6} sm={2}>
                                  <TextField
                                    label="Вылет"
                                    type="number"
                                    size="small"
                                    value={params.leftConnectionLength === 0 ? '' : (params.leftConnectionLength ?? '')}
                                    onChange={(e) => updateParam('leftConnectionLength', e.target.value)}
                                    placeholder="Авто"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                  />
                                </Grid>
                              );
                            }
                            // Диаметры совпадают или гребёнка меньше — поле не показываем
                            return null;
                          }
                          // Обычный фитинг — показываем поле длины фитинга
                          return (
                            <Grid item xs={6} sm={2}>
                              <TextField
                                label="Вылет"
                                type="number"
                                size="small"
                                value={params.leftConnectionFittingLength === 0 ? '' : (params.leftConnectionFittingLength ?? '')}
                                onChange={(e) => updateParam('leftConnectionFittingLength', e.target.value === '' ? null : e.target.value)}
                                placeholder="Авто"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                              />
                            </Grid>
                          );
                        })()}
                  </>
                )}
              </Grid>

              {/* Вход/Выход справа */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Подключение справа
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={params.hasRightConnection}
                        onChange={(e) => updateParam('hasRightConnection', e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Вкл.</Typography>}
                  />
                </Grid>
                {params.hasRightConnection && (
                  <>
                    <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel shrink>Категория</InputLabel>
                            <Select
                              value={params.rightConnectionFittingCategory || ''}
                              label="Категория"
                              displayEmpty
                              notched
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                updateParam({
                                  rightConnectionFittingCategory: newCategory,
                                  rightConnectionFitting: '', // Сбрасываем фитинг при смене категории
                                  rightConnectionFittingLength: null
                                });
                              }}
                            >
                              <MenuItem value="">
                                <em>Выбрать</em>
                              </MenuItem>
                              {circuitFittingCategories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel shrink>Фитинг</InputLabel>
                            <Select
                              value={params.rightConnectionFitting || ''}
                              label="Фитинг"
                              displayEmpty
                              notched
                              onChange={(e) => {
                                const newFitting = e.target.value;
                                
                                // Проверяем совместимость фитинга с диаметром гребенки
                                const isCompatible = params.shape === 'square'
                                  ? isEndFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                  : isEndFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                
                                if (newFitting && !isCompatible) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                  });
                                  return;
                                }
                                
                                const updates = {
                                  rightConnectionFitting: newFitting,
                                  rightConnectionFittingLength: null,
                                  rightConnectionLength: null
                                };
                                
                                // Для квадратных профилей нет переходов и эллиптических заглушек
                                // Используем getCapRequirementForFitting только для круглых труб
                                if (newFitting && params.shape === 'round') {
                                  const capReq = getCapRequirementForFitting(newFitting, params.mainPipeDiameter);
                                  
                                  if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                    // Нужен переход
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'reducer';
                                    updates.rightReducer = capReq.reducer.key;
                                  } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                    // Нужна эллиптическая заглушка
                                    updates.hasRightCap = true;
                                    updates.rightCapElementType = 'cap';
                                    updates.rightCapType = 'elliptical';
                                  } else if (capReq.fitsFlush) {
                                    // Впритык - закрываем заглушки
                                    updates.hasRightCap = false;
                                  }
                                }
                                
                                updateParam(updates);
                              }}
                            >
                              <MenuItem value="">
                                <em>Выбрать</em>
                              </MenuItem>
                              {getFittingsByCategory(params.rightConnectionFittingCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile).map((f) => {
                                const isCompatible = params.shape === 'square'
                                  ? isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile)
                                  : isEndFittingCompatibleWithMainPipe(f.id, params.mainPipeDiameter);
                                return (
                                  <MenuItem 
                                    key={f.id} 
                                    value={f.id}
                                    onClick={(e) => {
                                      if (!isCompatible) {
                                        e.stopPropagation();
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      textDecoration: !isCompatible ? 'line-through' : 'none',
                                      color: !isCompatible ? 'text.disabled' : 'inherit',
                                      cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                      '&:hover': {
                                        backgroundColor: !isCompatible ? 'transparent' : undefined,
                                      },
                                    }}
                                  >
                                    {f.label}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                        {/* Поле "Длина" (вылет) показываем только если:
                            1. Фланец (свободный или приварной) с переходом или заглушкой (диаметры НЕ совпадают, гребёнка > фланца)
                            2. Накидная гайка или шайба с наружной резьбой с заглушкой (диаметры НЕ совпадают)
                            3. Или обычный фитинг (не фланец, не накидная гайка, не шайба)
                            Для фланца/накидной гайки/шайбы БЕЗ заглушки (диаметры совпадают) — поле не показываем */}
                        {(() => {
                          const isFlange = params.rightConnectionFitting?.startsWith('Flanec_');
                          const isNakGaika = params.rightConnectionFitting?.startsWith('Nak_Gaika_');
                          const isShaibaNarRezba = params.rightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
                          
                          if (isNakGaika || isShaibaNarRezba) {
                            // Для накидных гаек и шайб с наружной резьбой: проверяем совпадение диаметров
                            let pipeDiameter;
                            if (isNakGaika) {
                              const size = extractSizeFromNakGaikaKey(params.rightConnectionFitting);
                              pipeDiameter = getPipeDiameterForNakGaika(size);
                            } else {
                              const size = extractSizeFromShaibaNarRezba(params.rightConnectionFitting);
                              pipeDiameter = getPipeDiameterForShaibaNarRezba(size);
                            }
                            
                            // Показываем поле "Вылет" только если диаметры НЕ совпадают (нужна заглушка)
                            const needsCap = pipeDiameter && pipeDiameter !== params.mainPipeDiameter;
                            if (needsCap) {
                            return (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  label="Вылет"
                                  type="number"
                                  size="small"
                                  value={params.rightConnectionLength === 0 ? '' : (params.rightConnectionLength ?? '')}
                                  onChange={(e) => updateParam('rightConnectionLength', e.target.value)}
                                  placeholder="Авто"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                />
                              </Grid>
                            );
                            }
                            // Диаметры совпадают - поле не показываем
                            return null;
                          } else if (isFlange) {
                            const dn = extractDnFromFlangeKey(params.rightConnectionFitting);
                            const flangePipeDiameter = getPipeDiameterForDn(dn);
                            // Для квадратных профилей всегда показываем вылет (нет логики "одинаковых диаметров")
                            // Для круглых труб показываем поле если диаметры не совпадают И гребёнка больше фланца
                            const needsReducerOrCap = params.shape === 'square' 
                              ? true 
                              : (flangePipeDiameter && flangePipeDiameter !== params.mainPipeDiameter && params.mainPipeDiameter > flangePipeDiameter);
                            if (needsReducerOrCap) {
                              return (
                                <Grid item xs={6} sm={2}>
                                  <TextField
                                    label="Вылет"
                                    type="number"
                                    size="small"
                                    value={params.rightConnectionLength === 0 ? '' : (params.rightConnectionLength ?? '')}
                                    onChange={(e) => updateParam('rightConnectionLength', e.target.value)}
                                    placeholder="Авто"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                  />
                                </Grid>
                              );
                            }
                            // Диаметры совпадают или гребёнка меньше — поле не показываем
                            return null;
                          }
                          // Обычный фитинг — показываем поле длины фитинга
                          return (
                            <Grid item xs={6} sm={2}>
                              <TextField
                                label="Вылет"
                                type="number"
                                size="small"
                                value={params.rightConnectionFittingLength === 0 ? '' : (params.rightConnectionFittingLength ?? '')}
                                onChange={(e) => updateParam('rightConnectionFittingLength', e.target.value === '' ? null : e.target.value)}
                                placeholder="Авто"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                              />
                            </Grid>
                          );
                        })()}
                  </>
                )}
              </Grid>

              {/* Заглушки и переходы */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Заглушки и переходы конц.
              </Typography>
              <Grid container spacing={2}>
                {/* Левая сторона */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={params.hasLeftCap}
                          onChange={(e) => updateParam('hasLeftCap', e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Слева</Typography>}
                    />
                    {params.hasLeftCap && (
                      <>
                        {/* Для квадратных форм только прямые заглушки */}
                        {params.shape === 'square' ? (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            Прямая заглушка
                          </Typography>
                        ) : (
                          <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Элемент</InputLabel>
                              <Select
                                value={params.leftCapElementType || 'cap'}
                                label="Элемент"
                                onChange={(e) => updateParam('leftCapElementType', e.target.value)}
                              >
                                <MenuItem value="cap">Заглушка</MenuItem>
                                <MenuItem value="reducer">Переход конц.</MenuItem>
                              </Select>
                            </FormControl>
                            {params.leftCapElementType === 'cap' || !params.leftCapElementType ? (
                              <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Тип</InputLabel>
                                <Select
                                  value={params.leftCapType}
                                  label="Тип"
                                  onChange={(e) => updateParam('leftCapType', e.target.value)}
                                >
                                  {params.mainPipeDiameter <= 101.6 && (
                                    <MenuItem value="straight">Прямая</MenuItem>
                                  )}
                                  <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Переход</InputLabel>
                                <Select
                                  value={params.leftReducer || concentricReducers[0]?.id}
                                  label="Переход"
                                  onChange={(e) => updateParam('leftReducer', e.target.value)}
                                >
                                  {concentricReducers.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                  {params.hasLeftCap && (
                    <Typography variant="caption" color="text.secondary">
                      {params.shape === 'square'
                        ? `Заглушка прямая ${params.mainPipeProfile.replace('x', '×')}`
                        : params.leftCapElementType === 'reducer'
                          ? `${fittingsData[params.leftReducer]?.displayName || 'Переход'}`
                          : params.leftCapType === 'straight' 
                            ? `Заглушка прямая ⌀${params.mainPipeDiameter}`
                            : `Заглушка эллиптическая ⌀${params.mainPipeDiameter}`
                      }
                    </Typography>
                  )}
                </Grid>
                
                {/* Правая сторона */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={params.hasRightCap}
                          onChange={(e) => updateParam('hasRightCap', e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Справа</Typography>}
                    />
                    {params.hasRightCap && (
                      <>
                        {/* Для квадратных форм только прямые заглушки */}
                        {params.shape === 'square' ? (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            Прямая заглушка
                          </Typography>
                        ) : (
                          <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Элемент</InputLabel>
                              <Select
                                value={params.rightCapElementType || 'cap'}
                                label="Элемент"
                                onChange={(e) => updateParam('rightCapElementType', e.target.value)}
                              >
                                <MenuItem value="cap">Заглушка</MenuItem>
                                <MenuItem value="reducer">Переход конц.</MenuItem>
                              </Select>
                            </FormControl>
                            {params.rightCapElementType === 'cap' || !params.rightCapElementType ? (
                              <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Тип</InputLabel>
                                <Select
                                  value={params.rightCapType}
                                  label="Тип"
                                  onChange={(e) => updateParam('rightCapType', e.target.value)}
                                >
                                  {params.mainPipeDiameter <= 101.6 && (
                                    <MenuItem value="straight">Прямая</MenuItem>
                                  )}
                                  <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Переход</InputLabel>
                                <Select
                                  value={params.rightReducer || concentricReducers[0]?.id}
                                  label="Переход"
                                  onChange={(e) => updateParam('rightReducer', e.target.value)}
                                >
                                  {concentricReducers.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                  {params.hasRightCap && (
                    <Typography variant="caption" color="text.secondary">
                      {params.shape === 'square'
                        ? `Заглушка прямая ${params.mainPipeProfile.replace('x', '×')}`
                        : params.rightCapElementType === 'reducer'
                          ? `${fittingsData[params.rightReducer]?.displayName || 'Переход'}`
                          : params.rightCapType === 'straight' 
                            ? `Заглушка прямая ⌀${params.mainPipeDiameter}`
                            : `Заглушка эллиптическая ⌀${params.mainPipeDiameter}`
                      }
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {/* Параметры верхних контуров */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Верхние подключения
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Количество:</Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateCircuitCount(-1)}
                      disabled={params.circuitCount <= 0}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                      {params.circuitCount}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateCircuitCount(1)}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
                {params.circuitCount > 0 && (
                  <>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ первого"
                        type="number"
                        size="small"
                        value={params.firstCircuitOffsetMode === 'fromBottomCircuit' 
                          ? effectiveFirstCircuitOffset 
                          : (params.firstCircuitOffset === 0 ? '' : params.firstCircuitOffset)}
                        onChange={(e) => updateParam('firstCircuitOffset', e.target.value)}
                        disabled={params.firstCircuitOffsetMode === 'fromBottomCircuit'}
                        fullWidth
                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ последнего"
                        type="number"
                        size="small"
                        value={params.useManualLength ? lastCircuitOffsetCalculated : (params.lastCircuitOffset === 0 ? '' : params.lastCircuitOffset)}
                        onChange={(e) => updateParam('lastCircuitOffset', e.target.value)}
                        fullWidth
                        disabled={params.useManualLength || !shouldUseTopOffset}
                        InputProps={{ 
                          readOnly: params.useManualLength || !shouldUseTopOffset,
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                        helperText={params.useManualLength ? "Авто" : (!shouldUseTopOffset ? "Использует нижний" : "")}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Между подключениями"
                        type="number"
                        size="small"
                        value={hasCustomSpacing ? '' : params.circuitSpacing}
                        placeholder={hasCustomSpacing ? 'произвольный' : ''}
                        onChange={(e) => updateCircuitSpacing(e.target.value)}
                        fullWidth
                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                      />
                    </Grid>
                    
                    {/* Переключатель привязки к нижнему контуру */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={params.firstCircuitOffsetMode === 'fromBottomCircuit'}
                              onChange={(e) => {
                                const isEnabled = e.target.checked;
                                if (isEnabled) {
                                  // Включаем режим привязки
                                  setParams(prev => ({
                                    ...prev,
                                    firstCircuitOffsetMode: 'fromBottomCircuit',
                                    firstCircuitOffsetSaved: prev.firstCircuitOffset,
                                    firstCircuitOffset: prev.firstCircuitOffsetLastUsed || 0,
                                    // Взаимная исключительность: отключаем привязку нижних
                                    bottomFirstCircuitOffsetMode: 'fromEdge',
                                    bottomFirstCircuitOffset: prev.bottomFirstCircuitOffsetSaved !== null 
                                      ? prev.bottomFirstCircuitOffsetSaved 
                                      : prev.bottomFirstCircuitOffset,
                                  }));
                                } else {
                                  // Отключаем режим привязки
                                  setParams(prev => ({
                                    ...prev,
                                    firstCircuitOffsetMode: 'fromEdge',
                                    firstCircuitOffsetLastUsed: prev.firstCircuitOffset,
                                    firstCircuitOffset: prev.firstCircuitOffsetSaved !== null 
                                      ? prev.firstCircuitOffsetSaved 
                                      : prev.firstCircuitOffset,
                                  }));
                                }
                              }}
                              disabled={bottomCircuits.length === 0}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              Привязать к нижнему контуру
                            </Typography>
                          }
                        />
                        
                        {/* Поля для выбора контура и отступа от него */}
                        {params.firstCircuitOffsetMode === 'fromBottomCircuit' && bottomCircuits.length > 0 && (
                          <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Контур</InputLabel>
                              <Select
                                value={params.firstCircuitOffsetReferenceIndex || 0}
                                label="Контур"
                                onChange={(e) => updateParam('firstCircuitOffsetReferenceIndex', e.target.value)}
                              >
                                {bottomCircuits.map((_, index) => (
                                  <MenuItem key={index} value={index}>
                                    {index + 1}-й нижний
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              label="Отступ от контура"
                              type="number"
                              size="small"
                              value={params.firstCircuitOffset || 0}
                              onChange={(e) => updateParam('firstCircuitOffset', e.target.value)}
                              sx={{ width: 150 }}
                              InputProps={{
                                endAdornment: <Typography variant="caption">мм</Typography>
                              }}
                            />
                          </>
                        )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Таблица параметров каждого контура */}
              {params.circuitCount > 0 && (
                <TableContainer sx={{ maxHeight: '17.5rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1 }}>
                  <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5 }}>До след.</TableCell>
                      <TableCell sx={{ fontWeight: 600, p: 0.5, width: 50 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {circuits.map((circuit, index) => (
                      <TableRow 
                        key={circuit.id}
                        draggable
                        onDragStart={() => setDraggedCircuitIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedCircuitIndex !== null && draggedCircuitIndex !== index) {
                            moveCircuit(draggedCircuitIndex, index);
                          }
                          setDraggedCircuitIndex(null);
                        }}
                        onDragEnd={() => setDraggedCircuitIndex(null)}
                        sx={{ 
                          cursor: 'move',
                          opacity: draggedCircuitIndex === index ? 0.5 : 1,
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ p: 0.5 }}>
                          <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'grab' }} />
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                          <Select
                            size="small"
                            value={circuit.fittingCategory || 'Ниппели'}
                                onChange={(e) => {
                                  const newCategory = e.target.value;
                                  const fittingsInCategory = getFittingsByCategory(newCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile);
                                  const firstCompatibleFitting = fittingsInCategory.find(f => f.isCompatible);
                                  
                                  // Если нет совместимых фитингов в категории, показываем предупреждение
                                  if (!firstCompatibleFitting) {
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                    return;
                                  }
                                  
                                  const firstFitting = firstCompatibleFitting.id;
                                  updateCircuit(index, { 
                                    fittingCategory: newCategory, 
                                    fittingId: firstFitting, 
                                    fittingLength: null,
                                    diameter: fittingsData[firstFitting]?.diameter || circuit.diameter
                                  });
                                }}
                                sx={{ minWidth: 100, fontSize: '0.75rem' }}
                              >
                                {circuitFittingCategories.map((cat) => (
                                  <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                                    {cat.label}
                                  </MenuItem>
                                ))}
                              </Select>
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                              <Select
                                size="small"
                                value={circuit.fittingId}
                                onChange={(e) => {
                                  const newFittingId = e.target.value;
                                  // Проверяем совместимость
                                  const isCompatible = params.shape === 'square'
                                    ? isFittingCompatibleWithSquareProfile(newFittingId, params.mainPipeProfile)
                                    : isFittingCompatibleWithMainPipe(newFittingId, params.mainPipeDiameter);
                                  
                                  if (!isCompatible) {
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                    // Не обновляем значение
                                    return;
                                  }
                                  updateCircuit(index, { 
                                    fittingId: newFittingId, 
                                    fittingLength: null,
                                    diameter: fittingsData[newFittingId]?.diameter || circuit.diameter
                                  });
                                }}
                                sx={{ minWidth: 120, fontSize: '0.75rem' }}
                              >
                                {getFittingsByCategory(circuit.fittingCategory || 'Ниппели', params.mainPipeDiameter, params.shape, params.mainPipeProfile).map((f) => (
                                  <MenuItem 
                                    key={f.id} 
                                    value={f.id}
                                    onClick={(e) => {
                                      if (!f.isCompatible) {
                                        e.stopPropagation();
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      fontSize: '0.8rem',
                                      textDecoration: !f.isCompatible ? 'line-through' : 'none',
                                      color: !f.isCompatible ? 'text.disabled' : 'inherit',
                                      cursor: !f.isCompatible ? 'not-allowed' : 'pointer',
                                      '&:hover': {
                                        backgroundColor: !f.isCompatible ? 'transparent' : undefined,
                                      },
                                    }}
                                  >
                                    {f.label}
                                  </MenuItem>
                                ))}
                              </Select>
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                          {circuit.fittingId?.startsWith('Flanec_') || circuit.fittingId?.startsWith('Nak_Gaika_') || circuit.fittingId?.startsWith('Shaib_Nar_Rezba_') ? (
                            <TextField
                              type="number"
                              size="small"
                              value={circuit.flangePipeLength === 0 ? '' : (circuit.flangePipeLength ?? '')}
                              onChange={(e) => updateCircuit(index, 'flangePipeLength', e.target.value)}
                              placeholder="Авто"
                              sx={{ width: 85 }}
                              InputProps={{ 
                                endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                sx: { fontSize: '0.8rem' }
                              }}
                              title="Вылет трубки"
                            />
                          ) : (
                            <TextField
                              type="number"
                              size="small"
                              value={circuit.fittingLength === 0 ? '' : (circuit.fittingLength ?? '')}
                              onChange={(e) => updateCircuit(index, 'fittingLength', e.target.value === '' ? null : e.target.value)}
                              placeholder="Авто"
                              sx={{ width: 85 }}
                              InputProps={{ 
                                endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                sx: { fontSize: '0.8rem' }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                          {index < circuits.length - 1 ? (
                            <TextField
                              type="number"
                              size="small"
                              value={circuit.spacingToNext ?? ''}
                              placeholder={String(params.circuitSpacing)}
                              onChange={(e) => updateCircuit(index, 'spacingToNext', e.target.value === '' ? null : e.target.value)}
                              sx={{ width: 75 }}
                              InputProps={{ sx: { fontSize: '0.8rem' } }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => deleteCircuit(index)}
                            sx={{ color: 'error.main' }}
                            title="Удалить подключение"
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              {/* Параметры нижних контуров */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                Нижние подключения
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Количество:</Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateBottomCircuitCount(-1)}
                      disabled={params.bottomCircuitCount <= 0}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                      {params.bottomCircuitCount}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateBottomCircuitCount(1)}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
                {params.bottomCircuitCount > 0 && (
                  <>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ первого"
                        type="number"
                        size="small"
                        value={params.bottomFirstCircuitOffsetMode === 'fromTopCircuit' 
                          ? effectiveBottomFirstCircuitOffset 
                          : (params.bottomFirstCircuitOffset === 0 ? '' : params.bottomFirstCircuitOffset)}
                        onChange={(e) => updateParam('bottomFirstCircuitOffset', e.target.value)}
                        disabled={params.bottomFirstCircuitOffsetMode === 'fromTopCircuit'}
                        fullWidth
                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Отступ последнего"
                        type="number"
                        size="small"
                        value={params.useManualLength ? bottomLastCircuitOffsetCalculated : (params.bottomLastCircuitOffset === 0 ? '' : params.bottomLastCircuitOffset)}
                        onChange={(e) => updateParam('bottomLastCircuitOffset', e.target.value)}
                        fullWidth
                        disabled={params.useManualLength || shouldUseTopOffset}
                        InputProps={{ 
                          readOnly: params.useManualLength || shouldUseTopOffset,
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                        helperText={params.useManualLength ? "Авто" : (shouldUseTopOffset ? "Использует верхний" : "")}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Между подключениями"
                        type="number"
                        size="small"
                        value={hasBottomCustomSpacing ? '' : params.bottomCircuitSpacing}
                        placeholder={hasBottomCustomSpacing ? 'произвольный' : ''}
                        onChange={(e) => updateBottomCircuitSpacing(e.target.value)}
                        fullWidth
                        InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                      />
                    </Grid>
                    
                    {/* Переключатель привязки к верхнему контуру */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={params.bottomFirstCircuitOffsetMode === 'fromTopCircuit'}
                              onChange={(e) => {
                                const isEnabled = e.target.checked;
                                if (isEnabled) {
                                  // Включаем режим привязки
                                  setParams(prev => ({
                                    ...prev,
                                    bottomFirstCircuitOffsetMode: 'fromTopCircuit',
                                    bottomFirstCircuitOffsetSaved: prev.bottomFirstCircuitOffset,
                                    bottomFirstCircuitOffset: prev.bottomFirstCircuitOffsetLastUsed || 0,
                                    // Взаимная исключительность: отключаем привязку верхних
                                    firstCircuitOffsetMode: 'fromEdge',
                                    firstCircuitOffset: prev.firstCircuitOffsetSaved !== null 
                                      ? prev.firstCircuitOffsetSaved 
                                      : prev.firstCircuitOffset,
                                  }));
                                } else {
                                  // Отключаем режим привязки
                                  setParams(prev => ({
                                    ...prev,
                                    bottomFirstCircuitOffsetMode: 'fromEdge',
                                    bottomFirstCircuitOffsetLastUsed: prev.bottomFirstCircuitOffset,
                                    bottomFirstCircuitOffset: prev.bottomFirstCircuitOffsetSaved !== null 
                                      ? prev.bottomFirstCircuitOffsetSaved 
                                      : prev.bottomFirstCircuitOffset,
                                  }));
                                }
                              }}
                              disabled={circuits.length === 0}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              Привязать к верхнему контуру
                            </Typography>
                          }
                        />
                        
                        {/* Поля для выбора контура и отступа от него */}
                        {params.bottomFirstCircuitOffsetMode === 'fromTopCircuit' && circuits.length > 0 && (
                          <>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Контур</InputLabel>
                              <Select
                                value={params.bottomFirstCircuitOffsetReferenceIndex || 0}
                                label="Контур"
                                onChange={(e) => updateParam('bottomFirstCircuitOffsetReferenceIndex', e.target.value)}
                              >
                                {circuits.map((_, index) => (
                                  <MenuItem key={index} value={index}>
                                    {index + 1}-й верхний
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              label="Отступ от контура"
                              type="number"
                              size="small"
                              value={params.bottomFirstCircuitOffset || 0}
                              onChange={(e) => updateParam('bottomFirstCircuitOffset', e.target.value)}
                              sx={{ width: 150 }}
                              InputProps={{
                                endAdornment: <Typography variant="caption">мм</Typography>
                              }}
                            />
                          </>
                        )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Таблица нижних контуров */}
              {params.bottomCircuitCount > 0 && (
                <TableContainer sx={{ maxHeight: '13.75rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                  <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5 }}>До след.</TableCell>
                        <TableCell sx={{ fontWeight: 600, p: 0.5, width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bottomCircuits.map((circuit, index) => (
                        <TableRow 
                          key={circuit.id}
                          draggable
                          onDragStart={() => setDraggedBottomCircuitIndex(index)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (draggedBottomCircuitIndex !== null && draggedBottomCircuitIndex !== index) {
                              moveBottomCircuit(draggedBottomCircuitIndex, index);
                            }
                            setDraggedBottomCircuitIndex(null);
                          }}
                          onDragEnd={() => setDraggedBottomCircuitIndex(null)}
                          sx={{ 
                            cursor: 'move',
                            opacity: draggedBottomCircuitIndex === index ? 0.5 : 1,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <TableCell sx={{ p: 0.5 }}>
                            <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'grab' }} />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <Select
                              size="small"
                              value={circuit.fittingCategory || 'Ниппели'}
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    const fittingsInCategory = getFittingsByCategory(newCategory, params.mainPipeDiameter, params.shape, params.mainPipeProfile);
                                    const firstCompatibleFitting = fittingsInCategory.find(f => f.isCompatible);
                                    
                                    // Если нет совместимых фитингов в категории, показываем предупреждение
                                    if (!firstCompatibleFitting) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      return;
                                    }
                                    
                                    const firstFitting = firstCompatibleFitting.id;
                                    updateBottomCircuit(index, { 
                                      fittingCategory: newCategory, 
                                      fittingId: firstFitting, 
                                      fittingLength: null,
                                      diameter: fittingsData[firstFitting]?.diameter || circuit.diameter
                                    });
                                  }}
                                  sx={{ minWidth: 100, fontSize: '0.75rem' }}
                                >
                                  {circuitFittingCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                                      {cat.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                                <Select
                                  size="small"
                                  value={circuit.fittingId}
                                  onChange={(e) => {
                                    const newFittingId = e.target.value;
                                    // Проверяем совместимость
                                    const isCompatible = params.shape === 'square'
                                      ? isFittingCompatibleWithSquareProfile(newFittingId, params.mainPipeProfile)
                                      : isFittingCompatibleWithMainPipe(newFittingId, params.mainPipeDiameter);
                                    
                                    if (!isCompatible) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      // Не обновляем значение
                                      return;
                                    }
                                    updateBottomCircuit(index, { 
                                      fittingId: newFittingId, 
                                      fittingLength: null,
                                      diameter: fittingsData[newFittingId]?.diameter || circuit.diameter
                                    });
                                  }}
                                  sx={{ minWidth: 120, fontSize: '0.75rem' }}
                                >
                                  {getFittingsByCategory(circuit.fittingCategory || 'Ниппели', params.mainPipeDiameter, params.shape, params.mainPipeProfile).map((f) => (
                                    <MenuItem 
                                      key={f.id} 
                                      value={f.id}
                                      onClick={(e) => {
                                        if (!f.isCompatible) {
                                          e.stopPropagation();
                                          setIncompatibilityDialog({
                                            open: true,
                                            message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                          });
                                        }
                                      }}
                                      sx={{ 
                                        fontSize: '0.8rem',
                                        textDecoration: !f.isCompatible ? 'line-through' : 'none',
                                        color: !f.isCompatible ? 'text.disabled' : 'inherit',
                                        cursor: !f.isCompatible ? 'not-allowed' : 'pointer',
                                        '&:hover': {
                                          backgroundColor: !f.isCompatible ? 'transparent' : undefined,
                                        },
                                      }}
                                    >
                                      {f.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {circuit.fittingId?.startsWith('Flanec_') || circuit.fittingId?.startsWith('Nak_Gaika_') || circuit.fittingId?.startsWith('Shaib_Nar_Rezba_') ? (
                              /* Для фланцев, накидных гаек и шайб с наружной резьбой показываем поле "Вылет трубки" */
                              <TextField
                                type="number"
                                size="small"
                                value={circuit.flangePipeLength === 0 ? '' : (circuit.flangePipeLength ?? '')}
                                onChange={(e) => updateBottomCircuit(index, 'flangePipeLength', e.target.value)}
                                placeholder="Авто"
                                sx={{ width: 85 }}
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                title="Вылет трубки"
                              />
                            ) : (
                              <TextField
                                type="number"
                                size="small"
                                value={circuit.fittingLength === 0 ? '' : (circuit.fittingLength ?? '')}
                                onChange={(e) => updateBottomCircuit(index, 'fittingLength', e.target.value === '' ? null : e.target.value)}
                                placeholder="Авто"
                                sx={{ width: 85 }}
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {index < bottomCircuits.length - 1 ? (
                              <TextField
                                type="number"
                                size="small"
                                value={circuit.spacingToNext ?? ''}
                                placeholder={String(params.bottomCircuitSpacing)}
                              onChange={(e) => updateBottomCircuit(index, 'spacingToNext', e.target.value === '' ? null : e.target.value)}
                              sx={{ width: 75 }}
                              InputProps={{ sx: { fontSize: '0.8rem' } }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ p: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => deleteBottomCircuit(index)}
                            sx={{ color: 'error.main' }}
                            title="Удалить подключение"
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Параметры центральных фитингов */}
              {/* Центральные фитинги (термометры и лицевые подключения) */}
              <CenterFittingsUI
                centerFittings={centerFittings}
                centerFittingCount={params.centerFittingCount}
                onUpdateCount={updateCenterFittingCount}
                onUpdateFitting={updateCenterFitting}
                onDeleteFitting={deleteCenterFitting}
                centerFittingCategories={centerFittingCategories}
                getFittingsByCategory={getFittingsByCategory}
                isFittingCompatibleWithMainPipe={isFittingCompatibleWithMainPipe}
                isFittingCompatibleWithSquareProfile={isFittingCompatibleWithSquareProfile}
                mainPipeDiameter={params.mainPipeDiameter}
                shape={params.shape}
                mainPipeProfile={params.mainPipeProfile}
                circuitCount={params.circuitCount}
                bottomCircuitCount={params.bottomCircuitCount}
                onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                mode="floor"
                currentRow="top"
              />

              <Divider sx={{ my: 3 }} />

              {/* Кнопка генерации */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGenerate}
                startIcon={<PlayArrowIcon />}
                fullWidth
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Сгенерировать чертёж
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Диалог предупреждения о несовместимости */}
      <Dialog
        open={incompatibilityDialog.open}
        onClose={() => setIncompatibilityDialog({ open: false, message: '' })}
      >
        <DialogTitle>Предупреждение</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {incompatibilityDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIncompatibilityDialog({ open: false, message: '' })} 
            color="primary"
            autoFocus
          >
            ОК
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Создает объект pipeSpec с правильным покрытием
 * @param {number} diameter - Диаметр трубы
 * @param {string} thickness - Толщина трубы
 * @param {string} preferredCoating - Предпочитаемое покрытие
 * @returns {object} - Объект pipeSpec
 */
const createPipeSpec = (diameter, thickness, preferredCoating) => {
  const actualCoating = getActualCoating(diameter, thickness, preferredCoating);
  return {
    shape: 'round',
    diameter: diameter,
    thickness: thickness,
    coating: actualCoating,
  };
};

/**
 * Генератор чертежа для этажного коллектора
 */
function generateFloorCollector(params, effectiveFirstCircuitOffset = null, effectiveBottomFirstCircuitOffset = null, shouldUseTopOffset = true) {
  const {
    shape = 'round',
    mainPipeDiameter,
    mainPipeProfile,
    mainPipeLength,
    firstCircuitOffset,
    lastCircuitOffset,
    circuitSpacing,
    circuits,
    // Нижние контуры
    bottomFirstCircuitOffset,
    bottomLastCircuitOffset,
    bottomCircuitSpacing,
    bottomCircuits,
    // Центральные фитинги
    centerFittings = [],
    // Подключения
    hasLeftConnection,
    hasRightConnection,
    leftConnectionType,
    rightConnectionType,
    leftConnectionDiameter,
    rightConnectionDiameter,
    leftConnectionLength,
    rightConnectionLength,
    leftConnectionFitting,
    rightConnectionFitting,
    leftConnectionFittingLength,
    rightConnectionFittingLength,
    // Заглушки и переходы
    hasLeftCap,
    hasRightCap,
    leftCapElementType = 'cap',
    rightCapElementType = 'cap',
    leftCapType,
    rightCapType,
    leftReducer,
    rightReducer,
  } = params;

  // Используем эффективные отступы, если они переданы
  const actualFirstCircuitOffset = effectiveFirstCircuitOffset !== null 
    ? effectiveFirstCircuitOffset 
    : firstCircuitOffset;
  const actualBottomFirstCircuitOffset = effectiveBottomFirstCircuitOffset !== null 
    ? effectiveBottomFirstCircuitOffset 
    : bottomFirstCircuitOffset;

  // Нормализация параметров для квадратных труб
  // Для квадратных профилей принудительно устанавливаем прямые заглушки
  let normalizedLeftCapType = leftCapType;
  let normalizedRightCapType = rightCapType;
  let normalizedLeftCapElementType = leftCapElementType;
  let normalizedRightCapElementType = rightCapElementType;
  
  if (shape === 'square') {
    normalizedLeftCapType = 'straight';
    normalizedRightCapType = 'straight';
    normalizedLeftCapElementType = 'cap';
    normalizedRightCapElementType = 'cap';
  }

  // Определяем покрытие для всей системы (только для круглых труб)
  const allDiameters = shape === 'round' ? [mainPipeDiameter] : [];
  
  // Импортируем функции для получения диаметров промежуточных труб
  const {
    extractDnFromFlangeKey,
    extractSizeFromNakGaikaKey,
    extractSizeFromShaibaNarRezba,
    getPipeDiameterForDn,
    getPipeDiameterForNakGaika,
    getPipeDiameterForShaibaNarRezba,
  } = require('./collectorUtils');
  
  // Функция для получения диаметра промежуточной трубы для фитинга
  const getPipeDiameterForFitting = (fittingId) => {
    if (!fittingId) return null;
    
    if (fittingId.startsWith('Flanec_')) {
      const dn = extractDnFromFlangeKey(fittingId);
      return getPipeDiameterForDn(dn);
    }
    
    if (fittingId.startsWith('Nak_Gaika_')) {
      const size = extractSizeFromNakGaikaKey(fittingId);
      return getPipeDiameterForNakGaika(size);
    }
    
    if (fittingId.startsWith('Shaib_Nar_Rezba_')) {
      const size = extractSizeFromShaibaNarRezba(fittingId);
      return getPipeDiameterForShaibaNarRezba(size);
    }
    
    return null;
  };
  
  // Добавляем диаметры ТОЛЬКО промежуточных труб фланцев из контуров
  // (сами контуры - ниппели, отводы и т.д. - НЕ влияют на покрытие основной системы)
  circuits.forEach(circuit => {
    if (circuit && circuit.fittingId && circuit.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(circuit.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  bottomCircuits.forEach(circuit => {
    if (circuit && circuit.fittingId && circuit.fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(circuit.fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  
  // Добавляем диаметры из торцевых подключений
  if (hasLeftConnection && leftConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(leftConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (hasRightConnection && rightConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(rightConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  
  // Убираем дубликаты
  const uniqueDiameters = [...new Set(allDiameters)];
  
  // Проверяем наличие оцинкованных фланцев в системе
  const hasGalvanizedFlanges = () => {
    const checkCircuits = (circuits) => circuits.some(circuit => 
      circuit.fittingId && circuit.fittingId.includes('_galv')
    );
    
    if (checkCircuits(circuits)) return true;
    if (bottomCircuits && checkCircuits(bottomCircuits)) return true;
    
    // Проверяем торцевые подключения
    if ((hasLeftConnection && leftConnectionFitting && leftConnectionFitting.includes('_galv')) ||
        (hasRightConnection && rightConnectionFitting && rightConnectionFitting.includes('_galv'))) return true;
    
    return false;
  };
  
  // Определяем покрытие:
  // - Если основная труба - квадратный профиль → всегда Матовый (профили не бывают зеркальными)
  // - Если есть оцинкованные фланцы → всегда Матовый (для аннотации)
  // - Иначе: Зеркало если у всех труб есть, иначе Матовый
  const { determineCoating } = require('./pipeThicknessUtils');
  const hasGalvFlanges = hasGalvanizedFlanges();
  
  let coating;
  if (shape === 'square') {
    // Квадратные профили всегда матовые
    coating = 'Матовый';
  } else {
    // Для круглых труб добавляем диаметр основной трубы в список
    const allDiametersWithMain = [...uniqueDiameters, mainPipeDiameter];
    const uniqueDiametersWithMain = [...new Set(allDiametersWithMain)];
    coating = hasGalvFlanges ? 'Матовый' : determineCoating(uniqueDiametersWithMain);
  }

  const cylinders = [];
  const fittings = [];
  const welds = [];

  // Начальная позиция на чертеже (центр листа A3)
  const startX = 400;

  // Переменные для хранения информации о торцевых подключениях и заглушках
  let leftConnectionInfo = null;
  let rightConnectionInfo = null;
  let leftCapId = null;
  let rightCapId = null;
  let leftBortshaibaId = null;
  let rightBortshaibaId = null;
  let leftIntermediatePipeId = null;  // ID промежуточной трубы для приварных фланцев
  let rightIntermediatePipeId = null;

  // Функция для получения ключа заглушки по типу и диаметру/профилю
  const getCapKey = (capType, diameter, isSquare = false, profile = null) => {
    if (isSquare) {
      // Для квадратных труб всегда прямые заглушки с профилем
      // Заменяем латинскую x на русскую х для соответствия ключам в каталоге
      const profileKey = profile.replace('x', 'х');
      return `Zaglushka_PR_${profileKey}`;
    }
    
    if (capType === 'straight') {
      return `Zaglushka_PR_${diameter}`;
    } else {
      return `Zaglushka_EL_${diameter}`;
    }
  };

  // Вычисляем длину заглушек/переходов СНАЧАЛА (нужно для расчёта длины гребёнки)
  // Прямые заглушки - входят в общий размер (вместе с гребёнкой)
  // Эллиптические заглушки и переходы - НЕ входят в общий размер
  let leftCapActualLength = 0;
  let rightCapActualLength = 0;
  let leftStraightCapLength = 0;
  let rightStraightCapLength = 0;
  
  if (hasLeftCap) {
    // Для квадратных труб переходы не используются - всегда заглушка
    if (normalizedLeftCapElementType === 'reducer' && shape === 'round') {
      // Переход концентрический (только для круглых труб)
      const reducerData = fittingsData[leftReducer];
      if (reducerData) {
        leftCapActualLength = reducerData.length;
        // Переходы НЕ входят в общий размер гребёнки (как эллиптические заглушки)
      }
    } else {
      // Заглушка (для квадратных всегда прямая)
      const leftCapKey = getCapKey(normalizedLeftCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
      const leftCapData = fittingsData[leftCapKey];
      if (leftCapData) {
        leftCapActualLength = leftCapData.length;
        // Для квадратных труб все заглушки прямые
        if (normalizedLeftCapType === 'straight' || shape === 'square') {
          leftStraightCapLength = leftCapData.length;
        }
      }
    }
  }
  
  if (hasRightCap) {
    // Для квадратных труб переходы не используются - всегда заглушка
    if (normalizedRightCapElementType === 'reducer' && shape === 'round') {
      // Переход концентрический (только для круглых труб)
      const reducerData = fittingsData[rightReducer];
      if (reducerData) {
        rightCapActualLength = reducerData.length;
        // Переходы НЕ входят в общий размер гребёнки
      }
    } else {
      // Заглушка (для квадратных всегда прямая)
      const rightCapKey = getCapKey(normalizedRightCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
      const rightCapData = fittingsData[rightCapKey];
      if (rightCapData) {
        rightCapActualLength = rightCapData.length;
        // Для квадратных труб все заглушки прямые
        if (normalizedRightCapType === 'straight' || shape === 'square') {
          rightStraightCapLength = rightCapData.length;
        }
      }
    }
  }

  // Вычисляем длины бортшайб/шайб (для свободных фланцев и накидных гаек)
  // Шайбы входят в общий размер гребёнки ТОЛЬКО если нет перехода
  // Если есть переход — шайба идёт после перехода и НЕ вычитается из гребёнки
  let leftBortshaibaLength = 0;
  let rightBortshaibaLength = 0;
  
  if (hasLeftConnection && leftConnectionType === 'fitting') {
    const isFlange = leftConnectionFitting?.startsWith('Flanec_');
    const isFreeFlange = isFlange && !leftConnectionFitting?.endsWith('_welded');
    const isNakGaika = leftConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = leftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasLeftCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(leftConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          leftBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasLeftCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[leftConnectionFitting];
      if (shaibaInfo) {
        leftBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  if (hasRightConnection && rightConnectionType === 'fitting') {
    const isFlange = rightConnectionFitting?.startsWith('Flanec_');
    const isFreeFlange = isFlange && !rightConnectionFitting?.endsWith('_welded');
    const isNakGaika = rightConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = rightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasRightCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(rightConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          rightBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasRightCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[rightConnectionFitting];
      if (shaibaInfo) {
        rightBortshaibaLength = shaibaInfo.length;
      }
    }
  }

  // Для прямых заглушек и бортшайб: они входят в общую длину, поэтому гребёнка короче
  const actualMainPipeLength = mainPipeLength - leftStraightCapLength - rightStraightCapLength - leftBortshaibaLength - rightBortshaibaLength;

  // 1. Основная труба (гребёнка) - горизонтальная
  // Её позиция смещена вправо на длину левой прямой заглушки и левой бортшайбы
  const mainPipeId = `cyl-main-${Date.now()}`;
  const mainPipeStartX = startX + leftStraightCapLength + leftBortshaibaLength;
  
  // Определяем размер и спецификацию основной трубы в зависимости от формы
  let mainPipeSize, mainPipeSpec;
  
  if (shape === 'square') {
    // Для квадратного профиля
    const { DEFAULT_SQUARE_PIPE_COMBOS, SQUARE_PROFILE_DISPLAY_SIZE } = require('../pipeCatalog');
    const displaySize = SQUARE_PROFILE_DISPLAY_SIZE[mainPipeProfile] || 60;
    mainPipeSize = displaySize;
    
    // Ищем подходящую комбинацию профиль|толщина|покрытие
    const profileKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => 
      key.startsWith(`SQUARE_PROFILE|${mainPipeProfile}|`)
    );
    
    let thickness = '2';
    // Для квадратных профилей всегда используем 'Матовый', так как они не бывают зеркальными
    let squareCoating = 'Матовый';
    
    if (profileKey) {
      const parts = profileKey.split('|');
      thickness = parts[2] || '2';
      squareCoating = parts[3] || 'Матовый';
    }
    
    // Создаем объект pipeSpec для квадратных труб (как ожидает PriceDialog)
    // Используем squareCoating для спецификации трубы, но coating для аннотации
    mainPipeSpec = {
      shape: 'square',
      profile: mainPipeProfile,
      thickness: thickness,
      coating: squareCoating,
      squareType: 'profile'
    };
  } else {
    // Для круглой трубы
    mainPipeSize = mainPipeDiameter;
    mainPipeSpec = createPipeSpec(mainPipeDiameter, getMinThickness(mainPipeDiameter, coating), coating);
  }
  
  // Вычисляем позицию Y для симметричного расширения трубы
  // mainPipeCenterY - фиксированный центр трубы (для симметричного расширения)
  const mainPipeCenterY = 500;
  // startY - верхняя точка основной трубы (вычисляется от центра)
  const startY = mainPipeCenterY - mainPipeSize / 2;
  
  cylinders.push({
    id: mainPipeId,
    x: mainPipeStartX,
    y: startY,
    width: actualMainPipeLength,
    height: mainPipeSize,
    shape: shape, // Добавляем информацию о форме
    pipeSpec: mainPipeSpec,
  });

  // 2. Левая заглушка или переход (на левом торце гребёнки)
  if (hasLeftCap) {
    if (normalizedLeftCapElementType === 'reducer' && shape === 'round') {
      // Переход концентрический (только для круглых труб)
      const reducerData = fittingsData[leftReducer];
      if (reducerData) {
        leftCapId = `fitting-reducer-left-${Date.now()}`;
        fittings.push({
          id: leftCapId,
          key: leftReducer,
          itemKey: leftReducer,
          x: 0,
          y: 0,
          width: reducerData.diameter,
          height: reducerData.length,
          groupX: mainPipeStartX, // На левом краю гребёнки
          groupY: startY + mainPipeSize / 2,
          rotation: -90,
          offsetX: reducerData.diameter / 2,
          offsetY: reducerData.length,
        });
      }
    } else {
      // Заглушка
      const leftCapKey = getCapKey(normalizedLeftCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
      const leftCapData = fittingsData[leftCapKey];
      if (leftCapData) {
        leftCapId = `fitting-cap-left-${Date.now()}`;
        const capWidth = shape === 'square' ? mainPipeSize : leftCapData.diameter;
        // Заглушка всегда на краю гребёнки (визуально впритык)
        // Разница между прямой и эллиптической - в том, входит ли она в общий размер (leftStraightCapLength)
        const capGroupX = mainPipeStartX;
        fittings.push({
          id: leftCapId,
          key: leftCapKey,
          itemKey: leftCapKey,
          x: 0,
          y: 0,
          width: capWidth,
          height: leftCapData.length,
          groupX: capGroupX,
          groupY: startY + mainPipeSize / 2,
          rotation: -90,
          offsetX: capWidth / 2,
          offsetY: leftCapData.length,
        });
      }
    }
  }

  // 3. Правая заглушка или переход (на правом торце гребёнки)
  if (hasRightCap) {
    if (normalizedRightCapElementType === 'reducer' && shape === 'round') {
      // Переход концентрический (только для круглых труб)
      const reducerData = fittingsData[rightReducer];
      if (reducerData) {
        rightCapId = `fitting-reducer-right-${Date.now()}`;
        fittings.push({
          id: rightCapId,
          key: rightReducer,
          itemKey: rightReducer,
          x: 0,
          y: 0,
          width: reducerData.diameter,
          height: reducerData.length,
          groupX: mainPipeStartX + actualMainPipeLength, // На правом краю гребёнки
          groupY: startY + mainPipeSize / 2,
          rotation: 90,
          offsetX: reducerData.diameter / 2,
          offsetY: reducerData.length,
        });
      }
    } else {
      // Заглушка
      const rightCapKey = getCapKey(normalizedRightCapType, mainPipeDiameter, shape === 'square', mainPipeProfile);
      const rightCapData = fittingsData[rightCapKey];
      if (rightCapData) {
        rightCapId = `fitting-cap-right-${Date.now()}`;
        const capWidth = shape === 'square' ? mainPipeSize : rightCapData.diameter;
        // Заглушка всегда на краю гребёнки (визуально впритык)
        // Разница между прямой и эллиптической - в том, входит ли она в общий размер (rightStraightCapLength)
        const capGroupX = mainPipeStartX + actualMainPipeLength;
        fittings.push({
          id: rightCapId,
          key: rightCapKey,
          itemKey: rightCapKey,
          x: 0,
          y: 0,
          width: capWidth,
          height: rightCapData.length,
          groupX: capGroupX,
          groupY: startY + mainPipeSize / 2,
          rotation: 90,
          offsetX: capWidth / 2,
          offsetY: rightCapData.length,
        });
      }
    }
  }

  // 4. Левое подключение (от левого края общей конструкции)
  if (hasLeftConnection) {
    // Для прямых заглушек: левый край = startX (заглушка входит в mainPipeLength)
    // Для эллиптических заглушек и переходов: левый край = startX - длина (выступают наружу)
    // Для без заглушки: левый край = mainPipeStartX (край гребёнки)
    let leftEdgeX = mainPipeStartX; // По умолчанию - от гребёнки
    if (hasLeftCap) {
      if (normalizedLeftCapElementType === 'reducer') {
        // Переход выступает наружу
        leftEdgeX = startX - leftCapActualLength;
      } else if (normalizedLeftCapType === 'straight' || shape === 'square') {
        leftEdgeX = startX; // Прямая заглушка входит в общий размер (для квадратных всегда прямая)
      } else {
        leftEdgeX = startX - leftCapActualLength; // Эллиптическая выступает
      }
    }
    
    if (leftConnectionType === 'pipe') {
      const leftPipeId = `cyl-left-${Date.now()}`;
      const pipeLength = leftConnectionLength || 100;
      cylinders.push({
        id: leftPipeId,
        x: leftEdgeX - pipeLength,
        y: startY + (mainPipeSize - leftConnectionDiameter) / 2,
        width: pipeLength,
        height: leftConnectionDiameter,
        pipeSpec: createPipeSpec(leftConnectionDiameter, determineThickness(mainPipeDiameter, leftConnectionDiameter, coating), coating),
      });
      welds.push({
        id: `weld-left-${Date.now()}`,
        x: leftEdgeX,
        y: startY + mainPipeSize / 2,
        objects: [mainPipeId, leftPipeId],
      });
      leftConnectionInfo = {
        id: leftPipeId,
        type: 'cylinder',
        length: pipeLength,
        diameter: leftConnectionDiameter,
        startX: leftEdgeX,
      };
    } else {
      // Фитинг слева - поворачиваем на -90° чтобы смотрел влево
      const fittingInfo = fittingsData[leftConnectionFitting];
      if (fittingInfo) {
        const defaultFittingLength = endFittingAutoLength(leftConnectionFitting, null, fittingInfo.length);
        
        const fLength = Number(leftConnectionFittingLength) || defaultFittingLength;
        const fDiameter = fittingInfo.diameter;
        const leftFittingId = `fitting-left-${Date.now()}`;
        
        // Для фланцев, накидных гаек и шайб с наружной резьбой: они "налезают" на гребёнку/трубку
        const isFlange = leftConnectionFitting.startsWith('Flanec_');
        const isFreeFlange = isFlange && !leftConnectionFitting.endsWith('_welded');
        const isWeldedFlange = isFlange && !isFreeFlange;
        const isNakGaika = leftConnectionFitting.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = leftConnectionFitting.startsWith('Shaib_Nar_Rezba_');
        const needsWasher = isFreeFlange || isNakGaika; // Нужна ли шайба/бортшайба (для шайб с нар. резьбой НЕ нужна)
        const flangeOffset = isFlange ? fLength : 0;
        
        // Для свободных фланцев, приварных с переходами/заглушками, накидных гаек и шайб с нар. резьбой
        let bortshaibaLength = 0;
        let leftBortshaibaData = null;
        let leftIntermediatePipeData = null;
        let connectionPointX = leftEdgeX; // Точка подключения (край гребёнки или перехода)

        // Получаем диаметр трубы под фланец, накидную гайку или шайбу с нар. резьбой
        let pipeDiameterForFitting;
        if (isFlange) {
          const dn = extractDnFromFlangeKey(leftConnectionFitting);
          pipeDiameterForFitting = getPipeDiameterForDn(dn);
        } else if (isNakGaika) {
          const size = extractSizeFromNakGaikaKey(leftConnectionFitting);
          pipeDiameterForFitting = getPipeDiameterForNakGaika(size);
        } else if (isShaibaNarRezba) {
          const size = extractSizeFromShaibaNarRezba(leftConnectionFitting);
          pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size);
        }
        const flangePipeDiameter = pipeDiameterForFitting;
        
        // Получаем ключ шайбы/бортшайбы
        const shaibaKey = getShaibaKeyForFitting(leftConnectionFitting);
        
        // Проверяем, есть ли переход через "Заглушки и переходы"
        const hasReducerFromCaps = hasLeftCap && normalizedLeftCapElementType === 'reducer';
        const hasEllipticalCap = hasLeftCap && normalizedLeftCapElementType === 'cap' && normalizedLeftCapType === 'elliptical';
        const hasStraightCap = hasLeftCap && normalizedLeftCapElementType === 'cap' && (normalizedLeftCapType === 'straight' || shape === 'square');
        const reducerLength = hasReducerFromCaps ? leftCapActualLength : 0;
        const needsReducer = flangePipeDiameter && flangePipeDiameter !== (shape === 'square' ? mainPipeSize : mainPipeDiameter);

        // Условие входа в блок сложной логики (промежуточная труба, шайба/бортшайба и т.д.)
        // 1. Это свободный фланец (всегда нужна бортшайба)
        // 2. Это приварной фланец, но есть переход/заглушка или несовпадение диаметров (нужна трубка)
        // 3. Это накидная гайка (всегда нужна трубка + шайба)
        // 4. Это шайба с наружной резьбой (всегда нужна трубка, сама шайба = фитинг)
        const shouldUseComplexLogic = isFreeFlange || (isWeldedFlange && (needsReducer || hasReducerFromCaps || hasEllipticalCap || hasStraightCap)) || isNakGaika || isShaibaNarRezba;
        
        if (shouldUseComplexLogic) {
          
          if (needsWasher && shaibaKey) {
             const shaibaInfo = fittingsData[shaibaKey];
             bortshaibaLength = shaibaInfo?.length || 10;
          }
          
          // Для шайб с наружной резьбой: вычитаем длину самой шайбы из вылета (труба + шайба = вылет)
          if (isShaibaNarRezba) {
             const shaibaInfo = fittingsData[leftConnectionFitting];
             bortshaibaLength = shaibaInfo?.length || 10;
          }
          
          // === ЛОГИКА 1: Диаметры совпадают (фланец/гайка и гребёнка одного диаметра) ===
          if (!needsReducer && !hasReducerFromCaps && !hasEllipticalCap && !hasStraightCap) {
            // Для накидных гаек и свободных фланцев: шайба/бортшайба впритык к гребёнке, БЕЗ промежуточной трубки
            if ((isNakGaika || isFreeFlange) && shaibaKey) {
               const shaibaInfo = fittingsData[shaibaKey];
               if (shaibaInfo) {
                 const bortshaibaId = `fitting-shaiba-left-${Date.now()}`;
                 leftBortshaibaData = {
                   id: bortshaibaId,
                   key: shaibaKey,
                   itemKey: shaibaKey,
                   x: 0,
                   y: 0,
                   width: shaibaInfo.diameter,
                   height: bortshaibaLength,
                   ...(shaibaInfo.snapPoints && {
                     snapPoints: shaibaInfo.snapPoints,
                     useOriginalSize: true,
                   }),
                   correctionOffset: 3,
                   groupX: mainPipeStartX,
                   groupY: startY + mainPipeSize / 2,
                   rotation: -90,
                   offsetX: shaibaInfo.diameter / 2,
                   offsetY: bortshaibaLength,
                 };
                 fittings.push(leftBortshaibaData);
                 leftBortshaibaId = bortshaibaId;
                 connectionPointX = mainPipeStartX;
               }
            }
            // Для шайб с наружной резьбой: сама шайба впритык к гребёнке (не создаём отдельную бортшайбу)
            else if (isShaibaNarRezba) {
              // Для шайбы с нар. резьбой диаметры совпадают → она входит в общий размер
              // Просто обновляем connectionPointX, сама шайба будет создана позже
              connectionPointX = mainPipeStartX;
            }
          }
          // === ЛОГИКА 2: С переходом (разные диаметры) ===
          else if (hasReducerFromCaps) {
            // Малый конец перехода находится на mainPipeStartX - reducerLength
            const reducerSmallEndX = mainPipeStartX - reducerLength;
            connectionPointX = reducerSmallEndX;
            
            // Длина промежуточной трубы = общая длина - переход - бортшайба
            // Для приварных bortshaibaLength = 0, поэтому просто вычитается переход
            // Для накидных гаек и шайб с резьбой используем дефолт (40 для круглых, 55 для квадратных), для остальных - значение из параметра
            const totalConnectionLength = leftConnectionLength || ((isNakGaika || isShaibaNarRezba) ? (shape === 'square' ? 58 : 40) : 100);
            const intermediatePipeLength = Math.max(0, totalConnectionLength - reducerLength - bortshaibaLength);
            
            // 1. Добавляем промежуточную трубу меньшего диаметра
            if (intermediatePipeLength > 0 && flangePipeDiameter) {
              leftIntermediatePipeId = `cyl-left-intermediate-${Date.now()}`;
              leftIntermediatePipeData = {
                id: leftIntermediatePipeId,
                x: reducerSmallEndX - intermediatePipeLength,
                y: startY + (mainPipeSize - flangePipeDiameter) / 2,
                width: intermediatePipeLength,
                height: flangePipeDiameter,
                pipeSpec: createPipeSpec(flangePipeDiameter, determineThickness(mainPipeDiameter, flangePipeDiameter, coating), coating),
              };
              cylinders.push(leftIntermediatePipeData);
              connectionPointX = leftIntermediatePipeData.x; // Обновляем точку подключения (конец трубы)
            }
            
            // 2. Добавляем бортшайбу/шайбу на конец промежуточной трубы (или перехода)
            // Для свободных фланцев и накидных гаек
            if (needsWasher && shaibaKey) {
                const bortshaibaGroupX = connectionPointX;
                const bortshaibaInfo = fittingsData[shaibaKey];
                
                if (bortshaibaInfo) {
                  const bortshaibaId = `fitting-shaiba-left-${Date.now()}`;
                  leftBortshaibaData = {
                    id: bortshaibaId,
                    key: shaibaKey,
                    itemKey: shaibaKey,
                    x: 0,
                    y: 0,
                    width: bortshaibaInfo.diameter,
                    height: bortshaibaLength,
                    // Только для фитингов с snapPoints
                    ...(bortshaibaInfo.snapPoints && {
                      snapPoints: bortshaibaInfo.snapPoints,
                      useOriginalSize: true,
                    }),
                    correctionOffset: 3,
                    groupX: bortshaibaGroupX,
                    groupY: startY + mainPipeSize / 2,
                    rotation: -90,
                    offsetX: bortshaibaInfo.diameter / 2,
                    offsetY: bortshaibaLength,
                  };
                  fittings.push(leftBortshaibaData);
                  leftBortshaibaId = bortshaibaId;
                }
            }
          }
          // === ЛОГИКА 3: С заглушкой (эллиптической или прямой) (разные диаметры, нет перехода) ===
          else if (hasEllipticalCap || hasStraightCap) {
            // Заглушка на конце гребёнки
            const capLength = leftCapActualLength || 22; // Длина заглушки
            // Для накидных гаек и шайб с резьбой используем дефолт (40 для круглых, 55 для квадратных), для остальных - значение из параметра
            const totalConnectionLength = leftConnectionLength || ((isNakGaika || isShaibaNarRezba) ? (shape === 'square' ? 58 : 40) : 100);
            
            // Для накидных гаек и шайб с резьбой: вылет отсчитывается ОТ ЗАГЛУШКИ
            // Для остальных фитингов: вылет отсчитывается от трубы (как было раньше)
            let intermediatePipeLength;
            let capEndX;
            
            if (hasStraightCap) {
              // Прямая заглушка: входит в общий размер, поэтому промежуточная труба начинается от startX (левого края конструкции)
              capEndX = startX; // Левый край конструкции (где заглушка)
              // Для накидных гаек и шайб с резьбой: НЕ вычитаем длину заглушки (вылет от заглушки)
              if (isNakGaika || isShaibaNarRezba) {
                intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
              } else {
                intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
              }
            } else {
              // Эллиптическая заглушка: выступает наружу
              capEndX = mainPipeStartX - capLength;
              // Для накидных гаек и шайб с резьбой: НЕ вычитаем длину заглушки (вылет от заглушки)
              if (isNakGaika || isShaibaNarRezba) {
                intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
              } else {
                intermediatePipeLength = Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
              }
            }
            
            connectionPointX = capEndX;
            
            // 1. Добавляем промежуточную трубу меньшего диаметра (диаметра фланца)
            if (intermediatePipeLength > 0 && flangePipeDiameter) {
              leftIntermediatePipeId = `cyl-left-intermediate-${Date.now()}`;
              leftIntermediatePipeData = {
                id: leftIntermediatePipeId,
                x: capEndX - intermediatePipeLength,
                y: startY + (mainPipeSize - flangePipeDiameter) / 2,
                width: intermediatePipeLength,
                height: flangePipeDiameter,
                pipeSpec: createPipeSpec(flangePipeDiameter, determineThickness(mainPipeDiameter, flangePipeDiameter, coating), coating),
              };
              cylinders.push(leftIntermediatePipeData);
              connectionPointX = leftIntermediatePipeData.x;
            }
            
            // 2. Добавляем бортшайбу/шайбу на конец промежуточной трубы (или эллиптической заглушки)
            if (needsWasher && shaibaKey) {
                const bortshaibaGroupX = connectionPointX;
                const bortshaibaInfo = fittingsData[shaibaKey];

                if (bortshaibaInfo) {
                  const bortshaibaId = `fitting-shaiba-left-${Date.now()}`;
                  leftBortshaibaData = {
                    id: bortshaibaId,
                    key: shaibaKey,
                    itemKey: shaibaKey,
                    x: 0,
                    y: 0,
                    width: bortshaibaInfo.diameter,
                    height: bortshaibaLength,
                    // Только для фитингов с snapPoints
                    ...(bortshaibaInfo.snapPoints && {
                      snapPoints: bortshaibaInfo.snapPoints,
                      useOriginalSize: true,
                    }),
                    correctionOffset: 3,
                    groupX: bortshaibaGroupX,
                    groupY: startY + mainPipeSize / 2,
                    rotation: -90,
                    offsetX: bortshaibaInfo.diameter / 2,
                    offsetY: bortshaibaLength,
                  };
                  fittings.push(leftBortshaibaData);
                  leftBortshaibaId = bortshaibaId;
                }
            }
          }
          // === ЛОГИКА 4: Fallback (нет переходов/заглушек, но фланец/гайка меньше гребёнки) ===
          else if (needsWasher && shaibaKey) {
            // Просто ставим шайбу/бортшайбу на край гребёнки как fallback
            const bortshaibaInfo = fittingsData[shaibaKey];
            if (bortshaibaInfo) {
              const bortshaibaId = `fitting-shaiba-left-${Date.now()}`;
              leftBortshaibaData = {
                id: bortshaibaId,
                key: shaibaKey,
                itemKey: shaibaKey,
                x: 0,
                y: 0,
                width: bortshaibaInfo.diameter,
                height: bortshaibaLength,
                // Только для фитингов с snapPoints
                ...(bortshaibaInfo.snapPoints && {
                  snapPoints: bortshaibaInfo.snapPoints,
                  useOriginalSize: true,
                }),
                correctionOffset: 3,
                groupX: mainPipeStartX, // На краю гребёнки (fallback)
                groupY: startY + mainPipeSize / 2,
                rotation: -90,
                offsetX: bortshaibaInfo.diameter / 2,
                offsetY: bortshaibaLength,
              };
              fittings.push(leftBortshaibaData);
              leftBortshaibaId = bortshaibaId;
              connectionPointX = mainPipeStartX;
            }
          }
        }
        
        // Фланец/накидная гайка левее шайбы/бортшайбы (или на connectionPointX если нет шайбы)
        // Верх шайбы = groupX (внешний край, offsetY уже сместил точку привязки)
        const leftBortshaibaTop = leftBortshaibaData ? leftBortshaibaData.groupX : null;
        
        // Позиция фланца/накидной гайки:
        // Если со шайбой: от шайбы (её groupX) + длина
        // Если приварной (сложная логика): от connectionPointX + длина
        // Если обычный (простая логика): от leftEdgeX + длина
        
        let flangeX;
        if (leftBortshaibaData) {
             flangeX = leftBortshaibaTop + fLength;
        } else if (isShaibaNarRezba && shouldUseComplexLogic) {
             // Для шайб с наружной резьбой: размещаем ПОСЛЕ промежуточной трубы (как бортшайбу, не надеваем на трубу)
             flangeX = connectionPointX;
        } else if (shouldUseComplexLogic) {
             // Приварной на сложной логике (промежуточная труба)
             flangeX = connectionPointX + fLength;
        } else {
             // Стандартная логика
             flangeX = leftEdgeX + flangeOffset;
        }
        
        let dimensionOverride = {};
        
        fittings.push({
          id: leftFittingId,
          key: leftConnectionFitting,
          itemKey: leftConnectionFitting,
          x: 0,
          y: 0,
          width: fDiameter,
          height: fLength,
          groupX: (isFlange || isNakGaika || isShaibaNarRezba) ? flangeX : leftEdgeX,
          groupY: startY + mainPipeSize / 2,
          rotation: -90,
          offsetX: fDiameter / 2,
          // Для шайбы с наружной резьбой, когда диаметры совпадают: offsetY = fLength (как у бортшайбы)
          // Для остальных случаев: offsetY = fLength (стандартно)
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        // Для шайбы с наружной резьбой, когда диаметры совпадают: используем её ID для размерных линий
        if (isShaibaNarRezba && shouldUseComplexLogic && !needsReducer && !hasReducerFromCaps && !hasEllipticalCap && !hasStraightCap) {
          leftBortshaibaId = leftFittingId;
        }
        
        const endBody = leftConnectionFitting?.startsWith('Mufta_') || leftConnectionFitting?.startsWith('Nippel_');
        const calculatedLength = endBody
          ? fLength
          : (leftConnectionLength || ((isNakGaika || isShaibaNarRezba) ? (shape === 'square' ? 58 : 40) : 100));
        
        leftConnectionInfo = {
          id: leftFittingId,
          type: 'fitting',
          // Для фланцев, накидных гаек и шайб с нар. резьбой используем leftConnectionLength или дефолт
          // Для накидных гаек и шайб с резьбой дефолт 58 (квадратные) или 40 (круглые), для фланцев - 100
          length: calculatedLength,
          diameter: fDiameter,
          startX: leftEdgeX,
          fittingKey: leftConnectionFitting,
          // Для накидных гаек сохраняем ID трубки и шайбы для размерных линий
          ...(isNakGaika && {
            intermediatePipeId: leftIntermediatePipeId,
            bortshaibaId: leftBortshaibaId,
            // Сохраняем ID заглушки (размер от неё) - и для прямой и для эллиптической
            capId: (hasEllipticalCap || hasStraightCap) ? leftCapId : null,
          }),
          // Для шайб с наружной резьбой сохраняем ID трубки и заглушки для размерных линий
          ...(isShaibaNarRezba && {
            intermediatePipeId: leftIntermediatePipeId,
            // Сохраняем ID заглушки (размер от неё) - и для прямой и для эллиптической
            capId: (hasEllipticalCap || hasStraightCap) ? leftCapId : null,
          }),
          // Добавляем overrideText если есть
          ...dimensionOverride,
        };
      }
    }
  }

  // 5. Правое подключение (от правого края общей конструкции)
  if (hasRightConnection) {
    // Для прямых заглушек: правый край = startX + mainPipeLength (заглушка входит в mainPipeLength)
    // Для эллиптических заглушек и переходов: правый край = startX + mainPipeLength + длина (выступают наружу)
    // Для без заглушки: правый край = mainPipeStartX + actualMainPipeLength (край гребёнки)
    let rightEdgeX = mainPipeStartX + actualMainPipeLength; // По умолчанию - от гребёнки
    if (hasRightCap) {
      if (normalizedRightCapElementType === 'reducer') {
        // Переход выступает наружу
        rightEdgeX = startX + mainPipeLength + rightCapActualLength;
      } else if (normalizedRightCapType === 'straight' || shape === 'square') {
        rightEdgeX = startX + mainPipeLength; // Прямая заглушка входит в общий размер (для квадратных всегда прямая)
      } else {
        rightEdgeX = startX + mainPipeLength + rightCapActualLength; // Эллиптическая выступает
      }
    }
    
    if (rightConnectionType === 'pipe') {
      const rightPipeId = `cyl-right-${Date.now()}`;
      const pipeLength = rightConnectionLength || 100;
      cylinders.push({
        id: rightPipeId,
        x: rightEdgeX,
        y: startY + (mainPipeSize - rightConnectionDiameter) / 2,
        width: pipeLength,
        height: rightConnectionDiameter,
        pipeSpec: createPipeSpec(rightConnectionDiameter, determineThickness(mainPipeDiameter, rightConnectionDiameter, coating), coating),
      });
      welds.push({
        id: `weld-right-${Date.now()}`,
        x: rightEdgeX,
        y: startY + mainPipeSize / 2,
        objects: [mainPipeId, rightPipeId],
      });
      rightConnectionInfo = {
        id: rightPipeId,
        type: 'cylinder',
        length: pipeLength,
        diameter: rightConnectionDiameter,
        startX: rightEdgeX,
      };
    } else {
      // Фитинг справа - поворачиваем на 90° чтобы смотрел вправо
      const fittingInfo = fittingsData[rightConnectionFitting];
      if (fittingInfo) {
        const defaultFittingLength = endFittingAutoLength(rightConnectionFitting, null, fittingInfo.length);
        
        const fLength = Number(rightConnectionFittingLength) || defaultFittingLength;
        const fDiameter = fittingInfo.diameter;
        const rightFittingId = `fitting-right-${Date.now()}`;
        
        // Для фланцев, накидных гаек и шайб с наружной резьбой: они "налезают" на гребёнку/трубку
        const isFlange = rightConnectionFitting.startsWith('Flanec_');
        const isFreeFlange = isFlange && !rightConnectionFitting.endsWith('_welded');
        const isWeldedFlange = isFlange && !isFreeFlange;
        const isNakGaika = rightConnectionFitting.startsWith('Nak_Gaika_');
        const isShaibaNarRezba = rightConnectionFitting.startsWith('Shaib_Nar_Rezba_');
        const needsWasher = isFreeFlange || isNakGaika; // Нужна ли шайба/бортшайба (для шайб с нар. резьбой НЕ нужна)
        const flangeOffset = isFlange ? fLength : 0;
        
        // Правый край гребёнки
        const rightPipeEdge = mainPipeStartX + actualMainPipeLength;
        let connectionPointX = rightPipeEdge;
        
        // Для свободных фланцев, приварных с переходами/заглушками, накидных гаек и шайб с нар. резьбой
        let bortshaibaLength = 0;
        let rightBortshaibaData = null;
        let rightIntermediatePipeData = null;
        
        // Получаем диаметр трубы под фланец, накидную гайку или шайбу с нар. резьбой
        let pipeDiameterForFitting;
        if (isFlange) {
          const dn = extractDnFromFlangeKey(rightConnectionFitting);
          pipeDiameterForFitting = getPipeDiameterForDn(dn);
        } else if (isNakGaika) {
          const size = extractSizeFromNakGaikaKey(rightConnectionFitting);
          pipeDiameterForFitting = getPipeDiameterForNakGaika(size);
        } else if (isShaibaNarRezba) {
          const size = extractSizeFromShaibaNarRezba(rightConnectionFitting);
          pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size);
        }
        const flangePipeDiameter = pipeDiameterForFitting;
        
        // Получаем ключ шайбы/бортшайбы
        const shaibaKey = getShaibaKeyForFitting(rightConnectionFitting);
        
        // Проверяем, есть ли переход через "Заглушки и переходы"
        const hasReducerFromCaps = hasRightCap && normalizedRightCapElementType === 'reducer';
        const hasEllipticalCap = hasRightCap && normalizedRightCapElementType === 'cap' && normalizedRightCapType === 'elliptical';
        const hasStraightCap = hasRightCap && normalizedRightCapElementType === 'cap' && (normalizedRightCapType === 'straight' || shape === 'square');
        const reducerLength = hasReducerFromCaps ? rightCapActualLength : 0;
        const needsReducer = flangePipeDiameter && flangePipeDiameter !== (shape === 'square' ? mainPipeSize : mainPipeDiameter);

        // Условие входа в блок сложной логики
        const shouldUseComplexLogic = isFreeFlange || (isWeldedFlange && (needsReducer || hasReducerFromCaps || hasEllipticalCap || hasStraightCap)) || isNakGaika || isShaibaNarRezba;
        
        if (shouldUseComplexLogic) {
          
          if (needsWasher && shaibaKey) {
             const shaibaInfo = fittingsData[shaibaKey];
             bortshaibaLength = shaibaInfo?.length || 10;
          }
          
          // Для шайб с наружной резьбой: вычитаем длину самой шайбы из вылета (труба + шайба = вылет)
          if (isShaibaNarRezba) {
             const shaibaInfo = fittingsData[rightConnectionFitting];
             bortshaibaLength = shaibaInfo?.length || 10;
          }
          
          // === ЛОГИКА 1: Диаметры совпадают (фланец/гайка и гребёнка одного диаметра) ===
          if (!needsReducer && !hasReducerFromCaps && !hasEllipticalCap && !hasStraightCap) {
            // Для накидных гаек и свободных фланцев: шайба/бортшайба впритык к гребёнке, БЕЗ промежуточной трубки
            if ((isNakGaika || isFreeFlange) && shaibaKey) {
               const shaibaInfo = fittingsData[shaibaKey];
               if (shaibaInfo) {
                 const bortshaibaId = `fitting-shaiba-right-${Date.now()}`;
                 rightBortshaibaData = {
                   id: bortshaibaId,
                   key: shaibaKey,
                   itemKey: shaibaKey,
                   x: 0,
                   y: 0,
                   width: shaibaInfo.diameter,
                   height: bortshaibaLength,
                   ...(shaibaInfo.snapPoints && {
                     snapPoints: shaibaInfo.snapPoints,
                     useOriginalSize: true,
                   }),
                   correctionOffset: 3,
                   groupX: rightPipeEdge,
                   groupY: startY + mainPipeSize / 2,
                   rotation: 90,
                   offsetX: shaibaInfo.diameter / 2,
                   offsetY: bortshaibaLength,
                 };
                 fittings.push(rightBortshaibaData);
                 rightBortshaibaId = bortshaibaId;
                 connectionPointX = rightPipeEdge;
               }
            }
            // Для шайб с наружной резьбой: сама шайба впритык к гребёнке (не создаём отдельную бортшайбу)
            else if (isShaibaNarRezba) {
              // Для шайбы с нар. резьбой диаметры совпадают → она входит в общий размер
              // Просто обновляем connectionPointX, сама шайба будет создана позже
              connectionPointX = rightPipeEdge;
            }
          }
          // === ЛОГИКА 2: С переходом (разные диаметры) ===
          else if (hasReducerFromCaps) {
            // Малый конец перехода находится на rightPipeEdge + reducerLength
            const reducerSmallEndX = rightPipeEdge + reducerLength;
            connectionPointX = reducerSmallEndX;
            
            // Длина промежуточной трубы = общая длина - переход - бортшайба
            // Для накидных гаек и шайб с резьбой используем дефолт (40 для круглых, 55 для квадратных), для остальных - значение из параметра
            const totalConnectionLength = rightConnectionLength || ((isNakGaika || isShaibaNarRezba) ? (shape === 'square' ? 58 : 40) : 100);
            const intermediatePipeLength = Math.max(0, totalConnectionLength - reducerLength - bortshaibaLength);
            
            // 1. Добавляем промежуточную трубу меньшего диаметра
            if (intermediatePipeLength > 0 && flangePipeDiameter) {
              rightIntermediatePipeId = `cyl-right-intermediate-${Date.now()}`;
              rightIntermediatePipeData = {
                id: rightIntermediatePipeId,
                x: reducerSmallEndX,
                y: startY + (mainPipeSize - flangePipeDiameter) / 2,
                width: intermediatePipeLength,
                height: flangePipeDiameter,
                pipeSpec: createPipeSpec(flangePipeDiameter, determineThickness(mainPipeDiameter, flangePipeDiameter, coating), coating),
              };
              cylinders.push(rightIntermediatePipeData);
              connectionPointX = rightIntermediatePipeData.x + intermediatePipeLength;
            }
            
            // 2. Добавляем бортшайбу/шайбу на конец промежуточной трубы (или перехода)
            if (needsWasher && shaibaKey) {
                const bortshaibaGroupX = connectionPointX;
                const bortshaibaInfo = fittingsData[shaibaKey];

                if (bortshaibaInfo) {
                  const bortshaibaId = `fitting-shaiba-right-${Date.now()}`;
                  rightBortshaibaData = {
                    id: bortshaibaId,
                    key: shaibaKey,
                    itemKey: shaibaKey,
                    x: 0,
                    y: 0,
                    width: bortshaibaInfo.diameter,
                    height: bortshaibaLength,
                    // Только для фитингов с snapPoints
                    ...(bortshaibaInfo.snapPoints && {
                      snapPoints: bortshaibaInfo.snapPoints,
                      useOriginalSize: true,
                    }),
                    correctionOffset: 3,
                    groupX: bortshaibaGroupX,
                    groupY: mainPipeCenterY,
                    rotation: 90,
                    offsetX: bortshaibaInfo.diameter / 2,
                    offsetY: bortshaibaLength,
                  };
                  fittings.push(rightBortshaibaData);
                  rightBortshaibaId = bortshaibaId;
                }
            }
          }
          // === ЛОГИКА 3: С заглушкой (эллиптической или прямой) (разные диаметры, нет перехода) ===
          else if (hasEllipticalCap || hasStraightCap) {
            // Заглушка на конце гребёнки
            const capLength = rightCapActualLength || 22; // Длина заглушки
            // Для накидных гаек и шайб с резьбой используем дефолт (40 для круглых, 55 для квадратных), для остальных - значение из параметра
            const totalConnectionLength = rightConnectionLength || ((isNakGaika || isShaibaNarRezba) ? (shape === 'square' ? 58 : 40) : 100);
            
            // Для накидных гаек и шайб с резьбой: вылет отсчитывается ОТ ЗАГЛУШКИ
            // Для остальных фитингов: вылет отсчитывается от трубы (как было раньше)
            let intermediatePipeLength;
            let capEndX;
            
            if (hasStraightCap) {
              // Прямая заглушка: входит в общий размер, поэтому промежуточная труба начинается от startX + mainPipeLength (правого края конструкции)
              capEndX = startX + mainPipeLength; // Правый край конструкции (где заглушка)
              // Для накидных гаек и шайб с резьбой: НЕ вычитаем длину заглушки (вылет от заглушки)
              if (isNakGaika || isShaibaNarRezba) {
                intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
              } else {
                intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
              }
            } else {
              // Эллиптическая заглушка: выступает наружу
              capEndX = rightPipeEdge + capLength;
              // Для накидных гаек и шайб с резьбой: НЕ вычитаем длину заглушки (вылет от заглушки)
              if (isNakGaika || isShaibaNarRezba) {
                intermediatePipeLength = Math.max(0, totalConnectionLength - bortshaibaLength);
              } else {
                intermediatePipeLength = Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
              }
            }
            
            connectionPointX = capEndX;
            
            // 1. Добавляем промежуточную трубу меньшего диаметра (диаметра фланца)
            if (intermediatePipeLength > 0 && flangePipeDiameter) {
              rightIntermediatePipeId = `cyl-right-intermediate-${Date.now()}`;
              rightIntermediatePipeData = {
                id: rightIntermediatePipeId,
                x: capEndX,
                y: startY + (mainPipeSize - flangePipeDiameter) / 2,
                width: intermediatePipeLength,
                height: flangePipeDiameter,
                pipeSpec: createPipeSpec(flangePipeDiameter, determineThickness(mainPipeDiameter, flangePipeDiameter, coating), coating),
              };
              cylinders.push(rightIntermediatePipeData);
              connectionPointX = rightIntermediatePipeData.x + intermediatePipeLength;
            }
            
            // 2. Добавляем бортшайбу/шайбу на конец промежуточной трубы (или эллиптической заглушки)
            if (needsWasher && shaibaKey) {
                const bortshaibaGroupX = connectionPointX;
                const bortshaibaInfo = fittingsData[shaibaKey];

                if (bortshaibaInfo) {
                  const bortshaibaId = `fitting-shaiba-right-${Date.now()}`;
                  rightBortshaibaData = {
                    id: bortshaibaId,
                    key: shaibaKey,
                    itemKey: shaibaKey,
                    x: 0,
                    y: 0,
                    width: bortshaibaInfo.diameter,
                    height: bortshaibaLength,
                    // Только для фитингов с snapPoints
                    ...(bortshaibaInfo.snapPoints && {
                      snapPoints: bortshaibaInfo.snapPoints,
                      useOriginalSize: true,
                    }),
                    correctionOffset: 3,
                    groupX: bortshaibaGroupX,
                    groupY: mainPipeCenterY,
                    rotation: 90,
                    offsetX: bortshaibaInfo.diameter / 2,
                    offsetY: bortshaibaLength,
                  };
                  fittings.push(rightBortshaibaData);
                  rightBortshaibaId = bortshaibaId;
                }
            }
          }
          // === ЛОГИКА 4: Fallback ===
          else if (needsWasher && shaibaKey) {
             // Fallback: просто ставим шайбу/бортшайбу на край гребёнки
             const bortshaibaInfo = fittingsData[shaibaKey];
             if (bortshaibaInfo) {
               const bortshaibaId = `fitting-shaiba-right-${Date.now()}`;
               rightBortshaibaData = {
                 id: bortshaibaId,
                 key: shaibaKey,
                 itemKey: shaibaKey,
                 x: 0,
                 y: 0,
                 width: bortshaibaInfo.diameter,
                 height: bortshaibaLength,
                 // Только для фитингов с snapPoints
                 ...(bortshaibaInfo.snapPoints && {
                   snapPoints: bortshaibaInfo.snapPoints,
                   useOriginalSize: true,
                 }),
                 correctionOffset: 3,
                 groupX: rightPipeEdge,
                 groupY: startY + mainPipeDiameter / 2,
                 rotation: 90,
                 offsetX: bortshaibaInfo.diameter / 2,
                 offsetY: bortshaibaLength,
               };
               fittings.push(rightBortshaibaData);
               rightBortshaibaId = bortshaibaId;
               connectionPointX = rightPipeEdge;
             }
          }
        }
        
        // Фланец/накидная гайка левее шайбы/бортшайбы (или на connectionPointX если нет шайбы)
        const rightBortshaibaTop = rightBortshaibaData ? rightBortshaibaData.groupX : null;
        
        let flangeX;
        if (rightBortshaibaData) {
            flangeX = rightBortshaibaTop - fLength;
        } else if (isShaibaNarRezba && shouldUseComplexLogic) {
            // Для шайб с наружной резьбой: размещаем ПОСЛЕ промежуточной трубы (как бортшайбу, не надеваем на трубу)
            flangeX = connectionPointX;
        } else if (shouldUseComplexLogic) {
            // Приварной на сложной логике
            flangeX = connectionPointX - fLength;
        } else {
            // Стандартная логика
            flangeX = rightPipeEdge - flangeOffset;
        }
        
        let dimensionOverride = {};
        
        fittings.push({
          id: rightFittingId,
          key: rightConnectionFitting,
          itemKey: rightConnectionFitting,
          x: 0,
          y: 0,
          width: fDiameter,
          height: fLength,
          groupX: (isFlange || isNakGaika || isShaibaNarRezba) ? flangeX : rightEdgeX,
          groupY: startY + mainPipeSize / 2,
          rotation: 90,
          offsetX: fDiameter / 2,
          // Для шайбы с наружной резьбой, когда диаметры совпадают: offsetY = fLength (как у бортшайбы)
          // Для остальных случаев: offsetY = fLength (стандартно)
          offsetY: fLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        // Для шайбы с наружной резьбой, когда диаметры совпадают: используем её ID для размерных линий
        if (isShaibaNarRezba && shouldUseComplexLogic && !needsReducer && !hasReducerFromCaps && !hasEllipticalCap && !hasStraightCap) {
          rightBortshaibaId = rightFittingId;
        }
        
        rightConnectionInfo = {
          id: rightFittingId,
          type: 'fitting',
          // Для фланцев, накидных гаек и шайб с нар. резьбой используем rightConnectionLength или дефолт
          // Для накидных гаек и шайб с резьбой дефолт 40 (круглые) или 55 (квадратные), для фланцев - 100
          length: (rightConnectionFitting?.startsWith('Mufta_') || rightConnectionFitting?.startsWith('Nippel_'))
            ? fLength
            : (rightConnectionLength || ((isNakGaika || isShaibaNarRezba) ? (shape === 'square' ? 58 : 40) : 100)),
          diameter: fDiameter,
          startX: rightEdgeX,
          fittingKey: rightConnectionFitting,
          // Для накидных гаек сохраняем ID трубки и шайбы для размерных линий
          ...(isNakGaika && {
            intermediatePipeId: rightIntermediatePipeId,
            bortshaibaId: rightBortshaibaId,
            // Сохраняем ID заглушки (размер от неё) - и для прямой и для эллиптической
            capId: (hasEllipticalCap || hasStraightCap) ? rightCapId : null,
          }),
          // Для шайб с наружной резьбой сохраняем ID трубки и заглушки для размерных линий
          ...(isShaibaNarRezba && {
            intermediatePipeId: rightIntermediatePipeId,
            // Сохраняем ID заглушки (размер от неё) - и для прямой и для эллиптической
            capId: (hasEllipticalCap || hasStraightCap) ? rightCapId : null,
          }),
          // Добавляем overrideText если есть
          ...dimensionOverride,
        };
      }
    }
  }

  // 6. Отводы контуров (вертикальные)
  const circuitObjects = []; // Для хранения информации о контурах для размерных линий
  
  // Вычисляем позиции контуров с учётом индивидуальных межосевых
  let currentX = startX + actualFirstCircuitOffset;
  
  const circuitPositionsX = circuits.map((circuit, index) => {
    const pos = currentX;
    if (index < circuits.length - 1) {
      // Проверяем что circuit не undefined
      const spacing = circuit ? (Number(circuit.spacingToNext) || circuitSpacing) : circuitSpacing;
      currentX += spacing;
    }
    return pos;
  });
  
  circuits.forEach((circuit, index) => {
    // Пропускаем undefined или null элементы
    if (!circuit) {
      return;
    }
    
    const circuitX = circuitPositionsX[index];
    
    if (circuit.type === 'pipe') {
      const circuitId = `cyl-circuit-${index + 1}-${Date.now()}`;
      const pipeLength = Number(circuit.length) || 80;
      const pipeDiameter = Number(circuit.diameter) || 26.9;
      cylinders.push({
        id: circuitId,
        x: circuitX - pipeDiameter / 2,
        y: startY - pipeLength,
        width: pipeDiameter,
        height: pipeLength,
        pipeSpec: createPipeSpec(pipeDiameter, determineThickness(mainPipeDiameter, pipeDiameter, coating), coating),
      });
      welds.push({
        id: `weld-circuit-${index + 1}-${Date.now()}`,
        x: circuitX,
        y: startY,
        objects: [mainPipeId, circuitId],
      });
      
      // Сохраняем данные для размерных линий
      circuitObjects.push({
        id: circuitId,
        type: 'cylinder',
        x: circuitX - pipeDiameter / 2,
        y: startY - pipeLength,
        width: pipeDiameter,
        height: pipeLength,
        centerX: circuitX,
      });
    } else {
      // Фитинг контура
      const fittingInfo = fittingsData[circuit.fittingId];
      if (fittingInfo) {
        const isFlange = circuit.fittingId?.startsWith('Flanec_');
        const isFreeFlange = isFlange && !circuit.fittingId?.endsWith('_welded');
        
        if (isFlange) {
          // === ФЛАНЕЦ НА КОНТУРЕ ===
          // Свободный: трубка → бортшайба → фланец (надет на трубку)
          // Приварной: трубка → фланец (надет на трубку, без бортшайбы)
          // Вылет = длина трубки (фланец надевается сверху)
          const dn = extractDnFromFlangeKey(circuit.fittingId);
          const flangePipeDiameter = getPipeDiameterForDn(dn) || fittingInfo.diameter;
          const flangePipeLength = Number(circuit.flangePipeLength) || 100;
          const flangeLength = fittingInfo.length;
          const flangeDiameter = fittingInfo.diameter;
          
          // 1. Добавляем трубку (вертикальная, вверх от гребёнки)
          const circuitPipeId = `cyl-circuit-flange-${index + 1}-${Date.now()}`;
          let currentY = startY; // Начинаем от верха гребёнки
          
          // Бортшайба для свободных фланцев
          let bortshaibaLength = 0;
          if (isFreeFlange) {
            const bortshaibaKey = `Bortshaiba_${dn}`;
            const bortshaibaInfo = fittingsData[bortshaibaKey];
            if (bortshaibaInfo) {
              bortshaibaLength = bortshaibaInfo.length || 10;
            }
          }
          
          // Длина трубки:
          // Для свободного: вылет - бортшайба (бортшайба на конце, общий вылет = трубка + бортшайба)
          // Для приварного: вылет (фланец надет на трубку)
          const actualPipeLength = isFreeFlange
            ? Math.max(0, flangePipeLength - bortshaibaLength)
            : flangePipeLength;
          
          if (actualPipeLength > 0) {
            cylinders.push({
              id: circuitPipeId,
              x: circuitX - flangePipeDiameter / 2,
              y: startY - actualPipeLength,
              width: flangePipeDiameter,
              height: actualPipeLength,
              pipeSpec: createPipeSpec(flangePipeDiameter, determineThickness(flangePipeDiameter, mainPipeDiameter), coating),
              // Глубина вставки патрубка фланца в основную трубу (для производственной длины)
              insertionDepth: flangePipeInsertion(mainPipeDiameter, flangePipeDiameter, params.shape),
            });
            welds.push({
              id: `weld-circuit-flange-pipe-${index + 1}-${Date.now()}`,
              x: circuitX,
              y: startY,
              objects: [mainPipeId, circuitPipeId],
            });
            currentY = startY - actualPipeLength;
          }
          
          // 2. Добавляем бортшайбу (для свободных фланцев)
          let bortshaibaId = null;
          if (isFreeFlange) {
            const bortshaibaKey = `Bortshaiba_${dn}`;
            const bortshaibaInfo = fittingsData[bortshaibaKey];
            if (bortshaibaInfo) {
              bortshaibaId = `fitting-circuit-bortshaiba-${index + 1}-${Date.now()}`;
              // Бортшайба на верхнем конце трубки (rotation = 0, смотрит вверх)
              fittings.push({
                id: bortshaibaId,
                key: bortshaibaKey,
                itemKey: bortshaibaKey,
                x: 0,
                y: 0,
                width: bortshaibaInfo.diameter,
                height: bortshaibaLength,
                snapPoints: bortshaibaInfo.snapPoints,
                correctionOffset: 3,
                useOriginalSize: true,
                groupX: circuitX,
                groupY: currentY,
                rotation: 0,
                offsetX: bortshaibaInfo.diameter / 2,
                offsetY: bortshaibaLength,
                igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
                isLongCircuit: false, // IGES: короткие патрубки у этажного
                igesCreateHole: true, // IGES: этот фитинг создаёт отверстие
                holeDiameter: getHoleDiameterForPipe(flangePipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
                igesHoleDirection: 'up', // IGES: верхний контур — отверстие вверх
              });
              currentY = currentY - bortshaibaLength;
            }
          }
          
          // 3. Добавляем фланец (надет на трубку)
          const flangeId = `fitting-circuit-flange-${index + 1}-${Date.now()}`;
          // Фланец надет на трубку, его верх = верху трубки
          // Для свободного: бортшайба на конце трубки, фланец перед ней
          // Для приварного: фланец на конце трубки (приварен)
          const flangeY = startY - actualPipeLength + flangeLength;
          
          fittings.push({
            id: flangeId,
            key: circuit.fittingId,
            itemKey: circuit.fittingId,
            x: 0,
            y: 0,
            width: flangeDiameter,
            height: flangeLength,
            groupX: circuitX,
            groupY: flangeY,
            rotation: 0,
            offsetX: flangeDiameter / 2,
            offsetY: flangeLength,
            igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
            isLongCircuit: false, // IGES: короткие патрубки у этажного
            igesCreateHole: !isFreeFlange, // IGES: для приварных фланцев (без бортшайбы) создаёт отверстие
            holeDiameter: getHoleDiameterForPipe(flangePipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
            igesHoleDirection: 'up', // IGES: верхний контур — отверстие вверх
          });
          
          // Сохраняем данные для размерных линий (вылет контура)
          // Для свободного фланца: верхняя точка = бортшайба
          // Для приварного: верхняя точка = фланец
          const circuitObjData = {
            id: circuitPipeId,
            type: 'cylinder',
            x: circuitX - flangePipeDiameter / 2,
            y: startY - flangePipeLength, // Верх = конец вылета
            width: flangePipeDiameter,
            height: flangePipeLength, // Полный вылет
            centerX: circuitX,
            isFlange: true,
            isFreeFlange: isFreeFlange,
            flangeId: flangeId, // ID фланца для balloons
            // Верхняя точка: бортшайба (свободный) или фланец (приварной)
            topObjectId: isFreeFlange && bortshaibaId ? bortshaibaId : flangeId,
            topObjectType: 'fitting',
            // Нижняя точка: всегда трубка (у гребёнки)
            bottomObjectId: circuitPipeId,
            bottomObjectType: 'cylinder',
          };
          circuitObjects.push(circuitObjData);
        } else if (circuit.fittingId?.startsWith('Nak_Gaika_')) {
          // === НАКИДНАЯ ГАЙКА НА КОНТУРЕ ===
          // Трубка → шайба накидной гайки → накидная гайка (надета на трубку)
          // Вылет = длина трубки (накидная гайка надевается сверху)
          const size = extractSizeFromNakGaikaKey(circuit.fittingId);
          const nakGaikaPipeDiameter = getPipeDiameterForNakGaika(size);
          const nakGaikaPipeLength = nakShaibaPipeMeasure(circuit.flangePipeLength, 0, params.shape, false).visible;
          const nakGaikaLength = fittingInfo.length;
          const nakGaikaDiameter = fittingInfo.diameter;
          
          // 1. Добавляем трубку (вертикальная, вверх от гребёнки)
          const circuitPipeId = `cyl-circuit-nakgaika-${index + 1}-${Date.now()}`;
          let currentY = startY; // Начинаем от верха гребёнки
          
          // Шайба накидной гайки
          const shaibaKey = getShaibaKeyForFitting(circuit.fittingId);
          const shaibaInfo = fittingsData[shaibaKey];
          const shaibaLength = shaibaInfo?.length || 10;
          
          // Длина трубки: вылет - шайба (шайба на конце, общий вылет = трубка + шайба)
          const nakMeasure = nakShaibaPipeMeasure(circuit.flangePipeLength, shaibaLength, params.shape, false);
          const actualPipeLength = nakMeasure.outside;
          
          if (actualPipeLength > 0) {
            cylinders.push({
              id: circuitPipeId,
              x: circuitX - nakGaikaPipeDiameter / 2,
              y: startY - actualPipeLength,
              width: nakGaikaPipeDiameter,
              height: actualPipeLength,
              pipeSpec: createPipeSpec(nakGaikaPipeDiameter, 'x3', coating),
              insertionDepth: nakMeasure.insertion,
            });
            welds.push({
              id: `weld-circuit-nakgaika-pipe-${index + 1}-${Date.now()}`,
              x: circuitX,
              y: startY,
              objects: [mainPipeId, circuitPipeId],
            });
            currentY = startY - actualPipeLength;
          }
          
          // 2. Добавляем шайбу накидной гайки
          let shaibaId = null;
          if (shaibaInfo) {
            shaibaId = `fitting-circuit-shaiba-${index + 1}-${Date.now()}`;
            // Шайба на верхнем конце трубки (rotation = 0, смотрит вверх)
            fittings.push({
              id: shaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0,
              y: 0,
              width: shaibaInfo.diameter,
              height: shaibaLength,
              groupX: circuitX,
              groupY: currentY,
              rotation: 0,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: shaibaLength,
              igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
              isLongCircuit: false, // IGES: короткие патрубки у этажного
              igesCreateHole: true, // IGES: этот фитинг создаёт отверстие
              holeDiameter: getHoleDiameterForPipe(nakGaikaPipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
              igesHoleDirection: 'up', // IGES: верхний контур — отверстие вверх
            });
            currentY = currentY - shaibaLength;
          }
          
          // 3. Добавляем накидную гайку (надета на трубку)
          const nakGaikaId = `fitting-circuit-nakgaika-${index + 1}-${Date.now()}`;
          // Накидная гайка надета на трубку, её верх = верху трубки
          // Шайба на конце трубки, накидная гайка перед ней
          const nakGaikaY = startY - actualPipeLength + nakGaikaLength;
          
          fittings.push({
            id: nakGaikaId,
            key: circuit.fittingId,
            itemKey: circuit.fittingId,
            x: 0,
            y: 0,
            width: nakGaikaDiameter,
            height: nakGaikaLength,
            groupX: circuitX,
            groupY: nakGaikaY,
            rotation: 0,
            offsetX: nakGaikaDiameter / 2,
            offsetY: nakGaikaLength,
            igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
            isLongCircuit: false, // IGES: короткие патрубки у этажного
            igesCreateHole: false, // IGES: отверстие уже создано шайбой
            holeDiameter: nakGaikaDiameter, // IGES: для справки
            igesHoleDirection: 'up', // IGES: верхний контур — отверстие вверх
          });
          
          // Сохраняем данные для размерных линий и balloons
          const circuitObjData = {
            id: circuitPipeId,
            type: 'cylinder',
            x: circuitX - nakGaikaPipeDiameter / 2,
            y: startY - nakGaikaPipeLength, // Верх = конец вылета
            width: nakGaikaPipeDiameter,
            height: nakGaikaPipeLength, // Полный вылет
            centerX: circuitX,
            isNakGaika: true,
            nakGaikaId: nakGaikaId, // ID накидной гайки для balloons
            // Верхняя точка: шайба накидной гайки
            topObjectId: shaibaId,
            topObjectType: 'fitting',
            // Нижняя точка: всегда трубка (у гребёнки)
            bottomObjectId: circuitPipeId,
            bottomObjectType: 'cylinder',
            // Вылет уже видимый: размер совпадает с моделью
          };
          circuitObjects.push(circuitObjData);
        } else if (circuit.fittingId?.startsWith('Shaib_Nar_Rezba_')) {
          // === ШАЙБА С НАРУЖНОЙ РЕЗЬБОЙ НА КОНТУРЕ ===
          // Трубка → шайба с наружной резьбой (шайба идет ПОСЛЕ трубы, как бортшайба)
          // Вылет = длина трубки + шайба (пользователь указывает общий вылет)
          const size = extractSizeFromShaibaNarRezba(circuit.fittingId);
          const shaibaPipeDiameter = getPipeDiameterForShaibaNarRezba(size);
          const shaibaPipeLength = nakShaibaPipeMeasure(circuit.flangePipeLength, 0, params.shape, true).visible;
          const shaibaLength = fittingInfo.length;
          const shaibaDiameter = fittingInfo.diameter;
          
          // 1. Добавляем трубку (вертикальная, вверх от гребёнки)
          const circuitPipeId = `cyl-circuit-shaiba-${index + 1}-${Date.now()}`;
          let currentY = startY; // Начинаем от верха гребёнки
          
          // Длина трубки: вылет - длина шайбы (шайба идет после трубы, общий вылет = труба + шайба)
          const shaibaMeasure = nakShaibaPipeMeasure(circuit.flangePipeLength, shaibaLength, params.shape, true);
          const actualPipeLength = shaibaMeasure.outside;
          
          if (actualPipeLength > 0) {
            cylinders.push({
              id: circuitPipeId,
              x: circuitX - shaibaPipeDiameter / 2,
              y: startY - actualPipeLength,
              width: shaibaPipeDiameter,
              height: actualPipeLength,
              pipeSpec: createPipeSpec(shaibaPipeDiameter, 'x3', coating),
              insertionDepth: shaibaMeasure.insertion,
            });
            welds.push({
              id: `weld-circuit-shaiba-pipe-${index + 1}-${Date.now()}`,
              x: circuitX,
              y: startY,
              objects: [mainPipeId, circuitPipeId],
            });
            currentY = startY - actualPipeLength;
          }
          
          // 2. Добавляем шайбу с наружной резьбой (на верхнем конце трубки, rotation = 0, смотрит вверх)
          const shaibaId = `fitting-circuit-shaiba-nar-rezba-${index + 1}-${Date.now()}`;
          
          fittings.push({
            id: shaibaId,
            key: circuit.fittingId,
            itemKey: circuit.fittingId,
            x: 0,
            y: 0,
            width: shaibaDiameter,
            height: shaibaLength,
            groupX: circuitX,
            groupY: currentY,
            rotation: 0,
            offsetX: shaibaDiameter / 2,
            offsetY: shaibaLength,
            igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
            isLongCircuit: false, // IGES: короткие патрубки у этажного
            igesCreateHole: true, // IGES: этот фитинг создаёт отверстие
            holeDiameter: getHoleDiameterForPipe(shaibaPipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
            igesHoleDirection: 'up', // IGES: верхний контур — отверстие вверх
          });
          currentY = currentY - shaibaLength;
          
          // Сохраняем данные для размерных линий и balloons
          const circuitObjData = {
            id: circuitPipeId,
            type: 'cylinder',
            x: circuitX - shaibaPipeDiameter / 2,
            y: startY - shaibaPipeLength, // верх — торец шайбы
            width: shaibaPipeDiameter,
            height: shaibaPipeLength, // видимый вылет до торца шайбы
            centerX: circuitX,
            isShaibaNarRezba: true,
            shaibaId: shaibaId, // ID шайбы для balloons
            // Верхняя точка: шайба с наружной резьбой
            topObjectId: shaibaId,
            topObjectType: 'fitting',
            // Нижняя точка: всегда трубка (у гребёнки)
            bottomObjectId: circuitPipeId,
            bottomObjectType: 'cylinder',
            // Вылет уже видимый: размер совпадает с моделью
          };
          circuitObjects.push(circuitObjData);
        } else {
          // === ОБЫЧНЫЙ ФИТИНГ (не фланец, не накидная гайка, не шайба с наружной резьбой) ===
          const catalogLength = fittingInfo.length;
          let fLength;
          let dimensionOverride = {};

          const nippelMuftaResolved = resolveNippelMuftaFitting({
            fittingId: circuit.fittingId,
            fittingLength: circuit.fittingLength ?? null,
            pipeDiameter: mainPipeDiameter,
            catalogLength,
            shape,
          });

          if (nippelMuftaResolved) {
            fLength = nippelMuftaResolved.length;
            dimensionOverride = nippelMuftaResolved.dimensionOverride;
          } else {
            const defaultFittingLength = catalogLength;
            fLength = circuit.fittingLength != null ? Number(circuit.fittingLength) : defaultFittingLength;

            if (circuit.fittingLength === null || circuit.fittingLength === undefined) {
              const defaultLength = defaultFittingLength;
              let customValue = defaultLength;
              
              if (shape === 'square') {
                if (circuit.fittingId?.startsWith('Nippel_')) {
                  customValue = defaultLength - 3;
                  dimensionOverride = {
                    overrideEnabled: true,
                    overrideText: String(customValue),
                  };
                } else if (circuit.fittingId?.startsWith('Mufta_')) {
                  customValue = getSquareMuftaCircuitDisplayValue(circuit.fittingId, defaultLength);
                  if (customValue != null) {
                    dimensionOverride = {
                      overrideEnabled: true,
                      overrideText: String(customValue),
                    };
                  }
                } else if (circuit.fittingId?.startsWith('Nak_Gaika_') || circuit.fittingId?.startsWith('Shaib_Nar_Rezba_')) {
                  customValue = 54;
                  dimensionOverride = {
                    overrideEnabled: true,
                    overrideText: String(customValue),
                  };
                }
              } else {
                if (circuit.fittingId?.startsWith('Nippel_')) {
                  customValue = getNippelRoundOverrideValue(circuit.fittingId, mainPipeDiameter, defaultLength);
                  dimensionOverride = {
                    overrideEnabled: true,
                    overrideText: String(customValue),
                  };
                } else if (circuit.fittingId?.startsWith('Mufta_')) {
                  if (circuit.fittingId === 'Mufta_1.2' || circuit.fittingId === 'Mufta_3.8' || circuit.fittingId === 'Mufta_1.4') {
                    customValue = defaultLength - 5;
                  } else {
                    customValue = defaultLength - 10;
                  }
                  dimensionOverride = {
                    overrideEnabled: true,
                    overrideText: String(customValue),
                  };
                }
              }
            }
          }

          const fDiameter = fittingInfo.diameter;
          const fittingId = `fitting-circuit-${index + 1}-${Date.now()}`;
          
          // rotation = 0: фитинг смотрит вверх
          // Нижний центр фитинга должен касаться верхнего края трубы
          fittings.push({
            id: fittingId,
            key: circuit.fittingId,
            itemKey: circuit.fittingId,
            x: 0,
            y: 0,
            width: fDiameter,
            height: fLength,
            groupX: circuitX,
            groupY: startY,
            rotation: 0,
            offsetX: fDiameter / 2, // Центр по X
            offsetY: fLength, // Нижний край (точка соединения с трубой)
            ...dimensionOverride, // Добавляем информацию об измененном значении
            igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
            isLongCircuit: false, // IGES: короткие патрубки у этажного
            igesCreateHole: true, // IGES: этот фитинг создаёт отверстие
            holeDiameter: getHoleDiameterForPipe(fDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (уменьшен для сварки)
            igesHoleDirection: 'up', // IGES: верхний контур — отверстие вверх
          });
          
          // Сохраняем данные для размерных линий
          circuitObjects.push({
            id: fittingId,
            type: 'fitting',
            x: circuitX - fDiameter / 2,
            y: startY - fLength,
            width: fDiameter,
            height: fLength,
            centerX: circuitX,
            fittingKey: circuit.fittingId, // Сохраняем ключ фитинга для определения типа
            fittingLength: circuit.fittingLength, // Сохраняем пользовательскую длину
          });
        }
      }
    }
  });

  // 7. Нижние отводы контуров (вертикальные вниз)
  const bottomCircuitObjects = [];
  
  if (bottomCircuits && bottomCircuits.length > 0) {
    // Вычисляем позиции нижних контуров с учётом индивидуальных межосевых
    let currentBottomX = startX + actualBottomFirstCircuitOffset;
    const bottomCircuitPositionsX = bottomCircuits.map((circuit, index) => {
      const pos = currentBottomX;
      if (index < bottomCircuits.length - 1) {
        // Проверяем что circuit не undefined
        const spacing = circuit ? (Number(circuit.spacingToNext) || bottomCircuitSpacing) : bottomCircuitSpacing;
        currentBottomX += spacing;
      }
      return pos;
    });
    
    bottomCircuits.forEach((circuit, index) => {
      // Пропускаем undefined или null элементы
      if (!circuit) {
        return;
      }
      
      const circuitX = bottomCircuitPositionsX[index];
      
      if (circuit.type === 'pipe') {
        const circuitId = `cyl-bottom-circuit-${index + 1}-${Date.now()}`;
        const pipeLength = Number(circuit.length) || 80;
        const pipeDiameter = Number(circuit.diameter) || 26.9;
        cylinders.push({
          id: circuitId,
          x: circuitX - pipeDiameter / 2,
          y: startY + mainPipeSize, // Снизу гребёнки
          width: pipeDiameter,
          height: pipeLength,
          pipeSpec: createPipeSpec(pipeDiameter, determineThickness(mainPipeDiameter, pipeDiameter, coating), coating),
        });
        welds.push({
          id: `weld-bottom-circuit-${index + 1}-${Date.now()}`,
          x: circuitX,
          y: startY + mainPipeSize,
          objects: [mainPipeId, circuitId],
        });
        
        bottomCircuitObjects.push({
          id: circuitId,
          type: 'cylinder',
          x: circuitX - pipeDiameter / 2,
          y: startY + mainPipeSize,
          width: pipeDiameter,
          height: pipeLength,
          centerX: circuitX,
        });
      } else {
        // Фитинг контура
        const fittingInfo = fittingsData[circuit.fittingId];
        if (fittingInfo) {
          const isFlange = circuit.fittingId?.startsWith('Flanec_');
          const isFreeFlange = isFlange && !circuit.fittingId?.endsWith('_welded');
          
          if (isFlange) {
            // === ФЛАНЕЦ НА НИЖНЕМ КОНТУРЕ ===
            // Свободный: трубка → бортшайба → фланец (надет на трубку)
            // Приварной: трубка → фланец (надет на трубку, без бортшайбы)
            // Вылет = длина трубки (фланец надевается сверху)
            const dn = extractDnFromFlangeKey(circuit.fittingId);
            const flangePipeDiameter = getPipeDiameterForDn(dn) || fittingInfo.diameter;
            const flangePipeLength = Number(circuit.flangePipeLength) || 100;
            const flangeLength = fittingInfo.length;
            const flangeDiameter = fittingInfo.diameter;
            
            // Нижняя точка гребёнки
            const bottomY = startY + mainPipeSize;
            
            // Бортшайба для свободных фланцев
            let bortshaibaLength = 0;
            if (isFreeFlange) {
              const bortshaibaKey = `Bortshaiba_${dn}`;
              const bortshaibaInfo = fittingsData[bortshaibaKey];
              if (bortshaibaInfo) {
                bortshaibaLength = bortshaibaInfo.length || 10;
              }
            }
            
            // Длина трубки:
            // Для свободного: вылет - бортшайба (бортшайба на конце, общий вылет = трубка + бортшайба)
            // Для приварного: вылет (фланец надет на трубку)
            const actualPipeLength = isFreeFlange
              ? Math.max(0, flangePipeLength - bortshaibaLength)
              : flangePipeLength;
            
            // 1. Добавляем трубку (вертикальная, вниз от гребёнки)
            const circuitPipeId = `cyl-bottom-circuit-flange-${index + 1}-${Date.now()}`;
            let currentY = bottomY;
            
            if (actualPipeLength > 0) {
              cylinders.push({
                id: circuitPipeId,
                x: circuitX - flangePipeDiameter / 2,
                y: bottomY,
                width: flangePipeDiameter,
                height: actualPipeLength,
                pipeSpec: createPipeSpec(flangePipeDiameter, determineThickness(mainPipeDiameter, flangePipeDiameter, coating), coating),
                insertionDepth: flangePipeInsertion(mainPipeDiameter, flangePipeDiameter, params.shape),
              });
              welds.push({
                id: `weld-bottom-circuit-flange-pipe-${index + 1}-${Date.now()}`,
                x: circuitX,
                y: bottomY,
                objects: [mainPipeId, circuitPipeId],
              });
              currentY = bottomY + actualPipeLength;
            }
            
            // 2. Добавляем бортшайбу (для свободных фланцев)
            let bortshaibaId = null;
            if (isFreeFlange) {
              const bortshaibaKey = `Bortshaiba_${dn}`;
              const bortshaibaInfo = fittingsData[bortshaibaKey];
              if (bortshaibaInfo) {
                bortshaibaId = `fitting-bottom-circuit-bortshaiba-${index + 1}-${Date.now()}`;
                // Бортшайба на нижнем конце трубки (rotation = 180, смотрит вниз)
                fittings.push({
                  id: bortshaibaId,
                  key: bortshaibaKey,
                  itemKey: bortshaibaKey,
                  x: 0,
                  y: 0,
                  width: bortshaibaInfo.diameter,
                  height: bortshaibaLength,
                  snapPoints: bortshaibaInfo.snapPoints,
                  correctionOffset: 3,
                  useOriginalSize: true,
                  groupX: circuitX,
                  groupY: currentY,
                  rotation: 180,
                  offsetX: bortshaibaInfo.diameter / 2,
                  offsetY: bortshaibaLength,
                  igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
                  isLongCircuit: false, // IGES: короткие патрубки
                  igesCreateHole: true, // IGES: создаёт отверстие
                  holeDiameter: getHoleDiameterForPipe(flangePipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
                  igesHoleDirection: 'down', // IGES: нижний контур — отверстие вниз
                });
                currentY = currentY + bortshaibaLength;
              }
            }
            
            // 3. Добавляем фланец (надет на трубку)
            const flangeId = `fitting-bottom-circuit-flange-${index + 1}-${Date.now()}`;
            // Фланец надет на трубку, его низ = низу трубки
            // Для свободного: бортшайба на конце трубки, фланец перед ней
            // Для приварного: фланец на конце трубки (приварен)
            const flangeY = bottomY + actualPipeLength - flangeLength;
            
            fittings.push({
              id: flangeId,
              key: circuit.fittingId,
              itemKey: circuit.fittingId,
              x: 0,
              y: 0,
              width: flangeDiameter,
              height: flangeLength,
              groupX: circuitX,
              groupY: flangeY,
              rotation: 180, // Смотрит вниз
              offsetX: flangeDiameter / 2,
              offsetY: flangeLength,
              igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
              isLongCircuit: false, // IGES: короткие патрубки
              igesCreateHole: !isFreeFlange, // IGES: приварной создаёт отверстие
              holeDiameter: getHoleDiameterForPipe(flangePipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
              igesHoleDirection: 'down', // IGES: нижний контур — отверстие вниз
            });
            
            // Сохраняем данные для размерных линий (вылет контура)
            // Для свободного фланца: нижняя точка = бортшайба
            // Для приварного: нижняя точка = фланец
            const bottomCircuitObjData = {
              id: circuitPipeId,
              type: 'cylinder',
              x: circuitX - flangePipeDiameter / 2,
              y: bottomY, // Верх = низ гребёнки
              width: flangePipeDiameter,
              height: flangePipeLength, // Полный вылет
              centerX: circuitX,
              isFlange: true,
              isFreeFlange: isFreeFlange,
              flangeId: flangeId, // ID фланца для balloons
              // Верхняя точка: всегда трубка (у гребёнки)
              topObjectId: circuitPipeId,
              topObjectType: 'cylinder',
              // Нижняя точка: бортшайба (свободный) или фланец (приварной) (повернуты на 180°, используем 'top' сторону)
              bottomObjectId: isFreeFlange && bortshaibaId ? bortshaibaId : flangeId,
              bottomObjectType: 'fitting',
              bottomAttachSide: 'top', // Для повернутых фитингов используем локальную 'top' сторону
            };
            
            bottomCircuitObjects.push(bottomCircuitObjData);
          } else if (circuit.fittingId?.startsWith('Nak_Gaika_')) {
            // === НАКИДНАЯ ГАЙКА НА НИЖНЕМ КОНТУРЕ ===
            // Трубка → шайба накидной гайки → накидная гайка (надета на трубку)
            // Вылет = длина трубки (накидная гайка надевается сверху)
            const size = extractSizeFromNakGaikaKey(circuit.fittingId);
            const nakGaikaPipeDiameter = getPipeDiameterForNakGaika(size);
            const nakGaikaPipeLength = nakShaibaPipeMeasure(circuit.flangePipeLength, 0, params.shape, false).visible;
            const nakGaikaLength = fittingInfo.length;
            const nakGaikaDiameter = fittingInfo.diameter;
            
            // Нижняя точка гребёнки
            const bottomY = startY + mainPipeSize;
            
            // Шайба накидной гайки
            const shaibaKey = getShaibaKeyForFitting(circuit.fittingId);
            const shaibaInfo = fittingsData[shaibaKey];
            const shaibaLength = shaibaInfo?.length || 10;
            
            // Длина трубки: вылет - шайба (шайба на конце, общий вылет = трубка + шайба)
            const nakMeasure = nakShaibaPipeMeasure(circuit.flangePipeLength, shaibaLength, params.shape, false);
          const actualPipeLength = nakMeasure.outside;
            
            // 1. Добавляем трубку (вертикальная, вниз от гребёнки)
            const circuitPipeId = `cyl-bottom-circuit-nakgaika-${index + 1}-${Date.now()}`;
            let currentY = bottomY;
            
            if (actualPipeLength > 0) {
              cylinders.push({
                id: circuitPipeId,
                x: circuitX - nakGaikaPipeDiameter / 2,
                y: bottomY,
                width: nakGaikaPipeDiameter,
                height: actualPipeLength,
                pipeSpec: createPipeSpec(nakGaikaPipeDiameter, 'x3', coating),
              insertionDepth: nakMeasure.insertion,
              });
              welds.push({
                id: `weld-bottom-circuit-nakgaika-pipe-${index + 1}-${Date.now()}`,
                x: circuitX,
                y: bottomY,
                objects: [mainPipeId, circuitPipeId],
              });
              currentY = bottomY + actualPipeLength;
            }
            
            // 2. Добавляем шайбу накидной гайки
            let shaibaId = null;
            if (shaibaInfo) {
              shaibaId = `fitting-bottom-circuit-shaiba-${index + 1}-${Date.now()}`;
              // Шайба на нижнем конце трубки (rotation = 180, смотрит вниз)
              fittings.push({
                id: shaibaId,
                key: shaibaKey,
                itemKey: shaibaKey,
                x: 0,
                y: 0,
                width: shaibaInfo.diameter,
                height: shaibaLength,
                groupX: circuitX,
                groupY: currentY,
                rotation: 180,
                offsetX: shaibaInfo.diameter / 2,
                offsetY: shaibaLength,
                igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
                isLongCircuit: false, // IGES: короткие патрубки
                igesCreateHole: true, // IGES: создаёт отверстие
                holeDiameter: getHoleDiameterForPipe(nakGaikaPipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
                igesHoleDirection: 'down', // IGES: нижний контур — отверстие вниз
              });
              currentY = currentY + shaibaLength;
            }
            
            // 3. Добавляем накидную гайку (надета на трубку)
            const nakGaikaId = `fitting-bottom-circuit-nakgaika-${index + 1}-${Date.now()}`;
            // Накидная гайка надета на трубку, её низ = низу трубки
            // Шайба на конце трубки, накидная гайка перед ней
            const nakGaikaY = bottomY + actualPipeLength - nakGaikaLength;
            
            fittings.push({
              id: nakGaikaId,
              key: circuit.fittingId,
              itemKey: circuit.fittingId,
              x: 0,
              y: 0,
              width: nakGaikaDiameter,
              height: nakGaikaLength,
              groupX: circuitX,
              groupY: nakGaikaY,
              rotation: 180,
              offsetX: nakGaikaDiameter / 2,
              offsetY: nakGaikaLength,
              igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
              isLongCircuit: false, // IGES: короткие патрубки
              igesCreateHole: false, // IGES: отверстие создано шайбой
              holeDiameter: nakGaikaDiameter, // IGES: для справки
              igesHoleDirection: 'down', // IGES: нижний контур — отверстие вниз
            });
            
            // Сохраняем данные для размерных линий и balloons
            const bottomCircuitObjData = {
              id: circuitPipeId,
              type: 'cylinder',
              x: circuitX - nakGaikaPipeDiameter / 2,
              y: bottomY, // Верх = низ гребёнки
              width: nakGaikaPipeDiameter,
              height: nakGaikaPipeLength, // Полный вылет
              centerX: circuitX,
              isNakGaika: true,
              nakGaikaId: nakGaikaId, // ID накидной гайки для balloons
              // Верхняя точка: всегда трубка (у гребёнки)
              topObjectId: circuitPipeId,
              topObjectType: 'cylinder',
              // Нижняя точка: шайба накидной гайки (повернута на 180°, используем 'top' сторону)
              bottomObjectId: shaibaId,
              bottomObjectType: 'fitting',
              bottomAttachSide: 'top', // Для повернутой шайбы используем локальную 'top' сторону
            };
            
            bottomCircuitObjects.push(bottomCircuitObjData);
          } else if (circuit.fittingId?.startsWith('Shaib_Nar_Rezba_')) {
            // === ШАЙБА С НАРУЖНОЙ РЕЗЬБОЙ НА НИЖНЕМ КОНТУРЕ ===
            // Трубка → шайба с наружной резьбой (шайба идет ПОСЛЕ трубы, как бортшайба)
            // Вылет = длина трубки + шайба (пользователь указывает общий вылет)
            const size = extractSizeFromShaibaNarRezba(circuit.fittingId);
            const shaibaPipeDiameter = getPipeDiameterForShaibaNarRezba(size);
            const shaibaPipeLength = nakShaibaPipeMeasure(circuit.flangePipeLength, 0, params.shape, true).visible;
            const shaibaLength = fittingInfo.length;
            const shaibaDiameter = fittingInfo.diameter;
            
            // Нижняя точка гребёнки
            const bottomY = startY + mainPipeSize;
            
            // Длина трубки: вылет - длина шайбы (шайба идет после трубы, общий вылет = труба + шайба)
            const shaibaMeasure = nakShaibaPipeMeasure(circuit.flangePipeLength, shaibaLength, params.shape, true);
          const actualPipeLength = shaibaMeasure.outside;
            
            // 1. Добавляем трубку (вертикальная, вниз от гребёнки)
            const circuitPipeId = `cyl-bottom-circuit-shaiba-${index + 1}-${Date.now()}`;
            let currentY = bottomY;
            
            if (actualPipeLength > 0) {
              cylinders.push({
                id: circuitPipeId,
                x: circuitX - shaibaPipeDiameter / 2,
                y: bottomY,
                width: shaibaPipeDiameter,
                height: actualPipeLength,
                pipeSpec: createPipeSpec(shaibaPipeDiameter, 'x3', coating),
              insertionDepth: shaibaMeasure.insertion,
              });
              welds.push({
                id: `weld-bottom-circuit-shaiba-pipe-${index + 1}-${Date.now()}`,
                x: circuitX,
                y: bottomY,
                objects: [mainPipeId, circuitPipeId],
              });
              currentY = bottomY + actualPipeLength;
            }
            
            // 2. Добавляем шайбу с наружной резьбой (на нижнем конце трубки, rotation = 180, смотрит вниз)
            const shaibaId = `fitting-bottom-circuit-shaiba-nar-rezba-${index + 1}-${Date.now()}`;
            
            fittings.push({
              id: shaibaId,
              key: circuit.fittingId,
              itemKey: circuit.fittingId,
              x: 0,
              y: 0,
              width: shaibaDiameter,
              height: shaibaLength,
              groupX: circuitX,
              groupY: currentY,
              rotation: 180,
              offsetX: shaibaDiameter / 2,
              offsetY: shaibaLength,
              igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
              isLongCircuit: false, // IGES: короткие патрубки
              igesCreateHole: true, // IGES: создаёт отверстие
              holeDiameter: getHoleDiameterForPipe(shaibaPipeDiameter), // IGES: диаметр отверстия (уменьшен для сварки)
              igesHoleDirection: 'down', // IGES: нижний контур — отверстие вниз
            });
            currentY = currentY + shaibaLength;
            
            // Сохраняем данные для размерных линий и balloons
            const bottomCircuitObjData = {
              id: circuitPipeId,
              type: 'cylinder',
              x: circuitX - shaibaPipeDiameter / 2,
              y: bottomY, // Верх = низ гребёнки
              width: shaibaPipeDiameter,
              height: shaibaPipeLength, // Вылет = только длина трубки (как указал пользователь)
              centerX: circuitX,
              isShaibaNarRezba: true,
              shaibaId: shaibaId, // ID шайбы для balloons
              // Верхняя точка: всегда трубка (у гребёнки)
              topObjectId: circuitPipeId,
              topObjectType: 'cylinder',
              // Нижняя точка: шайба с наружной резьбой (повернута на 180°, используем 'top' сторону)
              bottomObjectId: shaibaId,
              bottomObjectType: 'fitting',
              bottomAttachSide: 'top', // Для повернутой шайбы используем локальную 'top' сторону
            };
            
            bottomCircuitObjects.push(bottomCircuitObjData);
          } else {
            // === ОБЫЧНЫЙ ФИТИНГ (не фланец, не накидная гайка, не шайба с наружной резьбой) ===
            const catalogLength = fittingInfo.length;
            let fLength;
            let dimensionOverride = {};

            const nippelMuftaResolved = resolveNippelMuftaFitting({
              fittingId: circuit.fittingId,
              fittingLength: circuit.fittingLength ?? null,
              pipeDiameter: mainPipeDiameter,
              catalogLength,
              shape,
            });

            if (nippelMuftaResolved) {
              fLength = nippelMuftaResolved.length;
              dimensionOverride = nippelMuftaResolved.dimensionOverride;
            } else {
              const defaultFittingLength = catalogLength;
              fLength = circuit.fittingLength != null ? Number(circuit.fittingLength) : defaultFittingLength;

              if (circuit.fittingLength === null || circuit.fittingLength === undefined) {
                const defaultLength = defaultFittingLength;
                let customValue = null;
                
                if (shape === 'square') {
                  if (circuit.fittingId.startsWith('Nippel_')) {
                    customValue = defaultLength - 3;
                  } else if (circuit.fittingId.startsWith('Mufta_')) {
                    customValue = getSquareMuftaCircuitDisplayValue(circuit.fittingId, defaultLength);
                  } else if (circuit.fittingId.startsWith('Nak_Gaika_') || circuit.fittingId.startsWith('Shaib_Nar_Rezba_')) {
                    customValue = 54;
                  }
                } else {
                  if (circuit.fittingId.startsWith('Nippel_')) {
                    customValue = getNippelRoundOverrideValue(circuit.fittingId, mainPipeDiameter, defaultLength);
                  } else if (circuit.fittingId.startsWith('Mufta_')) {
                    if (circuit.fittingId === 'Mufta_1.4' || circuit.fittingId === 'Mufta_3.8' || circuit.fittingId === 'Mufta_1.2') {
                      customValue = defaultLength - 5;
                    } else {
                      customValue = defaultLength - 10;
                    }
                  }
                }
                
                if (customValue !== null) {
                  dimensionOverride = {
                    overrideEnabled: true,
                    overrideText: String(customValue),
                  };
                }
              }
            }

            const fDiameter = fittingInfo.diameter;
            const fittingId = `fitting-bottom-circuit-${index + 1}-${Date.now()}`;
            
            fittings.push({
              id: fittingId,
              key: circuit.fittingId,
              itemKey: circuit.fittingId,
              x: 0,
              y: 0,
              width: fDiameter,
              height: fLength,
              groupX: circuitX,
              groupY: startY + mainPipeSize,
              rotation: 180, // Смотрит вниз
              offsetX: fDiameter / 2,
              offsetY: fLength,
              ...dimensionOverride,
              igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
              isLongCircuit: false, // IGES: короткие патрубки
              igesCreateHole: true, // IGES: создаёт отверстие
              holeDiameter: getHoleDiameterForPipe(fDiameter, { isSquare: shape === 'square' }), // IGES: диаметр отверстия (уменьшен для сварки)
              igesHoleDirection: 'down', // IGES: нижний контур — отверстие вниз
            });
            
            bottomCircuitObjects.push({
              id: fittingId,
              type: 'fitting',
              x: circuitX - fDiameter / 2,
              y: startY + mainPipeSize,
              width: fDiameter,
              height: fLength,
              centerX: circuitX,
            });
          }
        }
      }
    });
  }

  // 7. Центральные фитинги (на самой гребенке, для термометров)
  const centerFittingObjects = []; // Для хранения информации о центральных фитингах
  
  if (centerFittings && centerFittings.length > 0) {
    // Генерация центральных фитингов с использованием модуля
    const centerFittingsResult = generateCenterFittings({
      centerFittings,
      mainPipeId,
      mainPipeDiameter,
      mainCenterY: mainPipeCenterY,
      startX,
      mainPipeStartX,
      mainPipeLength,
      actualMainPipeLength: mainPipeLength - leftStraightCapLength - rightStraightCapLength,
      circuits: circuits,  // Передаем исходные данные контуров
      bottomCircuits: bottomCircuits,  // Передаем нижние контуры
      circuitSpacing,
      firstCircuitOffset,
      bottomFirstCircuitOffset,
      bottomCircuitSpacing,
      leftStraightCapLength,
      shape, // Передаем форму коллектора (round/square)
    });
    
    fittings.push(...centerFittingsResult.fittings);
    welds.push(...centerFittingsResult.welds);
    centerFittingObjects.push(...centerFittingsResult.centerFittingObjects);
  }

  // 8. Генерация размерных линий
  const dimensionLines = [];
  const dimOffset = 40; // Отступ размерных линий от объектов
  
  // Функция умного форматирования числа (60 → "60", 60.3 → "60.3")
  const formatDim = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return '0';
    if (Number.isInteger(num)) return num.toString();
    const fixed = num.toFixed(1);
    return fixed.endsWith('.0') ? Math.round(num).toString() : fixed;
  };
  
  // Вычисляем единые уровни для размерных линий
  const minCircuitTopY = circuitObjects.length > 0 
    ? Math.min(...circuitObjects.filter(c => c).map(c => c.y)) 
    : startY;
  const maxBottomCircuitY = bottomCircuitObjects.length > 0
    ? Math.max(...bottomCircuitObjects.filter(c => c).map(c => c.y + c.height))
    : startY + mainPipeSize;
  const topDimY = minCircuitTopY - dimOffset * 2; // Уровень верхних размеров (отступы, между контурами) - в 2 раза выше
  const bottomCircuitsDimY = maxBottomCircuitY + dimOffset * 2; // Уровень размеров нижних контуров - в 2 раза ниже
  const bottomDimY = bottomCircuitsDimY + (bottomCircuitObjects.length > 0 ? 40 : 0); // Торцевые размеры и гребёнка - ниже нижних контуров
  
  // Вспомогательная функция: проверяет совпадают ли позиции X двух контуров
  // Используется для скрытия дублирующих размерных линий нижних контуров (как в Distribution)
  const bottomMatchesOpposite = (bottomObj, topObj) => {
    if (!bottomObj || !topObj) return false;
    return Math.abs(bottomObj.centerX - topObj.centerX) < 0.1;
  };

  // 5.1 Общая длина (mainPipeLength уже включает прямые заглушки)
  const dimLeftX = startX;
  const dimRightX = startX + mainPipeLength;
  
  // Определяем элементы привязки для левого и правого края
  // Для прямых заглушек - привязка к заглушке
  // Для бортшайб - привязка к бортшайбе
  // Для гребёнки без заглушки - привязка к гребёнке
  const leftDimElement = (leftCapId && leftStraightCapLength > 0)
    ? { objectId: leftCapId, objectType: 'fitting', x: dimLeftX, y: startY + mainPipeSize, type: 'top' }
    : (leftBortshaibaId && leftBortshaibaLength > 0)
      ? { objectId: leftBortshaibaId, objectType: 'fitting', x: dimLeftX, y: startY + mainPipeSize, type: 'top' }
      : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX, y: startY + mainPipeSize, type: 'left' };
  
  const rightDimElement = (rightCapId && rightStraightCapLength > 0)
    ? { objectId: rightCapId, objectType: 'fitting', x: dimRightX, y: startY + mainPipeSize, type: 'top' }
    : (rightBortshaibaId && rightBortshaibaLength > 0)
      ? { objectId: rightBortshaibaId, objectType: 'fitting', x: dimRightX, y: startY + mainPipeSize, type: 'top' }
      : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX + actualMainPipeLength, y: startY + mainPipeSize, type: 'right' };
  
  dimensionLines.push({
    isHorizontal: true,
    lockOrientation: true,
    angle: 0,
    x1: dimLeftX,
    y1: startY + mainPipeSize,
    x2: dimRightX,
    y2: startY + mainPipeSize,
    position: { x: 0, y: bottomDimY },
    value: formatDim(mainPipeLength),
    rawValue: mainPipeLength,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [leftDimElement, rightDimElement],
  });

  // 5.2 Диаметр гребёнки (слева)
  const mainPipeDimensionLine = {
    isHorizontal: false,
    angle: 90,
    x1: startX,
    y1: startY,
    x2: startX,
    y2: startY + mainPipeSize,
    position: { x: startX - dimOffset - (hasLeftConnection ? (leftConnectionType === 'pipe' ? leftConnectionLength : 50) : 0), y: 0 },
    value: formatDim(mainPipeSize),
    rawValue: mainPipeSize,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: mainPipeId, objectType: 'cylinder', x: startX, y: startY, type: 'top' },
      { objectId: mainPipeId, objectType: 'cylinder', x: startX, y: startY + mainPipeSize, type: 'bottom' },
    ],
  };
  
  // Для квадратных труб добавляем измененное значение (профиль вместо размера)
  if (shape === 'square') {
    const profileThickness = mainPipeSpec.thickness || '2';
    mainPipeDimensionLine.overrideEnabled = true;
    mainPipeDimensionLine.overrideText = `${mainPipeProfile.replace('x', '×')} ×${profileThickness}`; // "80×80 ×2"
  } else {
    // Для круглых труб добавляем толщину к диаметру
    const pipeThickness = (mainPipeSpec.thickness || 'x2').replace('x', ''); // "x2" → "2"
    mainPipeDimensionLine.overrideEnabled = true;
    mainPipeDimensionLine.overrideText = `${mainPipeDiameter} ×${pipeThickness}`; // "60.3 ×2"
  }
  
  dimensionLines.push(mainPipeDimensionLine);

  // 5.3 Отступ первого контура от края (firstCircuitOffset уже от общего края)
  if (circuitObjects.length > 0) {
    const firstCircuit = circuitObjects[0];
    
    // Привязываемся на уровне верха фитинга, чтобы пунктирная линия не шла через фитинг
    const circuitTopY = firstCircuit.y;
    
    // Левый элемент привязки (заглушка, бортшайба или гребёнка)
    const leftOffsetElement = (leftCapId && leftStraightCapLength > 0)
      ? { objectId: leftCapId, objectType: 'fitting', x: dimLeftX, y: circuitTopY, type: 'top' }
      : (leftBortshaibaId && leftBortshaibaLength > 0)
        ? { objectId: leftBortshaibaId, objectType: 'fitting', x: dimLeftX, y: circuitTopY, type: 'top' }
        : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX, y: circuitTopY, type: 'left' };
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: dimLeftX,
      y1: circuitTopY,
      x2: firstCircuit.centerX,
      y2: circuitTopY,
      position: { x: 0, y: topDimY },
      value: formatDim(firstCircuitOffset),
      rawValue: firstCircuitOffset,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        leftOffsetElement,
        { objectId: firstCircuit.id, objectType: firstCircuit.type, x: firstCircuit.centerX, y: circuitTopY, type: 'top' },
      ],
    });
  }

  // 5.4 Расстояния между контурами (между верхними средними точками)
  for (let i = 0; i < circuitObjects.length - 1; i++) {
    const current = circuitObjects[i];
    const next = circuitObjects[i + 1];
    const spacing = next.centerX - current.centerX;
    
    // Верхние точки контуров (y = circuit.y для вертикальных контуров)
    const currentTopY = current.y;
    const nextTopY = next.y;
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true, // Сохраняем горизонтальную ориентацию
      angle: 0,
      x1: current.centerX,
      y1: Math.min(currentTopY, nextTopY),
      x2: next.centerX,
      y2: Math.min(currentTopY, nextTopY),
      position: { x: 0, y: topDimY }, // Единый уровень сверху
      value: formatDim(spacing),
      rawValue: spacing,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: current.id, objectType: current.type, x: current.centerX, y: currentTopY, type: 'top' },
        { objectId: next.id, objectType: next.type, x: next.centerX, y: nextTopY, type: 'top' },
      ],
    });
  }

  // 5.4.1 Последний отступ (от последнего контура до правого края)
  if (circuitObjects.length > 0) {
    const lastCircuit = circuitObjects[circuitObjects.length - 1];
    const lastOffset = lastCircuitOffset;
    
    // Привязываемся на уровне верха фитинга, чтобы пунктирная линия не шла через фитинг
    const circuitTopY = lastCircuit.y;
    
    // Правый элемент привязки (заглушка, бортшайба или гребёнка)
    const rightOffsetElement = (rightCapId && rightStraightCapLength > 0)
      ? { objectId: rightCapId, objectType: 'fitting', x: dimRightX, y: circuitTopY, type: 'top' }
      : (rightBortshaibaId && rightBortshaibaLength > 0)
        ? { objectId: rightBortshaibaId, objectType: 'fitting', x: dimRightX, y: circuitTopY, type: 'top' }
        : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX + actualMainPipeLength, y: circuitTopY, type: 'right' };
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: lastCircuit.centerX,
      y1: circuitTopY,
      x2: dimRightX,
      y2: circuitTopY,
      position: { x: 0, y: topDimY }, // Единый уровень сверху
      value: formatDim(lastOffset),
      rawValue: lastOffset,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: lastCircuit.id, objectType: lastCircuit.type, x: lastCircuit.centerX, y: circuitTopY, type: 'top' },
        rightOffsetElement,
      ],
    });
  }

  // 5.5 Длины контуров (вертикальные, справа от каждого)
  // Группируем контуры по ТИПУ И ДЛИНЕ:
  // - Для фланцев: только по вылету (тип фланца не важен)
  // - Для фитингов: по fittingId + длина (Nippel_3.4 40мм и Nippel_3.4 60мм - разные)
  // - Для труб: по диаметру + длина
  // Верхние и нижние контуры группируются ОТДЕЛЬНО
  const shownTopCircuitKeys = new Set();
  circuitObjects.forEach((circuit, index) => {
    const circuitData = circuits[index];
    let uniqueKey;
    
    if (circuit.isFlange) {
      // Для фланцев группируем только по вылету (тип фланца не важен)
      uniqueKey = `flange-${circuit.height}`;
    } else if (circuit.type === 'fitting' && circuitData?.fittingId) {
      // Для фитингов группируем по типу фитинга И длине
      uniqueKey = `fitting-${circuitData.fittingId}-${circuit.height}`;
    } else {
      // Для труб группируем по диаметру И длине
      uniqueKey = `pipe-${circuitData?.diameter || circuit.width}-${circuit.height}`;
    }
    
    // Пропускаем если такой тип уже показан (для верхних контуров)
    if (shownTopCircuitKeys.has(uniqueKey)) return;
    shownTopCircuitKeys.add(uniqueKey);
    
    // Для фланцев используем специальные объекты привязки (бортшайба для свободного, трубка для приварного)
    const topElementId = circuit.topObjectId || circuit.id;
    const topElementType = circuit.topObjectType || circuit.type;
    const bottomElementId = circuit.bottomObjectId || circuit.id;
    const bottomElementType = circuit.bottomObjectType || circuit.type;
    
    dimensionLines.push({
      isHorizontal: false,
      angle: 90,
      x1: circuit.x + circuit.width,
      y1: circuit.y,
      x2: circuit.x + circuit.width,
      y2: circuit.y + circuit.height,
      position: { x: circuit.x + circuit.width + 35, y: 0 },
      value: formatDim(circuit.height),
      rawValue: circuit.height,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: topElementId, objectType: topElementType, x: circuit.x + circuit.width, y: circuit.y, type: 'top' },
        { objectId: bottomElementId, objectType: bottomElementType, x: circuit.x + circuit.width, y: circuit.y + circuit.height, type: 'bottom' },
      ],
    });
  });

  // 5.6 Длина левого подключения (с учётом заглушки)
  const isLeftFlange = leftConnectionInfo?.fittingKey?.startsWith('Flanec_');
  const isLeftFreeFlange = isLeftFlange && !leftConnectionInfo?.fittingKey?.endsWith('_welded');
  const isLeftWeldedFlange = isLeftFlange && !isLeftFreeFlange;
  
  // Проверяем, нужен ли переход или заглушка для левого фланца
  let leftFlangeNeedsReducerOrCap = false;
  if (isLeftFlange && leftConnectionFitting) {
    const leftFlangeDn = extractDnFromFlangeKey(leftConnectionFitting);
    const leftFlangePipeDiameter = getPipeDiameterForDn(leftFlangeDn);
    // Для квадратных профилей всегда нужен вылет (нет логики "одинаковых диаметров")
    // Для круглых труб проверяем: диаметры НЕ совпадают И гребёнка больше
    if (shape === 'square') {
      leftFlangeNeedsReducerOrCap = true;
    } else {
      leftFlangeNeedsReducerOrCap = leftFlangePipeDiameter && leftFlangePipeDiameter !== mainPipeDiameter && mainPipeDiameter > leftFlangePipeDiameter;
    }
  }
  
  // Для свободных фланцев: размерная линия ТОЛЬКО если есть переход или заглушка (диаметры НЕ совпадают)
  // Привязываем к бортшайбе (внешний край) и гребёнке/заглушке
  if (isLeftFreeFlange && leftBortshaibaId && leftConnectionLength && leftFlangeNeedsReducerOrCap) {
    const fittingCenterY = mainPipeCenterY;
    const connLength = leftConnectionLength;
    const connStartX = mainPipeStartX; // От края гребёнки
    
    // Проверяем, есть ли прямая заглушка слева
    const hasLeftStraightCap = hasLeftCap && leftCapElementType === 'cap' && leftCapType === 'straight';
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connStartX - connLength,
      y1: fittingCenterY,
      x2: connStartX,
      y2: fittingCenterY,
      position: { x: 0, y: bottomDimY }, // Единый уровень снизу
      value: formatDim(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        // Левая точка — внешний край бортшайбы, правая точка — край заглушки (если прямая) или гребёнки
        { objectId: leftBortshaibaId, objectType: 'fitting', x: connStartX - connLength, y: fittingCenterY, type: 'top' },
        { objectId: hasLeftStraightCap && leftCapId ? leftCapId : mainPipeId, objectType: hasLeftStraightCap && leftCapId ? 'fitting' : 'cylinder', x: connStartX, y: fittingCenterY, type: hasLeftStraightCap && leftCapId ? 'top' : 'left' },
      ],
    });
  } 
  // Для приварных фланцев: размерная линия если есть переход/заглушка и промежуточная труба
  else if (isLeftWeldedFlange && leftIntermediatePipeId && leftConnectionLength && leftFlangeNeedsReducerOrCap) {
    const fittingCenterY = mainPipeCenterY;
    const connLength = leftConnectionLength;
    const connStartX = mainPipeStartX; // От края гребёнки
    
    // Проверяем, есть ли прямая заглушка слева
    const hasLeftStraightCap = hasLeftCap && leftCapElementType === 'cap' && leftCapType === 'straight';
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connStartX - connLength,
      y1: fittingCenterY,
      x2: connStartX,
      y2: fittingCenterY,
      position: { x: 0, y: bottomDimY }, // Единый уровень снизу
      value: formatDim(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        // Левая точка — левый край промежуточной трубы, правая точка — край заглушки (если прямая) или гребёнки
        { objectId: leftIntermediatePipeId, objectType: 'cylinder', x: connStartX - connLength, y: fittingCenterY, type: 'left' },
        { objectId: hasLeftStraightCap && leftCapId ? leftCapId : mainPipeId, objectType: hasLeftStraightCap && leftCapId ? 'fitting' : 'cylinder', x: connStartX, y: fittingCenterY, type: hasLeftStraightCap && leftCapId ? 'top' : 'left' },
      ],
    });
  }
  else if (leftConnectionInfo && !isLeftFlange) {
    // Для обычных фитингов (не фланцев) — показываем длину фитинга
    const fittingCenterY = mainPipeCenterY;
    const connLength = leftConnectionInfo.length;
    const isLeftPipe = leftConnectionInfo.type === 'cylinder';
    const isLeftNakGaika = leftConnectionInfo.fittingKey?.startsWith('Nak_Gaika_');
    const isLeftShaibaNarRezba = leftConnectionInfo.fittingKey?.startsWith('Shaib_Nar_Rezba_');
    const connStartX = leftConnectionInfo.startX || startX; // Начало от заглушки если есть
    
    // Для накидных гаек и шайб с нар. резьбой: размерная линия ТОЛЬКО если есть заглушка/переход (диаметры НЕ совпадают)
    const shouldSkipDimension = (isLeftNakGaika || isLeftShaibaNarRezba) && !leftConnectionInfo.capId && !leftConnectionInfo.intermediatePipeId;
    
    if (!shouldSkipDimension) {
    const elements = isLeftPipe ? [
        // Для трубы: левая точка трубы и правая точка трубы
        { objectId: leftConnectionInfo.id, objectType: 'cylinder', x: connStartX - connLength, y: fittingCenterY, type: 'left' },
        { objectId: leftConnectionInfo.id, objectType: 'cylinder', x: connStartX, y: fittingCenterY, type: 'right' },
      ] : (isLeftNakGaika && leftConnectionInfo.bortshaibaId) || isLeftShaibaNarRezba ? [
        // Для накидной гайки: от гребенки/заглушки до шайбы накидной гайки (размер показывает полный вылет)
        // Для шайбы с нар. резьбой: от гребенки/заглушки до самой шайбы (размер показывает полный вылет = труба + шайба)
        { 
          objectId: leftConnectionInfo.capId || mainPipeId, 
          objectType: leftConnectionInfo.capId ? 'fitting' : 'cylinder', 
          x: connStartX, 
          y: fittingCenterY, 
          type: leftConnectionInfo.capId ? 'top' : 'left' 
        },
        { 
          objectId: isLeftShaibaNarRezba ? leftConnectionInfo.id : leftConnectionInfo.bortshaibaId, 
          objectType: 'fitting', 
          x: connStartX - connLength, 
          y: fittingCenterY, 
          type: 'top' 
        },
      ] : [
        // Для фитинга (повёрнут на -90°): top→left, bottom→right в мировых координатах
        { objectId: leftConnectionInfo.id, objectType: 'fitting', x: connStartX - connLength, y: fittingCenterY, type: 'top' },
        { objectId: leftConnectionInfo.id, objectType: 'fitting', x: connStartX, y: fittingCenterY, type: 'bottom' },
      ];
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connStartX - connLength,
      y1: fittingCenterY,
      x2: connStartX,
      y2: fittingCenterY,
      position: { x: 0, y: bottomDimY }, // Единый уровень снизу
      value: formatDim(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: elements,
      // Добавляем overrideText если есть
      ...(leftConnectionInfo.overrideEnabled && {
        overrideEnabled: leftConnectionInfo.overrideEnabled,
        overrideText: leftConnectionInfo.overrideText,
      }),
    });
    }
  }

  // 5.7 Длина правого подключения (с учётом заглушки)
  const isRightFlange = rightConnectionInfo?.fittingKey?.startsWith('Flanec_');
  const isRightFreeFlange = isRightFlange && !rightConnectionInfo?.fittingKey?.endsWith('_welded');
  const isRightWeldedFlange = isRightFlange && !isRightFreeFlange;
  
  // Проверяем, нужен ли переход или заглушка для правого фланца
  let rightFlangeNeedsReducerOrCap = false;
  if (isRightFlange && rightConnectionFitting) {
    const rightFlangeDn = extractDnFromFlangeKey(rightConnectionFitting);
    const rightFlangePipeDiameter = getPipeDiameterForDn(rightFlangeDn);
    // Для квадратных профилей всегда нужен вылет (нет логики "одинаковых диаметров")
    // Для круглых труб проверяем: диаметры НЕ совпадают И гребёнка больше
    if (shape === 'square') {
      rightFlangeNeedsReducerOrCap = true;
    } else {
      rightFlangeNeedsReducerOrCap = rightFlangePipeDiameter && rightFlangePipeDiameter !== mainPipeDiameter && mainPipeDiameter > rightFlangePipeDiameter;
    }
  }
  
  // Для свободных фланцев: размерная линия ТОЛЬКО если есть переход или заглушка (диаметры НЕ совпадают)
  // Привязываем к бортшайбе (внешний край) и гребёнке/заглушке
  if (isRightFreeFlange && rightBortshaibaId && rightConnectionLength && rightFlangeNeedsReducerOrCap) {
    const fittingCenterY = mainPipeCenterY;
    const connLength = rightConnectionLength;
    const connStartX = mainPipeStartX + actualMainPipeLength; // От края гребёнки
    
    // Проверяем, есть ли прямая заглушка справа
    const hasRightStraightCap = hasRightCap && rightCapElementType === 'cap' && rightCapType === 'straight';
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connStartX,
      y1: fittingCenterY,
      x2: connStartX + connLength,
      y2: fittingCenterY,
      position: { x: 0, y: bottomDimY }, // Единый уровень снизу
      value: formatDim(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        // Левая точка — край заглушки (если прямая) или гребёнки, правая точка — внешний край бортшайбы
        { objectId: hasRightStraightCap && rightCapId ? rightCapId : mainPipeId, objectType: hasRightStraightCap && rightCapId ? 'fitting' : 'cylinder', x: connStartX, y: fittingCenterY, type: hasRightStraightCap && rightCapId ? 'top' : 'right' },
        { objectId: rightBortshaibaId, objectType: 'fitting', x: connStartX + connLength, y: fittingCenterY, type: 'top' },
      ],
    });
  }
  // Для приварных фланцев: размерная линия если есть переход/заглушка и промежуточная труба
  else if (isRightWeldedFlange && rightIntermediatePipeId && rightConnectionLength && rightFlangeNeedsReducerOrCap) {
    const fittingCenterY = mainPipeCenterY;
    const connLength = rightConnectionLength;
    const connStartX = mainPipeStartX + actualMainPipeLength; // От края гребёнки
    
    // Проверяем, есть ли прямая заглушка справа
    const hasRightStraightCap = hasRightCap && rightCapElementType === 'cap' && rightCapType === 'straight';
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connStartX,
      y1: fittingCenterY,
      x2: connStartX + connLength,
      y2: fittingCenterY,
      position: { x: 0, y: bottomDimY }, // Единый уровень снизу
      value: formatDim(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        // Левая точка — край заглушки (если прямая) или гребёнки, правая точка — правый край промежуточной трубы
        { objectId: hasRightStraightCap && rightCapId ? rightCapId : mainPipeId, objectType: hasRightStraightCap && rightCapId ? 'fitting' : 'cylinder', x: connStartX, y: fittingCenterY, type: hasRightStraightCap && rightCapId ? 'top' : 'right' },
        { objectId: rightIntermediatePipeId, objectType: 'cylinder', x: connStartX + connLength, y: fittingCenterY, type: 'right' },
      ],
    });
  }
  else if (rightConnectionInfo && !isRightFlange) {
    // Для обычных фитингов (не фланцев) — показываем длину фитинга
    const fittingCenterY = mainPipeCenterY;
    const connLength = rightConnectionInfo.length;
    const isRightPipe = rightConnectionInfo.type === 'cylinder';
    const isRightNakGaika = rightConnectionInfo.fittingKey?.startsWith('Nak_Gaika_');
    const isRightShaibaNarRezba = rightConnectionInfo.fittingKey?.startsWith('Shaib_Nar_Rezba_');
    const connStartX = rightConnectionInfo.startX || (startX + mainPipeLength); // Начало от заглушки если есть
    
    // Для накидных гаек и шайб с нар. резьбой: размерная линия ТОЛЬКО если есть заглушка/переход (диаметры НЕ совпадают)
    const shouldSkipDimension = (isRightNakGaika || isRightShaibaNarRezba) && !rightConnectionInfo.capId && !rightConnectionInfo.intermediatePipeId;
    
    if (!shouldSkipDimension) {
    const elements = isRightPipe ? [
        // Для трубы: левая точка трубы и правая точка трубы
        { objectId: rightConnectionInfo.id, objectType: 'cylinder', x: connStartX, y: fittingCenterY, type: 'left' },
        { objectId: rightConnectionInfo.id, objectType: 'cylinder', x: connStartX + connLength, y: fittingCenterY, type: 'right' },
      ] : (isRightNakGaika && rightConnectionInfo.bortshaibaId) || isRightShaibaNarRezba ? [
        // Для накидной гайки: от гребенки/заглушки до шайбы накидной гайки (размер показывает полный вылет)
        // Для шайбы с нар. резьбой: от гребенки/заглушки до самой шайбы (размер показывает полный вылет = труба + шайба)
        { 
          objectId: rightConnectionInfo.capId || mainPipeId, 
          objectType: rightConnectionInfo.capId ? 'fitting' : 'cylinder', 
          x: connStartX, 
          y: fittingCenterY, 
          type: rightConnectionInfo.capId ? 'top' : 'right' 
        },
        { 
          objectId: isRightShaibaNarRezba ? rightConnectionInfo.id : rightConnectionInfo.bortshaibaId, 
          objectType: 'fitting', 
          x: connStartX + connLength, 
          y: fittingCenterY, 
          type: 'top' 
        },
      ] : [
        // Для фитинга (повёрнут на 90°): bottom→left, top→right в мировых координатах
        { objectId: rightConnectionInfo.id, objectType: 'fitting', x: connStartX, y: fittingCenterY, type: 'bottom' },
        { objectId: rightConnectionInfo.id, objectType: 'fitting', x: connStartX + connLength, y: fittingCenterY, type: 'top' },
      ];
    
    dimensionLines.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: connStartX,
      y1: fittingCenterY,
      x2: connStartX + connLength,
      y2: fittingCenterY,
      position: { x: 0, y: bottomDimY }, // Единый уровень снизу
      value: formatDim(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: elements,
      // Добавляем overrideText если есть
      ...(rightConnectionInfo.overrideEnabled && {
        overrideEnabled: rightConnectionInfo.overrideEnabled,
        overrideText: rightConnectionInfo.overrideText,
      }),
    });
    }
  }

  // 5.8 Размерные линии для нижних контуров
  // Скрываем только те размеры, которые совпадают с верхними (как в Distribution)
  if (bottomCircuitObjects.length > 0) {
    // Отступ первого нижнего контура — показываем если не совпадает с верхним
    const firstBottomCircuit = bottomCircuitObjects[0];
    const firstTopCircuit = circuitObjects[0];
    const firstOffsetMatchesTop = firstTopCircuit &&
      bottomMatchesOpposite(firstBottomCircuit, firstTopCircuit) &&
      Math.abs(bottomFirstCircuitOffset - firstCircuitOffset) < 0.1;

    if (!firstOffsetMatchesTop) {
      const circuitTopY = firstBottomCircuit.y;
      const leftBottomOffsetElement = (leftCapId && leftStraightCapLength > 0)
        ? { objectId: leftCapId, objectType: 'fitting', x: dimLeftX, y: circuitTopY, type: 'top' }
        : (leftBortshaibaId && leftBortshaibaLength > 0)
          ? { objectId: leftBortshaibaId, objectType: 'fitting', x: dimLeftX, y: circuitTopY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX, y: circuitTopY, type: 'left' };

      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: dimLeftX,
        y1: circuitTopY,
        x2: firstBottomCircuit.centerX,
        y2: circuitTopY,
        position: { x: 0, y: bottomCircuitsDimY },
        value: formatDim(bottomFirstCircuitOffset),
        rawValue: bottomFirstCircuitOffset,
        extensionLines: [],
        precision: 1,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          leftBottomOffsetElement,
          { objectId: firstBottomCircuit.id, objectType: firstBottomCircuit.type, x: firstBottomCircuit.centerX, y: circuitTopY, type: 'top' },
        ],
      });
    }

    // Расстояния между нижними контурами — скрываем только совпадающие с верхними
    for (let i = 0; i < bottomCircuitObjects.length - 1; i++) {
      const current = bottomCircuitObjects[i];
      const next = bottomCircuitObjects[i + 1];
      const spacing = next.centerX - current.centerX;

      // Проверяем совпадает ли это расстояние с верхним
      const oppCurrent = circuitObjects[i];
      const oppNext = circuitObjects[i + 1];
      let shouldShow = true;
      if (oppCurrent && oppNext) {
        const oppSpacing = oppNext.centerX - oppCurrent.centerX;
        const posMatch = bottomMatchesOpposite(current, oppCurrent) && bottomMatchesOpposite(next, oppNext);
        const spacingMatch = Math.abs(spacing - oppSpacing) < 0.1;
        if (posMatch && spacingMatch) shouldShow = false;
      }

      if (!shouldShow) continue;

      const currentTopY = current.y;
      const nextTopY = next.y;
      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: current.centerX,
        y1: Math.min(currentTopY, nextTopY),
        x2: next.centerX,
        y2: Math.min(currentTopY, nextTopY),
        position: { x: 0, y: bottomCircuitsDimY },
        value: formatDim(spacing),
        rawValue: spacing,
        extensionLines: [],
        precision: 1,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: current.id, objectType: current.type, x: current.centerX, y: currentTopY, type: 'top' },
          { objectId: next.id, objectType: next.type, x: next.centerX, y: nextTopY, type: 'top' },
        ],
      });
    }

    // Последний отступ нижнего контура — показываем если не совпадает с верхним
    const lastBottomCircuit = bottomCircuitObjects[bottomCircuitObjects.length - 1];
    const lastTopCircuit = circuitObjects[circuitObjects.length - 1];
    const lastOffsetMatchesTop = lastTopCircuit &&
      bottomMatchesOpposite(lastBottomCircuit, lastTopCircuit) &&
      Math.abs(bottomLastCircuitOffset - lastCircuitOffset) < 0.1;

    if (!lastOffsetMatchesTop) {
      const lastCircuitTopY = lastBottomCircuit.y;
      const rightBottomOffsetElement = (rightCapId && rightStraightCapLength > 0)
        ? { objectId: rightCapId, objectType: 'fitting', x: dimRightX, y: lastCircuitTopY, type: 'top' }
        : (rightBortshaibaId && rightBortshaibaLength > 0)
          ? { objectId: rightBortshaibaId, objectType: 'fitting', x: dimRightX, y: lastCircuitTopY, type: 'top' }
          : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX + actualMainPipeLength, y: lastCircuitTopY, type: 'right' };

      dimensionLines.push({
        isHorizontal: true,
        lockOrientation: true,
        angle: 0,
        x1: lastBottomCircuit.centerX,
        y1: lastCircuitTopY,
        x2: dimRightX,
        y2: lastCircuitTopY,
        position: { x: 0, y: bottomCircuitsDimY },
        value: formatDim(bottomLastCircuitOffset),
        rawValue: bottomLastCircuitOffset,
        extensionLines: [],
        precision: 1,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: lastBottomCircuit.id, objectType: lastBottomCircuit.type, x: lastBottomCircuit.centerX, y: lastCircuitTopY, type: 'top' },
          rightBottomOffsetElement,
        ],
      });
    }
  }

  // Длины нижних контуров (ВСЕГДА показываем, независимо от совпадения позиций)
  if (bottomCircuitObjects.length > 0) {
    const shownBottomCircuitKeys = new Set();
    bottomCircuitObjects.forEach((circuit, index) => {
      const circuitData = bottomCircuits[index];
      let uniqueKey;
      if (circuit.isFlange) {
        // Для фланцев группируем только по вылету (тип фланца не важен)
        uniqueKey = `flange-${circuit.height}`;
      } else if (circuit.type === 'fitting' && circuitData?.fittingId) {
        // Для фитингов группируем по типу фитинга И длине
        uniqueKey = `fitting-${circuitData.fittingId}-${circuit.height}`;
      } else {
        // Для труб группируем по диаметру И длине
        uniqueKey = `pipe-${circuitData?.diameter || circuit.width}-${circuit.height}`;
      }
      
      // Пропускаем если такой тип уже показан (для нижних контуров отдельно)
      if (shownBottomCircuitKeys.has(uniqueKey)) return;
      shownBottomCircuitKeys.add(uniqueKey);

      // Для фланцев используем специальные объекты привязки
      const topElementId = circuit.topObjectId || circuit.id;
      const topElementType = circuit.topObjectType || circuit.type;
      const bottomElementId = circuit.bottomObjectId || circuit.id;
      const bottomElementType = circuit.bottomObjectType || circuit.type;
      
      // Для нижних контуров с фланцами, накидными гайками и шайбами с нар. резьбой (бортшайба/шайба или фланец/гайка повёрнуты на 180°): используем 'top' вместо 'bottom'
      // т.к. они смотрят вниз и их локальный 'top' = мировой низ
      const bottomPointType = ((circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba) && bottomElementType === 'fitting') ? 'top' : 'bottom';

      dimensionLines.push({
        isHorizontal: false,
        angle: 90,
        x1: circuit.x + circuit.width,
        y1: circuit.y,
        x2: circuit.x + circuit.width,
        y2: circuit.y + circuit.height,
        position: { x: circuit.x + circuit.width + 35, y: 0 },
        value: formatDim(circuit.height),
        rawValue: circuit.height,
        extensionLines: [],
        precision: 1,
        textPosition: { x: 0, y: 0 },
        textFontFamily: 'Arial',
        textFontStyle: 'italic',
        textColor: '#ff0000',
        elements: [
          { objectId: topElementId, objectType: topElementType, x: circuit.x + circuit.width, y: circuit.y, type: 'top' },
          { objectId: bottomElementId, objectType: bottomElementType, x: circuit.x + circuit.width, y: circuit.y + circuit.height, type: bottomPointType },
        ],
      });
    });
  }

  // 8.1 Размерные линии для центральных фитингов (тип "На расстоянии")
  if (centerFittingObjects.length > 0) {
    const midCollectorY = startY + mainPipeSize / 2;
    const centerDimensionLines = generateCenterFittingDimensions(
      centerFittingObjects,
      leftCapId || leftBortshaibaId || mainPipeId,
      rightCapId || rightBortshaibaId || mainPipeId,
      startX,
      midCollectorY,
      mainPipeDiameter,
      mainPipeLength,
      leftStraightCapLength,
      rightStraightCapLength,
      mainPipeId,
      actualMainPipeLength,
      mainPipeStartX,
      leftConnectionInfo,
      rightConnectionInfo,
      leftBortshaibaLength,
      rightBortshaibaLength
    );
    
    // Размерные линии уже в правильном формате, просто добавляем их
    dimensionLines.push(...centerDimensionLines);
  }

  // 9. Генерация позиций (balloons) для фитингов
  const balloons = [];
  
  // Функция для получения текста позиции по ключу фитинга
  const getFittingLabel = (fittingKey) => {
    if (!fittingKey) return '';
    
    const sizeMap = {
      '1.4': '1/4"',
      '3.8': '3/8"',
      '1.2': '1/2"',
      '3.4': '3/4"',
      '1': '1"',
      '1_1.4': '1.1/4"',
      '1_1.2': '1.1/2"',
      '2': '2"',
      '2_1/2': '2.1/2"',
      '3': '3"',
      '4': '4"',
    };
    
    // Ниппели
    if (fittingKey.startsWith('Nippel_')) {
      const size = fittingKey.replace('Nippel_', '');
      return (sizeMap[size] || size) + 'НР';
    }
    
    // Муфты
    if (fittingKey.startsWith('Mufta_')) {
      const size = fittingKey.replace('Mufta_', '');
      return (sizeMap[size] || size) + 'ВР';
    }
    
    // Переходы концентрические - "Переход\n60.3х48.3"
    if (fittingKey.startsWith('Perehod_Konc_')) {
      const dims = fittingKey.replace('Perehod_Konc_', ''); // например "60.3х48.3"
      return 'Переход\n' + dims;
    }
    
    // Фланцы - разбираем displayName и форматируем
    if (fittingKey.startsWith('Flanec_')) {
      const fittingData = fittingsData[fittingKey];
      if (fittingData) {
        const displayName = fittingData.displayName; // "Фланец Ду32 (своб.оцинк.)"
        // Извлекаем размер и тип
        const match = displayName.match(/Фланец (Ду\d+)\s*\((.+)\)/);
        if (match) {
          return 'Фланец ' + match[1] + '\n(' + match[2] + ')';
        }
        return displayName;
      }
    }
    
    // Накидные гайки - '1" Н.Г'
    if (fittingKey.startsWith('Nak_Gaika_')) {
      const size = fittingKey.replace('Nak_Gaika_', '');
      return (sizeMap[size] || size) + ' Н.Г';
    }
    
    // Шайбы с наружной резьбой - '1" Ш.Н.Р'
    if (fittingKey.startsWith('Shaib_Nar_Rezba_')) {
      const size = fittingKey.replace('Shaib_Nar_Rezba_', '');
      return (sizeMap[size] || size) + ' Ш.Н.Р';
    }
    
    // Другие фитинги - возвращаем displayName или ключ
    const fittingData = fittingsData[fittingKey];
    if (fittingData?.displayName) {
      return fittingData.displayName;
    }
    return fittingKey;
  };
  
  // Позиции для контурных фитингов (включая фланцы)
  // Объединяем пары соседних одинаковых фитингов (максимум 2)
  const processedCircuitIndices = new Set();
  
  circuitObjects.forEach((circuit, index) => {
    // Пропускаем трубы (не фитинги) и уже обработанные
    const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
    if (!isFitting || processedCircuitIndices.has(index)) return;
    
    const fittingKey = circuits[index]?.fittingId;
    const label = getFittingLabel(fittingKey);
    
    // Для фланцев используем flangeId, для накидных гаек - nakGaikaId, для шайб с нар. резьбой - shaibaId, для обычных фитингов - circuit.id
    const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
    
    // Проверяем следующий контур - можно ли объединить
    const nextIndex = index + 1;
    const nextCircuit = circuitObjects[nextIndex];
    const nextFittingKey = circuits[nextIndex]?.fittingId;
    const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
    
    // Позиция по Y = между верхом фитинга и размерной линией контуров (topDimY)
    const balloonY = (topDimY + circuit.y) / 2;
    
    if (nextCircuit && 
        nextIsFitting && 
        nextFittingKey === fittingKey &&
        !processedCircuitIndices.has(nextIndex)) {
      // Объединяем два соседних одинаковых фитинга
      // Позиция между ними по X
      const midX = (circuit.centerX + nextCircuit.centerX) / 2;
      const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
      
      balloons.push({
        id: `balloon-circuit-${index + 1}-${Date.now()}`,
        x: midX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [
          { type: 'fitting', id: targetId },
          { type: 'fitting', id: nextTargetId },
        ],
      });
      
      processedCircuitIndices.add(index);
      processedCircuitIndices.add(nextIndex);
    } else {
      // Одиночный фитинг - позиция между ПРЕДЫДУЩИМ (левым) и текущим контуром по X
      let posX;
      const prevIndex = index - 1;
      const prevCircuit = circuitObjects[prevIndex];
      
      if (prevCircuit) {
        // Есть предыдущий контур - позиция между ними
        posX = (prevCircuit.centerX + circuit.centerX) / 2;
      } else {
        // Первый контур - позиция слева от него
        posX = circuit.centerX - circuitSpacing / 2;
      }
      
      balloons.push({
        id: `balloon-circuit-${index + 1}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [{ type: 'fitting', id: targetId }],
      });
      
      processedCircuitIndices.add(index);
    }
  });
  
  // Позиции для нижних контурных фитингов (включая фланцы)
  const processedBottomCircuitIndices = new Set();
  
  bottomCircuitObjects.forEach((circuit, index) => {
    // Пропускаем трубы (не фитинги) и уже обработанные
    const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
    if (!isFitting || processedBottomCircuitIndices.has(index)) return;
    
    const fittingKey = bottomCircuits[index]?.fittingId;
    const label = getFittingLabel(fittingKey);
    
    // Для фланцев используем flangeId, для накидных гаек - nakGaikaId, для шайб с нар. резьбой - shaibaId, для обычных фитингов - circuit.id
    const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
    
    // Проверяем следующий контур - можно ли объединить
    const nextIndex = index + 1;
    const nextCircuit = bottomCircuitObjects[nextIndex];
    const nextFittingKey = bottomCircuits[nextIndex]?.fittingId;
    const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
    
    // Позиция по Y = между низом фитинга и размерной линией нижних контуров (поднята на 10px)
    const balloonY = (bottomCircuitsDimY + circuit.y + circuit.height) / 2 - 10;
    
    if (nextCircuit && 
        nextIsFitting && 
        nextFittingKey === fittingKey &&
        !processedBottomCircuitIndices.has(nextIndex)) {
      // Объединяем два соседних одинаковых фитинга
      const midX = (circuit.centerX + nextCircuit.centerX) / 2;
      const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
      
    balloons.push({
      id: `balloon-bottom-circuit-${index}-${Date.now()}`,
      x: midX,
      y: balloonY,
      text: label,
      textAlign: 'center',
      targets: [
        { type: 'fitting', id: targetId },
        { type: 'fitting', id: nextTargetId },
      ],
    });
      
      processedBottomCircuitIndices.add(index);
      processedBottomCircuitIndices.add(nextIndex);
    } else {
      // Одиночный фитинг - позиция между текущим и СЛЕДУЮЩИМ (правым) контуром по X
      let posX;
      const nextBottomCircuit = bottomCircuitObjects[index + 1];
      
      if (nextBottomCircuit) {
        posX = (circuit.centerX + nextBottomCircuit.centerX) / 2;
      } else {
        posX = circuit.centerX + bottomCircuitSpacing / 2;
      }
      
      balloons.push({
        id: `balloon-bottom-circuit-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [{ type: 'fitting', id: targetId }],
      });
      
      processedBottomCircuitIndices.add(index);
    }
  });

  // Базовая Y позиция для торцевых подписей
  const baseBalloonY = startY - 30;
  // Отступ между подписями
  const balloonYOffset = 50;

  // Позиция для левого перехода концентрического - ВЛЕВО от гребёнки
  const hasLeftFitting = leftConnectionInfo && leftConnectionInfo.type === 'fitting';
  const hasLeftReducerBalloon = hasLeftCap && leftCapElementType === 'reducer' && leftCapId;
  
  if (hasLeftReducerBalloon) {
    const label = getFittingLabel(leftReducer);
    const reducerEndX = mainPipeStartX - leftCapActualLength;
    // Если есть фитинг - переход выше, иначе на базовую высоту
    const reducerY = hasLeftFitting ? baseBalloonY - balloonYOffset : baseBalloonY;
    balloons.push({
      id: `balloon-left-reducer-${Date.now()}`,
      x: reducerEndX - 25,
      y: reducerY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: leftCapId }],
    });
  }

  // Позиция для левого фитинга - ВЛЕВО (от внешнего края фитинга)
  let leftFittingBalloonY = baseBalloonY;
  if (hasLeftFitting) {
    const label = getFittingLabel(leftConnectionFitting);
    // Внешний край фитинга (левый край) - от края перехода минус длина фитинга
    const fittingStartX = leftConnectionInfo.startX || startX;
    const leftFittingEndX = fittingStartX - leftConnectionInfo.length;
    balloons.push({
      id: `balloon-left-${Date.now()}`,
      x: leftFittingEndX - 60,
      y: leftFittingBalloonY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: leftConnectionInfo.id }],
    });
  }
  
  // Позиция для правого перехода концентрического - ВПРАВО от гребёнки
  const hasRightFitting = rightConnectionInfo && rightConnectionInfo.type === 'fitting';
  const hasRightReducerBalloon = hasRightCap && rightCapElementType === 'reducer' && rightCapId;
  
  if (hasRightReducerBalloon) {
    const label = getFittingLabel(rightReducer);
    const reducerEndX = mainPipeStartX + actualMainPipeLength + rightCapActualLength;
    // Если есть фитинг - переход выше, иначе на базовую высоту
    const reducerY = hasRightFitting ? baseBalloonY - balloonYOffset : baseBalloonY;
    balloons.push({
      id: `balloon-right-reducer-${Date.now()}`,
      x: reducerEndX + 25,
      y: reducerY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: rightCapId }],
    });
  }

  // Позиция для правого фитинга - ВПРАВО (от внешнего края фитинга)
  let rightFittingBalloonY = baseBalloonY;
  if (hasRightFitting) {
    const label = getFittingLabel(rightConnectionFitting);
    // Внешний край фитинга (правый край) - от края перехода плюс длина фитинга
    const fittingStartX = rightConnectionInfo.startX || (startX + mainPipeLength);
    const rightFittingEndX = fittingStartX + rightConnectionInfo.length;
    balloons.push({
      id: `balloon-right-${Date.now()}`,
      x: rightFittingEndX + 60,
      y: rightFittingBalloonY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: rightConnectionInfo.id }],
    });
  }

  // Позиции для центральных фитингов (муфты и ниппели на гребенке)
  if (centerFittingObjects.length > 0) {
    const midCollectorY = startY + mainPipeSize / 2;
    const centerBalloons = generateCenterFittingBalloons(
      centerFittingObjects,
      getFittingLabel,
      midCollectorY
    );
    balloons.push(...centerBalloons);
  }

  // 9. Модифицируем существующие размерные линии для контуров и торцевых подключений с измененными значениями
  // Ищем фитинги с overrideEnabled
  const fittingsWithOverride = fittings.filter(f => f.overrideEnabled && f.overrideText);
  
  fittingsWithOverride.forEach((fitting) => {
    // Ищем размерную линию для этого фитинга
    dimensionLines.forEach((dimLine, dimIndex) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;
      
      // Проверяем что линия относится к этому фитингу напрямую
      const hasFitting = dimLine.elements.some(el => el.objectId === fitting.id);
      
      // Для накидных гаек: проверяем также связь через bortshaibaId
      // Размерная линия для накидной гайки привязана к бортшайбе, а не к самой гайке
      const isNakGaikaFitting = fitting.key?.startsWith('Nak_Gaika_');
      let hasBortshaiba = false;
      if (isNakGaikaFitting) {
        // Проверяем левое подключение
        if (leftConnectionInfo && leftConnectionInfo.id === fitting.id && leftConnectionInfo.bortshaibaId) {
          hasBortshaiba = dimLine.elements.some(el => el.objectId === leftConnectionInfo.bortshaibaId);
        }
        // Проверяем правое подключение
        if (rightConnectionInfo && rightConnectionInfo.id === fitting.id && rightConnectionInfo.bortshaibaId) {
          hasBortshaiba = dimLine.elements.some(el => el.objectId === rightConnectionInfo.bortshaibaId);
        }
      }
      
      if (!hasFitting && !hasBortshaiba) return;
      
      // Определяем тип фитинга по rotation:
      // rotation = -90 или 90 -> торцевое подключение (горизонтальный фитинг)
      const isEndConnectionFitting = fitting.rotation === -90 || fitting.rotation === 90;
      
      // Для вертикальных линий (контуры): проверяем что значение совпадает с высотой фитинга
      if (!dimLine.isHorizontal) {
        if (fitting.overrideEnabled && fitting.overrideText != null &&
            (dimLine.rawValue === fitting.height || dimLine.value === String(fitting.height))) {
          dimLine.overrideEnabled = true;
          dimLine.overrideText = fitting.overrideText;
        }
      }
      // Для горизонтальных линий: применяем только для торцевых подключений
      else if (isEndConnectionFitting) {
        // Для торцевых подключений размерная линия может показывать:
        // - Полный вылет (40мм для накидных гаек/шайб с резьбой по дефолту)
        // - Длину фитинга
        // Не применяем к межосевым расстояниям (они обычно больше 40-100мм)
        const isLengthDimension = dimLine.rawValue === fitting.height || 
                                   dimLine.rawValue === 40 || 
                                   dimLine.rawValue === 100 ||
                                   (dimLine.rawValue >= 15 && dimLine.rawValue <= 50); // Диапазон для муфт на торцах
        
        if (isLengthDimension && fitting.overrideEnabled && fitting.overrideText != null) {
          dimLine.overrideEnabled = true;
          dimLine.overrideText = fitting.overrideText;
        }
      }
    });
  });
  
  // Также ищем circuitObjects с overrideEnabled (накидные гайки, шайбы с резьбой)
  const circuitObjectsWithOverride = circuitObjects.filter(obj => obj && obj.overrideEnabled && obj.overrideText);
  
  circuitObjectsWithOverride.forEach((circuitObj) => {
    // Ищем вертикальную размерную линию для этого объекта
    dimensionLines.forEach((dimLine) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;
      
      // Проверяем что это вертикальная линия
      if (dimLine.isHorizontal) return;
      
      // Проверяем что линия относится к этому объекту (по ID трубки или шайбы/гайки)
      const hasCircuitObj = dimLine.elements.some(el => 
        el.objectId === circuitObj.id || 
        el.objectId === circuitObj.topObjectId ||
        el.objectId === circuitObj.nakGaikaId ||
        el.objectId === circuitObj.shaibaId
      );
      if (!hasCircuitObj) return;
      
      // Проверяем что значение размерной линии совпадает с высотой объекта
      if (dimLine.rawValue === circuitObj.height || dimLine.value === String(circuitObj.height)) {
        // Это размерная линия высоты контура - добавляем измененное значение
        dimLine.overrideEnabled = true;
        dimLine.overrideText = circuitObj.overrideText;
      }
    });
  });
  
  // Также ищем bottomCircuitObjects с overrideEnabled (накидные гайки, шайбы с резьбой на нижних контурах)
  const bottomCircuitObjectsWithOverride = bottomCircuitObjects.filter(obj => obj && obj.overrideEnabled && obj.overrideText);
  
  bottomCircuitObjectsWithOverride.forEach((circuitObj) => {
    // Ищем вертикальную размерную линию для этого объекта
    dimensionLines.forEach((dimLine) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;
      
      // Проверяем что это вертикальная линия
      if (dimLine.isHorizontal) return;
      
      // Проверяем что линия относится к этому объекту (по ID трубки или шайбы/гайки)
      const hasCircuitObj = dimLine.elements.some(el => 
        el.objectId === circuitObj.id || 
        el.objectId === circuitObj.topObjectId ||
        el.objectId === circuitObj.nakGaikaId ||
        el.objectId === circuitObj.shaibaId
      );
      if (!hasCircuitObj) return;
      
      // Проверяем что значение размерной линии совпадает с высотой объекта
      if (dimLine.rawValue === circuitObj.height || dimLine.value === String(circuitObj.height)) {
        // Это размерная линия высоты контура - добавляем измененное значение
        dimLine.overrideEnabled = true;
        dimLine.overrideText = circuitObj.overrideText;
      }
    });
  });

  // === РАСЧЕТ ОБЩЕЙ ДЛИНЫ КОЛЛЕКТОРА ===
  // Общая длина = длина гребенки + прямые заглушки + бортшайбы + левое подключение + правое подключение + эллиптические заглушки/переходы
  let totalCollectorLength = mainPipeLength; // Начинаем с длины гребенки (БЕЗ прямых заглушек и бортшайб)
  
  // Добавляем прямые заглушки и бортшайбы (они НЕ включены в mainPipeLength)
  totalCollectorLength += leftStraightCapLength + rightStraightCapLength + leftBortshaibaLength + rightBortshaibaLength;
  
  // Добавляем левое подключение
  if (leftConnectionInfo) {
    totalCollectorLength += leftConnectionInfo.length || 0;
  }
  // Если слева НЕТ подключения, но есть заглушка/переход - добавляем её
  else if (hasLeftCap) {
    if (leftCapElementType === 'reducer') {
      // Переход выступает наружу
      totalCollectorLength += leftCapActualLength;
    } else if (leftCapType === 'elliptical') {
      // Эллиптическая заглушка выступает наружу
      totalCollectorLength += leftCapActualLength;
    }
    // Прямые заглушки уже добавлены выше (leftStraightCapLength)
  }
  
  // Добавляем правое подключение
  if (rightConnectionInfo) {
    totalCollectorLength += rightConnectionInfo.length || 0;
  }
  // Если справа НЕТ подключения, но есть заглушка/переход - добавляем её
  else if (hasRightCap) {
    if (rightCapElementType === 'reducer') {
      // Переход выступает наружу
      totalCollectorLength += rightCapActualLength;
    } else if (rightCapType === 'elliptical') {
      // Эллиптическая заглушка выступает наружу
      totalCollectorLength += rightCapActualLength;
    }
    // Прямые заглушки уже добавлены выше (rightStraightCapLength)
  }


  const result = {
    cylinders,
    fittings,
    welds,
    annotations: [],
    dimensionLines,
    balloons,
    lines: [],
    objectConstraints: [],
    settings: {
      zoom: 1,
      textZoom: 1,
      positionTextZoom: 1,
      isLandscape: true,
    },
    metadata: {
      totalLength: Math.round(totalCollectorLength),
      coating: coating, // Добавляем покрытие для аннотации
      // Параметры контуров для IGES экспорта
      circuitParams: {
        startX: startX,
        // Верхние контуры
        firstCircuitOffset: firstCircuitOffset,
        circuitSpacing: circuitSpacing,
        // Вычисляем позиции верхних контуров заново
        circuitPositions: (() => {
          const positions = [];
          let x = startX + firstCircuitOffset;
          circuits.forEach((circuit, i) => {
            // Пропускаем undefined или null элементы
            if (!circuit) return;
            positions.push({ x, direction: 'up' });
            if (i < circuits.length - 1) {
              x += Number(circuit.spacingToNext) || circuitSpacing;
            }
          });
          return positions;
        })(),
        // Нижние контуры
        bottomFirstCircuitOffset: bottomFirstCircuitOffset,
        bottomCircuitSpacing: bottomCircuitSpacing,
        // Вычисляем позиции нижних контуров заново
        bottomCircuitPositions: (() => {
          if (!bottomCircuits || bottomCircuits.length === 0) return [];
          const positions = [];
          let x = startX + actualBottomFirstCircuitOffset;
          bottomCircuits.forEach((circuit, i) => {
            // Пропускаем undefined или null элементы
            if (!circuit) return;
            positions.push({ x, direction: 'down' });
            if (i < bottomCircuits.length - 1) {
              x += Number(circuit.spacingToNext) || bottomCircuitSpacing;
            }
          });
          return positions;
        })(),
      },
    },
    // Добавляем информацию об общей длине
    calculatedParams: {
      totalLength: Math.round(totalCollectorLength),
      mainPipeLength: Math.round(mainPipeLength),
      leftConnectionLength: leftConnectionInfo ? Math.round(leftConnectionInfo.length || 0) : 0,
      rightConnectionLength: rightConnectionInfo ? Math.round(rightConnectionInfo.length || 0) : 0,
      coating: coating, // Добавляем покрытие
    },
  };
  
  // IGES: Создаём "снимок" координат сразу после генерации
  // Этот снимок будет использоваться для экспорта вне зависимости от перемещений на чертеже
  result.metadata.igesSnapshot = {
    cylinders: cylinders.map(cyl => ({
      id: cyl.id,
      x: cyl.x,
      y: cyl.y,
      width: cyl.width,
      height: cyl.height,
      rotation: cyl.rotation,
    })),
    fittings: fittings.map(fit => ({
      id: fit.id,
      groupX: fit.groupX,
      groupY: fit.groupY,
      diameter: fit.diameter,
      igesParentCylinderId: fit.igesParentCylinderId,
      igesCreateHole: fit.igesCreateHole,
      holeDiameter: fit.holeDiameter,
      igesHoleDirection: fit.igesHoleDirection,
      isLongCircuit: fit.isLongCircuit,
      pairedCylinderId: fit.pairedCylinderId,
    })),
  };
  
  return result;
}

export default FloorCollectorEditor;
